import * as i0 from '@angular/core';
import { EventEmitter, Directive, Inject, HostBinding, Output, HostListener, Component, ChangeDetectionStrategy, ContentChildren, NgModule, Input, Injectable, InjectionToken, PLATFORM_ID, ViewEncapsulation, ComponentRef, TemplateRef, Optional, forwardRef, ViewChild, ContentChild, inject, Host, Renderer2, ElementRef, numberAttribute } from '@angular/core';
import * as i1$1 from '@angular/common';
import { DOCUMENT, isPlatformBrowser, CommonModule, NgTemplateOutlet } from '@angular/common';
import { merge, BehaviorSubject, fromEvent, Subscription, combineLatest, Subject, Observable, EMPTY, of, timer, race } from 'rxjs';
import * as i1 from '@ng-bootstrap/ng-bootstrap';
import { NgbToastModule, NgbAlertModule, NgbDatepickerKeyboardService, NgbDate, NgbDropdownModule, NgbDatepickerModule, NgbAccordionModule, NgbPopoverConfig, NgbPopoverModule, NgbProgressbarModule, NgbCollapseModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { take, map, switchMap, distinctUntilChanged, throttleTime, tap, filter, endWith, takeUntil } from 'rxjs/operators';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import * as i3 from '@angular/router';
import { RouterModule } from '@angular/router';
import * as i1$2 from '@ng-select/ng-select';
import { NgSelectModule } from '@ng-select/ng-select';
import { HttpClient } from '@angular/common/http';

class SkipLinkDirective {
    onFocus() {
        this.inFocus.emit(true);
    }
    onClick() {
        this.focusOnTarget();
    }
    onBlur() {
        this.inFocus.emit(false);
    }
    constructor(element, document) {
        this.element = element;
        this.document = document;
        this.class = 'list-group-item list-group-item-action';
        this.inFocus = new EventEmitter();
    }
    focusOnTarget() {
        const href = String(this.element.nativeElement.getAttribute('href'));
        const parts = href.split('#');
        if (parts.length < 2) {
            return;
        }
        const id = parts.pop();
        if (!id) {
            return;
        }
        setTimeout(() => {
            const target = this.document.getElementById(id);
            if (target) {
                target.focus();
                this.onBlur();
            }
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: SkipLinkDirective, deps: [{ token: i0.ElementRef }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.7", type: SkipLinkDirective, isStandalone: true, selector: "[dfSkipLink]", outputs: { inFocus: "inFocus" }, host: { listeners: { "focus": "onFocus()", "click": "onClick()", "blur": "onBlur()" }, properties: { "class": "this.class" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: SkipLinkDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[dfSkipLink]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }], propDecorators: { class: [{
                type: HostBinding
            }], inFocus: [{
                type: Output
            }], onFocus: [{
                type: HostListener,
                args: ['focus']
            }], onClick: [{
                type: HostListener,
                args: ['click']
            }], onBlur: [{
                type: HostListener,
                args: ['blur']
            }] } });

class SkipLinksContainerComponent {
    constructor(changeDetector) {
        this.changeDetector = changeDetector;
        this.inFocus = false;
    }
    ngAfterContentInit() {
        if (!this.links.length) {
            return;
        }
        this.focusSubscription = merge(...this.links.map((link) => link.inFocus)).subscribe((focus) => {
            this.inFocus = focus;
            this.changeDetector.detectChanges();
        });
    }
    ngOnDestroy() {
        this.focusSubscription.unsubscribe();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: SkipLinksContainerComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.7", type: SkipLinksContainerComponent, isStandalone: true, selector: "df-skip-links-container", queries: [{ propertyName: "links", predicate: SkipLinkDirective }], ngImport: i0, template: "<section class=\"df-skip-links__section\">\n  <div class=\"df-skip-links\">\n    <div\n      class=\"df-skip-links-wrap list-group\"\n      [class.focus]=\"inFocus\"\n      (blur)=\"inFocus=false\"\n    >\n      <ng-content></ng-content>\n    </div>\n  </div>\n</section>\n", changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: SkipLinksContainerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'df-skip-links-container', changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, template: "<section class=\"df-skip-links__section\">\n  <div class=\"df-skip-links\">\n    <div\n      class=\"df-skip-links-wrap list-group\"\n      [class.focus]=\"inFocus\"\n      (blur)=\"inFocus=false\"\n    >\n      <ng-content></ng-content>\n    </div>\n  </div>\n</section>\n" }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }], propDecorators: { links: [{
                type: ContentChildren,
                args: [SkipLinkDirective]
            }] } });

class DfAccessibilityModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfAccessibilityModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.7", ngImport: i0, type: DfAccessibilityModule, imports: [SkipLinkDirective, SkipLinksContainerComponent], exports: [SkipLinkDirective, SkipLinksContainerComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfAccessibilityModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfAccessibilityModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [SkipLinkDirective, SkipLinksContainerComponent],
                    exports: [SkipLinkDirective, SkipLinksContainerComponent]
                }]
        }] });

class DfInsertIconDirective {
    constructor(el, renderer) {
        this.el = el;
        this.renderer = renderer;
        this.supportedCmps = ['alert', 'toast'];
    }
    ngAfterViewInit() {
        const cssClasses = this.el.nativeElement.classList;
        // we create the HTML icon element with the renderer
        const iconEl = this.renderer.createElement('span');
        this.renderer.addClass(iconEl, 'df-insert-icon');
        this.renderer.addClass(iconEl, this.getIconClassFromClasses(cssClasses));
        this.renderer.setAttribute(iconEl, 'aria-hidden', 'true');
        // we insert the icon element inside the DOM of the alert element
        this.renderer.insertBefore(this.el.nativeElement, iconEl, this.el.nativeElement.firstChild);
    }
    getIconClassFromClasses(cssClasses) {
        const cmp = this.supportedCmps.find((cmp) => cssClasses.contains(cmp));
        if (cmp) {
            if (cssClasses.contains(`${cmp}-success`)) {
                return 'fa-check-circle';
            }
            if (cssClasses.contains(`${cmp}-warning`)) {
                return 'fa-exclamation-triangle';
            }
            if (cssClasses.contains(`${cmp}-danger`)) {
                return 'fa-minus-circle';
            }
            if (cssClasses.contains(`${cmp}-tip`)) {
                return 'fa-lightbulb';
            }
        }
        return 'fa-info-circle';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfInsertIconDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.7", type: DfInsertIconDirective, isStandalone: true, selector: "ngb-alert[dfInsertAlertIcon],ngb-toast[dfInsertToastIcon]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfInsertIconDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'ngb-alert[dfInsertAlertIcon],ngb-toast[dfInsertToastIcon]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }] });

class DfInsertIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfInsertIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.7", ngImport: i0, type: DfInsertIconModule, imports: [DfInsertIconDirective], exports: [DfInsertIconDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfInsertIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfInsertIconModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [DfInsertIconDirective],
                    exports: [DfInsertIconDirective]
                }]
        }] });

class DfToastModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfToastModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.7", ngImport: i0, type: DfToastModule, imports: [DfInsertIconModule], exports: [NgbToastModule, DfInsertIconModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfToastModule, imports: [DfInsertIconModule, NgbToastModule, DfInsertIconModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfToastModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [DfInsertIconModule],
                    exports: [NgbToastModule, DfInsertIconModule]
                }]
        }] });

class DfAlertModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfAlertModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.7", ngImport: i0, type: DfAlertModule, imports: [DfInsertIconModule], exports: [NgbAlertModule, DfInsertIconModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfAlertModule, imports: [DfInsertIconModule, NgbAlertModule, DfInsertIconModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfAlertModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [DfInsertIconModule],
                    exports: [NgbAlertModule, DfInsertIconModule]
                }]
        }] });

class CloseInputDatePickerDirective {
    constructor(elementRef) {
        this.elementRef = elementRef;
    }
    onClick(targetElement) {
        const isConnected = document.body.parentElement.contains(targetElement);
        // targetElement.isConnected to check if it is attached to the DOM
        if (!targetElement || !isConnected || !this.inputDatePicker._cRef) {
            return;
        }
        const clickedInside = this.inputDatePicker._cRef.location.nativeElement.contains(targetElement);
        const clickedInsideInput = this.elementRef.nativeElement.contains(targetElement);
        if (!(clickedInside || clickedInsideInput)) {
            this.inputDatePicker.close();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: CloseInputDatePickerDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.7", type: CloseInputDatePickerDirective, isStandalone: true, selector: "[dfCloseInputDatePicker]", inputs: { inputDatePicker: ["dfCloseInputDatePicker", "inputDatePicker"] }, host: { listeners: { "document:click": "onClick($event.target)", "document:keyup": "onClick($event.target)" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: CloseInputDatePickerDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[dfCloseInputDatePicker]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { inputDatePicker: [{
                type: Input,
                args: ['dfCloseInputDatePicker']
            }], onClick: [{
                type: HostListener,
                args: ['document:click', ['$event.target']]
            }, {
                type: HostListener,
                args: ['document:keyup', ['$event.target']]
            }] } });

class DfTriggerClickDirective {
    constructor(elementRef) {
        this.elementRef = elementRef;
    }
    triggerClick(event) {
        event.stopPropagation();
        event.preventDefault();
        this.elementRef.nativeElement.click();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfTriggerClickDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.7", type: DfTriggerClickDirective, isStandalone: true, selector: "[dfTriggerClick]", host: { listeners: { "keydown.enter": "triggerClick($event)", "keydown.space": "triggerClick($event)" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfTriggerClickDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[dfTriggerClick]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { triggerClick: [{
                type: HostListener,
                args: ['keydown.enter', ['$event']]
            }, {
                type: HostListener,
                args: ['keydown.space', ['$event']]
            }] } });

/**
 * My dfInputIcon directive
 */
class DfInputIconDirective {
    constructor(renderer, el) {
        this.renderer = renderer;
        this.el = el;
        this.dfInputIcon = [];
        this.isDisabled = false;
        this.siblingsNode = [];
    }
    ngDoCheck() {
        if (this.el.nativeElement.disabled !== this.isDisabled) {
            this.isDisabled = this.el.nativeElement.disabled;
            this.siblingsNode.forEach(siblings => {
                if (this.isDisabled) {
                    this.renderer.addClass(siblings, 'df-disabled');
                }
                else {
                    this.renderer.removeClass(siblings, 'df-disabled');
                }
            });
        }
    }
    ngOnInit() {
        this.renderer.addClass(this.el.nativeElement, 'df-input-withicon');
        this.dfInputIcon.forEach(direction => {
            this.renderer.addClass(this.el.nativeElement, direction.toString());
            this.renderer.addClass(this.el.nativeElement.parentNode, `df-input-${direction}`);
            const sibling = this.el.nativeElement.parentNode.querySelector(`.input-group-${direction}`);
            if (sibling) {
                this.siblingsNode.push(sibling);
                if (direction.toString() === 'prepend') {
                    this.button = sibling.querySelector('button');
                    if (this.button) {
                        this.initButtonHandler();
                    }
                }
            }
        });
    }
    /**
     * onBlur catch blur event to set the focused to false
     */
    onBlur() {
        this.renderer.removeClass(this.el.nativeElement.parentNode, 'df-focused');
    }
    onFocus() {
        this.renderer.addClass(this.el.nativeElement.parentNode, 'df-focused');
    }
    onTab(e) {
        if (this.button) {
            e.preventDefault();
            this.button.focus();
            this.renderer.setAttribute(this.el.nativeElement, 'tabindex', '-1');
        }
    }
    ngOnDestroy() {
        if (this.removeListener) {
            this.removeListener();
        }
    }
    initButtonHandler() {
        this.renderer.setAttribute(this.button, 'tabindex', '-1');
        this.removeListener = this.renderer.listen(this.button, 'blur', () => {
            setTimeout(() => {
                this.renderer.removeAttribute(this.el.nativeElement, 'tabindex');
            });
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfInputIconDirective, deps: [{ token: i0.Renderer2 }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.7", type: DfInputIconDirective, isStandalone: true, selector: "[dfInputIcon]", inputs: { dfInputIcon: "dfInputIcon" }, host: { listeners: { "blur": "onBlur($event)", "focus": "onFocus($event)", "keydown.Tab": "onTab($event)" } }, exportAs: ["dfInputIcon"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfInputIconDirective, decorators: [{
            type: Directive,
            args: [{
                    exportAs: 'dfInputIcon',
                    selector: '[dfInputIcon]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.Renderer2 }, { type: i0.ElementRef }], propDecorators: { dfInputIcon: [{
                type: Input
            }], onBlur: [{
                type: HostListener,
                args: ['blur', ['$event']]
            }], onFocus: [{
                type: HostListener,
                args: ['focus', ['$event']]
            }], onTab: [{
                type: HostListener,
                args: ['keydown.Tab', ['$event']]
            }] } });

class DfAdvancedInputModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfAdvancedInputModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.7", ngImport: i0, type: DfAdvancedInputModule, imports: [DfTriggerClickDirective, DfInputIconDirective], exports: [DfTriggerClickDirective, DfInputIconDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfAdvancedInputModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfAdvancedInputModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [DfTriggerClickDirective, DfInputIconDirective],
                    exports: [DfTriggerClickDirective, DfInputIconDirective]
                }]
        }] });

const Key = {
    PageUp: 'PageUp',
    PageDown: 'PageDown',
    End: 'End',
    Home: 'Home'
};
class DfDatepickerKeyboardService extends NgbDatepickerKeyboardService {
    processKey(event, dp) {
        const { state, calendar } = dp;
        switch (event.code) {
            case Key.PageUp:
                dp.focusDate(calendar.getPrev(state.focusedDate, event.altKey ? 'y' : 'm'));
                break;
            case Key.PageDown:
                dp.focusDate(calendar.getNext(state.focusedDate, event.altKey ? 'y' : 'm'));
                break;
            case Key.End:
                dp.focusDate(event.altKey ? state.maxDate : state.lastDate);
                break;
            case Key.Home:
                dp.focusDate(event.altKey ? state.minDate : state.firstDate);
                break;
            default:
                super.processKey(event, dp);
                return;
        }
        event.preventDefault();
        event.stopPropagation();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfDatepickerKeyboardService, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfDatepickerKeyboardService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfDatepickerKeyboardService, decorators: [{
            type: Injectable
        }] });

var RightToLeftDirectionEnum;
(function (RightToLeftDirectionEnum) {
    RightToLeftDirectionEnum["LeftToRight"] = "ltr";
    RightToLeftDirectionEnum["RightToLeft"] = "rtl";
    RightToLeftDirectionEnum["Auto"] = "auto";
})(RightToLeftDirectionEnum || (RightToLeftDirectionEnum = {}));
class DfDirectionDetectionService {
    getPageDirection(element) {
        let curElement = element.nativeElement;
        while (curElement !== null) {
            if (curElement.getAttribute('dir') === RightToLeftDirectionEnum.RightToLeft) {
                return RightToLeftDirectionEnum.RightToLeft;
            }
            else if (curElement.getAttribute('dir') === RightToLeftDirectionEnum.LeftToRight) {
                return RightToLeftDirectionEnum.LeftToRight;
            }
            curElement = curElement.parentElement;
        }
        return RightToLeftDirectionEnum.LeftToRight;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfDirectionDetectionService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfDirectionDetectionService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfDirectionDetectionService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

/**
 * My dfDatepickerRange directive
 */
class DfDatepickerRangeDirective {
    constructor(renderer, el, dfDirectionServ) {
        this.renderer = renderer;
        this.el = el;
        this.dfDirectionServ = dfDirectionServ;
        this.isDisabled = false;
        this.siblingsNode = [];
        this.removeListener = [];
        this.focusState = [];
    }
    ngOnInit() {
        this.renderer.addClass(this.el.nativeElement, 'df-input-datepicker-range');
        this.inputsElements = [...this.el.nativeElement.querySelectorAll('input')];
        if (this.inputsElements.length === 2) {
            this.inputsElements.forEach((el, index) => this.initInput(el, index));
        }
    }
    initInput(el, index) {
        this.renderer.addClass(el, 'df-input-withicon');
        this.renderer.addClass(el, 'append');
        this.renderer.addClass(el.parentNode, 'df-input-append');
        const sibling = el.parentNode.querySelector('.input-group-append');
        if (sibling) {
            this.siblingsNode.push(sibling);
        }
        this.removeListener.push(this.renderer.listen(el, 'focus', () => {
            this.focusState[index] = true;
            this.renderer.addClass(this.el.nativeElement, 'df-focused');
        }));
        this.removeListener.push(this.renderer.listen(el, 'blur', () => {
            this.focusState[index] = false;
            setTimeout(() => this.removeFocus(), 10);
        }));
    }
    removeFocus() {
        if (!this.focusState.reduce((prev, curr) => prev || curr)) {
            this.renderer.removeClass(this.el.nativeElement, 'df-focused');
        }
    }
    ngAfterViewInit() {
        this.updateDirection();
    }
    /**
     * You need to manually update the direction or update the icon
     * if the direction (rtl / ltr) is changing dynamically
     */
    updateDirection() {
        const currentDirection = this.dfDirectionServ.getPageDirection(this.el);
        if (this.direction !== currentDirection) {
            this.direction = currentDirection;
            const icon = this.el.nativeElement.querySelector('.fa-long-arrow-right') ||
                this.el.nativeElement.querySelector('.fa-long-arrow-left');
            if (icon) {
                if (currentDirection === RightToLeftDirectionEnum.RightToLeft) {
                    this.renderer.removeClass(icon, 'fa-long-arrow-right');
                    this.renderer.addClass(icon, 'fa-long-arrow-left');
                }
                else {
                    this.renderer.removeClass(icon, 'fa-long-arrow-left');
                    this.renderer.addClass(icon, 'fa-long-arrow-right');
                }
            }
        }
    }
    ngDoCheck() {
        // Update this to support disabled
        const currentStatus = this.inputsElements.reduce((prev, current) => prev || current.disabled, false);
        if (this.isDisabled !== currentStatus) {
            this.isDisabled = currentStatus;
            this.siblingsNode.forEach((siblings) => {
                if (this.isDisabled) {
                    this.renderer.addClass(siblings, 'df-disabled');
                }
                else {
                    this.renderer.removeClass(siblings, 'df-disabled');
                }
            });
        }
    }
    ngOnDestroy() {
        this.removeListener.forEach((item) => item());
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfDatepickerRangeDirective, deps: [{ token: i0.Renderer2 }, { token: i0.ElementRef }, { token: DfDirectionDetectionService }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.7", type: DfDatepickerRangeDirective, isStandalone: true, selector: "[dfDatepickerRange]", exportAs: ["dfDatepickerRange"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfDatepickerRangeDirective, decorators: [{
            type: Directive,
            args: [{
                    exportAs: 'dfDatepickerRange',
                    selector: '[dfDatepickerRange]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.Renderer2 }, { type: i0.ElementRef }, { type: DfDirectionDetectionService }] });

class DfDatepickerRangeService {
    constructor(calendar, formatter) {
        this.calendar = calendar;
        this.formatter = formatter;
        this.fromDate = null;
        this.toDate = null;
        this.hoveredDate = null;
        this.fromDateAsStr = '';
        this.toDateAsStr = '';
        this.startDate = null;
        this.getFocusBack = null;
        this.isClosingOnToDate = true;
        this.datepicker = null;
    }
    onDateSelection(date) {
        if (!this.fromDate && !this.toDate) {
            this.fromDate = date;
            this.fromDateAsStr = this.formatter.format(this.fromDate);
        }
        else if (this.fromDate && !this.toDate && date.after(this.fromDate)) {
            this.toDate = date;
            this.toDateAsStr = this.formatter.format(this.toDate);
            if (this.isClosingOnToDate && this.dropDown) {
                this.dropDown.close();
            }
        }
        else {
            this.toDate = null;
            this.toDateAsStr = '';
            this.fromDate = date;
            this.fromDateAsStr = this.formatter.format(this.fromDate);
        }
    }
    setFocus() {
        if (this.datepicker) {
            this.datepicker.focus();
        }
    }
    returnFocus() {
        if (this.getFocusBack !== null) {
            this.getFocusBack.focus();
        }
    }
    openChange(event) {
        if (event) {
            this.setFocus();
        }
        else {
            this.returnFocus();
        }
    }
    toggle(dropDown, element = null, datepicker = null) {
        this.getFocusBack = element;
        this.dropDown = dropDown;
        this.datepicker = datepicker;
        dropDown.toggle();
    }
    isSelected(date) {
        return date.equals(this.fromDate) || date.equals(this.toDate);
    }
    isFrom(date) {
        return date.equals(this.fromDate) && !date.equals(this.toDate);
    }
    isTo(date) {
        const toDate = this.effectiveToDate();
        return !date.equals(this.fromDate) && date.equals(toDate);
    }
    isBetween(date) {
        const toDate = this.effectiveToDate();
        return date.after(this.fromDate) && date.before(toDate);
    }
    effectiveToDate() {
        return !this.toDate && this.hoveredDate ? this.hoveredDate : this.toDate;
    }
    parseInput(previous, typed) {
        const parsed = this.formatter.parse(typed);
        return parsed && this.calendar.isValid(NgbDate.from(parsed)) ? NgbDate.from(parsed) : previous;
    }
    updateFrom(typed) {
        this.fromDate = this.fromDate ? this.parseInput(this.fromDate, typed) : null;
        this.fromDateAsStr = this.formatter.format(this.fromDate);
    }
    updateTo(typed) {
        this.toDate = this.toDate ? this.parseInput(this.toDate, typed) : null;
        this.toDateAsStr = this.formatter.format(this.toDate);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfDatepickerRangeService, deps: [{ token: i1.NgbCalendar }, { token: i1.NgbDateParserFormatter }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfDatepickerRangeService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfDatepickerRangeService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i1.NgbCalendar }, { type: i1.NgbDateParserFormatter }] });

class DfDatePickerModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfDatePickerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.7", ngImport: i0, type: DfDatePickerModule, imports: [NgbDropdownModule, CloseInputDatePickerDirective, DfDatepickerRangeDirective], exports: [CloseInputDatePickerDirective,
            NgbDatepickerModule,
            DfAdvancedInputModule,
            DfDatepickerRangeDirective,
            NgbDropdownModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfDatePickerModule, providers: [
            { provide: NgbDatepickerKeyboardService, useClass: DfDatepickerKeyboardService },
            DfDatepickerRangeService
        ], imports: [NgbDropdownModule, NgbDatepickerModule,
            DfAdvancedInputModule,
            NgbDropdownModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfDatePickerModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [NgbDropdownModule, CloseInputDatePickerDirective, DfDatepickerRangeDirective],
                    exports: [
                        CloseInputDatePickerDirective,
                        NgbDatepickerModule,
                        DfAdvancedInputModule,
                        DfDatepickerRangeDirective,
                        NgbDropdownModule
                    ],
                    providers: [
                        { provide: NgbDatepickerKeyboardService, useClass: DfDatepickerKeyboardService },
                        DfDatepickerRangeService
                    ]
                }]
        }] });

// Default DF breakpoints
const DF_BREAKPOINTS_VAR_DEFAULT = {
    xs: '--df-breakpoint-xs',
    sm: '--df-breakpoint-sm',
    md: '--df-breakpoint-md',
    lg: '--df-breakpoint-lg',
    xl: '--df-breakpoint-xl',
    xxl: '--df-breakpoint-xxl',
    xxxl: '--df-breakpoint-xxxl'
};
const BREAKPOINTS_VARS = new InjectionToken('Bootstrap Breakpoints', {
    providedIn: 'root',
    factory() {
        return DF_BREAKPOINTS_VAR_DEFAULT;
    }
});
class DfBreakpoints {
    constructor(_breakPointsVar, platformID) {
        this._breakPointsVar = _breakPointsVar;
        this.platformID = platformID;
        this._breakPoints$ = new BehaviorSubject(null);
        this.initBreakpoints();
    }
    get breakPoints$() {
        return this._breakPoints$.asObservable();
    }
    initBreakpoints() {
        let breakPoints = null;
        if (isPlatformBrowser(this.platformID) && this._breakPoints$.value === null) {
            // we are in a browser so CSS variable are accessible
            // we init the BP only once
            const styles = window.getComputedStyle(document.documentElement);
            const bpArray = [];
            for (const el of Object.keys(this._breakPointsVar)) {
                const curValue = this._breakPointsVar[el];
                const bpValue = Number.parseInt(styles.getPropertyValue(curValue), 10);
                if (isNaN(bpValue)) {
                    // We should never enter here. Only if people did not put the correct list of CSS vars...
                    // Or if the CSS is not arrived yet.
                    // What should we do then?
                    // TODO init from the default list provided if we want to?
                }
                else {
                    bpArray.push([el, bpValue]);
                }
            }
            breakPoints = this.generate(bpArray);
            // In case the first page contains the directive and the CSS vars are not available
            // We may need to launch a second init at load time.
            if (Object.keys(breakPoints).length === 0) {
                fromEvent(window, 'load')
                    .pipe(take(1))
                    .subscribe(() => {
                    // Init once as the service is available everywhere
                    // We do not check if the CSS variable are changing over time
                    // here onload the CSS var should be available
                    this.initBreakpoints();
                });
            }
        }
        this._breakPoints$.next(breakPoints);
    }
    // Utilities to use if you want to create your own queries with your values.
    // sizes is an array of array BreakPoints with sizes[0] --> identifier of the BreakPoint
    // sizes[1] px value of the BreakPoint
    generate(sizes) {
        sizes = sizes.sort((a, b) => a[1] - b[1]);
        const dict = {};
        for (let i = 0; i < sizes.length; i++) {
            const name = sizes[i][0];
            const min = sizes[i][1];
            const max = i === sizes.length - 1 ? undefined : sizes[i + 1][1];
            if (min === 0) {
                dict[name] = `(max-width: ${max - 0.01}px)`;
            }
            else if (max) {
                dict[name] = `(max-width: ${max - 0.01}px) and (min-width: ${min}px)`;
            }
            else {
                dict[name] = `(min-width: ${min}px)`;
            }
        }
        return dict;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfBreakpoints, deps: [{ token: BREAKPOINTS_VARS }, { token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfBreakpoints, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfBreakpoints, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [BREAKPOINTS_VARS]
                }] }, { type: Object, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }] });
class DfMediaQuery {
    constructor(platformId) {
        this._isBrowser = isPlatformBrowser(platformId);
    }
    notSupported(query) {
        return { matches: false, media: query, addEventListener: () => { }, removeEventListener: () => { } };
    }
    matchMedia(query) {
        if (this._isBrowser && window.matchMedia) {
            return window.matchMedia(query);
        }
        else {
            return this.notSupported(query);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfMediaQuery, deps: [{ token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfMediaQuery, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfMediaQuery, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: Object, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }] });
class DfMediaObserver {
    constructor(_mediaQuery) {
        this._mediaQuery = _mediaQuery;
        this._subscription = new Subscription();
        this._queries = new Map();
    }
    /**
     * Verifies if at least one of the provided media-queries is matching
     * the current viewport
     */
    matches(queries) {
        queries = this._flattenQueries(Array.isArray(queries) ? queries : [queries]);
        return queries.some(q => this._registerQuery(q).mediaQueryList.matches);
    }
    /**
     * Produces an observable for a set of media-queries that will emit
     * meta information for any changes of the given queries.
     * @param value One or more media-queries to be observed.
     * @returns A stream of matches meta object for the given media-queries.
     */
    observe(queries) {
        queries = this._flattenQueries(Array.isArray(queries) ? queries : [queries]);
        return combineLatest(queries.map(q => this._registerQuery(q).mediaQueryList$)).pipe(map((events) => events.reduce((result, event) => {
            result.matches = result.matches || event.matches;
            result.breakpoints[event.media] = event.matches;
            return result;
        }, { matches: false, breakpoints: {} })));
    }
    ngOnDestroy() {
        this._subscription.unsubscribe();
    }
    _flattenQueries(queries) {
        return queries.map(q => q.split(',')).reduce((flat, a) => flat.concat(a));
    }
    _registerQuery(query) {
        if (this._queries.has(query)) {
            return this._queries.get(query);
        }
        const queryList = this._mediaQuery.matchMedia(query);
        const queryList$ = new BehaviorSubject({
            media: query,
            matches: queryList.matches
        });
        this._subscription.add(fromEvent(queryList, 'change').subscribe((event) => {
            queryList$.next(event);
        }));
        const meta = { mediaQueryList: queryList, mediaQueryList$: queryList$ };
        this._queries.set(query, meta);
        return meta;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfMediaObserver, deps: [{ token: DfMediaQuery }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfMediaObserver, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfMediaObserver, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: DfMediaQuery }] });

/**
 * Services you can use to get indication about the current width screen of your application
 * the matchMedia JS feature (https://developer.mozilla.org/fr/docs/Web/API/Window/matchMedia)
 */
class DfMedia {
    constructor(mediaObserver, _breakPointsService) {
        this.mediaObserver = mediaObserver;
        this._breakPointsService = _breakPointsService;
    }
    initQueries(mediaOrBreakpoints, breakPoints = {}) {
        const queries = [];
        if (Array.isArray(mediaOrBreakpoints)) {
            for (const el of mediaOrBreakpoints) {
                queries.push(breakPoints[el] ? breakPoints[el] : el);
            }
        }
        else {
            queries.push(breakPoints[mediaOrBreakpoints] ? breakPoints[mediaOrBreakpoints] : mediaOrBreakpoints);
        }
        return queries;
    }
    /**
     * Getter to have the observable indication if you are or not in the current scope of your queries
     * If one queries is matching result is `True` otherwise result is `False`
     * @param mediaOrBreakpoints either media query string/Array of string or/and in case of BS usage a
     * string/Array of string of the breakpoints name (sm...).
     */
    getObservable(mediaOrBreakpoints) {
        return this._breakPointsService.breakPoints$.pipe(map(breakpoints => {
            return breakpoints ? this.initQueries(mediaOrBreakpoints, breakpoints) : this.initQueries(mediaOrBreakpoints);
        }), switchMap(query => {
            return this.mediaObserver.observe(query);
        }), map(value => value.matches), distinctUntilChanged());
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfMedia, deps: [{ token: DfMediaObserver }, { token: DfBreakpoints }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfMedia, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfMedia, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: DfMediaObserver }, { type: DfBreakpoints }] });

/**
 * Directive using the matchMedia JS feature (https://developer.mozilla.org/fr/docs/Web/API/Window/matchMedia)
 * to be able to add or remove an element like ngIf is doing.
 * Import DfMediaModule from DF to use all media elements provided
 */
class DfIfMediaDirective {
    /**
     * Input of the directive
     * @param mediaOrBreakpoints either media query string/Array of string or in case of BS usage a
     * string/Array of string of the breakpoints name (sm...).
     */
    set dfIfMedia(mediaOrBreakpoints) {
        this._subscription.add(this._mediaService.getObservable(mediaOrBreakpoints).subscribe((isValidated) => {
            this.update(isValidated);
        }));
    }
    constructor(_viewContainerRef, _template, _mediaService) {
        this._viewContainerRef = _viewContainerRef;
        this._template = _template;
        this._mediaService = _mediaService;
        this._viewInstance = null;
        this._subscription = new Subscription();
    }
    ngOnDestroy() {
        if (this._viewInstance) {
            this._viewContainerRef.clear();
        }
        this._subscription.unsubscribe();
    }
    update(matches) {
        if (matches && this._viewInstance === null) {
            this._viewInstance = this._viewContainerRef.createEmbeddedView(this._template);
        }
        else if (!matches) {
            this._viewContainerRef.clear();
            this._viewInstance = null;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfIfMediaDirective, deps: [{ token: i0.ViewContainerRef }, { token: i0.TemplateRef }, { token: DfMedia }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.7", type: DfIfMediaDirective, isStandalone: true, selector: "[dfIfMedia]", inputs: { dfIfMedia: "dfIfMedia" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfIfMediaDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[dfIfMedia]', standalone: true }]
        }], ctorParameters: () => [{ type: i0.ViewContainerRef }, { type: i0.TemplateRef }, { type: DfMedia }], propDecorators: { dfIfMedia: [{
                type: Input
            }] } });

class DfMediaModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfMediaModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.7", ngImport: i0, type: DfMediaModule, imports: [DfIfMediaDirective], exports: [DfIfMediaDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfMediaModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfMediaModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [DfIfMediaDirective],
                    exports: [DfIfMediaDirective]
                }]
        }] });

class DfFooterModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfFooterModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.7", ngImport: i0, type: DfFooterModule, imports: [DfMediaModule, NgbAccordionModule], exports: [DfMediaModule, NgbAccordionModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfFooterModule, imports: [DfMediaModule, NgbAccordionModule, DfMediaModule, NgbAccordionModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfFooterModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [DfMediaModule, NgbAccordionModule],
                    exports: [DfMediaModule, NgbAccordionModule]
                }]
        }] });

class DfAmadeusIconComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfAmadeusIconComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.7", type: DfAmadeusIconComponent, isStandalone: true, selector: "df-amadeus-icon", ngImport: i0, template: `
    <svg
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      xmlns:xlink="http://www.w3.org/1999/xlink"
      x="0px"
      y="0px"
      viewBox="0 0 510.2 67.9"
      style="enable-background:new 0 0 510.2 67.9;"
      xml:space="preserve"
    >
      <g id="Layer_2"></g>
      <g id="Layer_1">
        <path
          d="M269.7,0c-8.3,0-19.8,1.4-26.9,4v61.9c5.8,1.2,16.8,2,24.4,2c26,0,36.1-10.2,36.1-34.4C303.4,12.5,290.5,0,269.7,0
        M257.5,12.6c2.4-0.5,7-1.1,11.6-1.1c10.6,0,18.8,5.9,18.8,22c0,15.5-4.1,22.9-20.2,22.9c-5.2,0-10.3-0.8-10.3-0.8V12.6z
        M461.5,65.5c0,0,9,2.4,21.7,2.4c12,0,27-4.1,27-20.3c0-10.6-7.8-15.1-20.5-19.7c-6.9-2.5-12.7-4.8-12.7-9.5
      c0-2.3,1.7-6.7,10.5-6.7c8.2,0,16.6,3.8,16.6,3.8l3.7-10.8C505.2,3.4,497.2,0,486.1,0c-12.1,0-23.8,6.4-23.8,19.3
      c0,13.3,12.1,17.3,21.5,20.5c6.4,2.2,11.8,4.4,11.8,8.7c0,4.1-2.3,7.7-13.1,7.7c-8.9,0-18.3-2-18.3-2L461.5,65.5z M429.6,50.3
      c-3.5,1.7-11,4.5-17.9,4.5c-10,0-11.7-4.5-11.7-14.9V0.6h-2.2c-3.3,0-10.6,0.6-12.4,0.9v40.3c0,17.6,4.3,26,22.2,26
      c13.7,0,21.7-5.3,22.2-5.3c0.2,0,4.9,2.9,14.3,2.9v-64c-1-0.2-8.1-0.9-11.7-0.9h-2.9V50.3z M198.5,0c-13,0-23.2,3.2-27.1,5.2
      l3.1,10.9c0,0,10-3.6,19.8-3.6c8.4,0,12.7,2.4,12.7,10.6v4.7h-12.7c-21.6,0-28,9.9-28,20.8c0,14.5,11.3,19.1,21.6,19.1
      c12,0,18.9-5,19.8-5c0.7,0,3.1,2.6,14,2.6V19.3C221.7,5.5,213.1,0,198.5,0 M207.1,52.7c-3.1,1.2-10.7,3.7-16.2,3.7
      c-6,0-10.6-2.1-10.6-8.7c0-5.7,3.1-9,11.4-9.3l15.4-0.6V52.7z M32.2,0C19.2,0,9,3.2,5.1,5.2l3.1,10.9c0,0,10-3.6,19.8-3.6
      c8.4,0,12.7,2.4,12.7,10.6v4.7H28c-21.6,0-28,9.9-28,20.8c0,14.5,11.3,19.1,21.6,19.1c12,0,18.9-5,19.8-5c0.7,0,3.1,2.6,14,2.6
      V19.3C55.4,5.5,46.8,0,32.2,0 M40.9,52.7c-3.1,1.2-10.7,3.7-16.2,3.7c-6,0-10.6-2.1-10.6-8.7c0-5.7,3.1-9,11.4-9.3l15.4-0.6V52.7z
        M114,39c-0.4,0.9-0.8,2.7-1.1,3.8c-0.3-1-0.8-2.8-1.1-3.8L99,6.1C96.8,0.7,93.4,0,87.2,0c-2.2,0-6.6,0.3-6.6,0.3L74,66.9h14.6
      l3.2-44.7l13.9,33.8c1,2.6,3.1,3.7,7.3,3.7c4.1,0,6.2-1,7.3-3.7L134,22.2l3.2,44.7h14.6l-6.6-66.6c0,0-4.4-0.3-6.6-0.3
      c-6.2,0-9.6,0.7-11.8,6.1L114,39z M320.8,64.7c0,0,11.2,3.1,25.5,3.1c15.3,0,22.3-1.7,22.3-1.7V54.8c0,0-7.6,1.5-18.3,1.5
      c-9.3,0-15.1-1.2-15.1-1.2v-16h28.9V27.8h-28.9V12.5c0,0,5.7-1,14.1-1c9.5,0,18.1,1.5,18.1,1.5V1.6c0,0-7.5-1.6-19.8-1.6
      c-16.3,0-27,2.9-27,2.9V64.7z"
        />
      </g>
    </svg>
  `, isInline: true, styles: [":root{--df-navbar-height: 3.5rem}:host{min-width:60px}:host svg{margin:10%}:host.icon-primary{fill:var(--df-primary-500)}:host.icon-white{fill:#fff}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfAmadeusIconComponent, decorators: [{
            type: Component,
            args: [{ selector: 'df-amadeus-icon', template: `
    <svg
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      xmlns:xlink="http://www.w3.org/1999/xlink"
      x="0px"
      y="0px"
      viewBox="0 0 510.2 67.9"
      style="enable-background:new 0 0 510.2 67.9;"
      xml:space="preserve"
    >
      <g id="Layer_2"></g>
      <g id="Layer_1">
        <path
          d="M269.7,0c-8.3,0-19.8,1.4-26.9,4v61.9c5.8,1.2,16.8,2,24.4,2c26,0,36.1-10.2,36.1-34.4C303.4,12.5,290.5,0,269.7,0
        M257.5,12.6c2.4-0.5,7-1.1,11.6-1.1c10.6,0,18.8,5.9,18.8,22c0,15.5-4.1,22.9-20.2,22.9c-5.2,0-10.3-0.8-10.3-0.8V12.6z
        M461.5,65.5c0,0,9,2.4,21.7,2.4c12,0,27-4.1,27-20.3c0-10.6-7.8-15.1-20.5-19.7c-6.9-2.5-12.7-4.8-12.7-9.5
      c0-2.3,1.7-6.7,10.5-6.7c8.2,0,16.6,3.8,16.6,3.8l3.7-10.8C505.2,3.4,497.2,0,486.1,0c-12.1,0-23.8,6.4-23.8,19.3
      c0,13.3,12.1,17.3,21.5,20.5c6.4,2.2,11.8,4.4,11.8,8.7c0,4.1-2.3,7.7-13.1,7.7c-8.9,0-18.3-2-18.3-2L461.5,65.5z M429.6,50.3
      c-3.5,1.7-11,4.5-17.9,4.5c-10,0-11.7-4.5-11.7-14.9V0.6h-2.2c-3.3,0-10.6,0.6-12.4,0.9v40.3c0,17.6,4.3,26,22.2,26
      c13.7,0,21.7-5.3,22.2-5.3c0.2,0,4.9,2.9,14.3,2.9v-64c-1-0.2-8.1-0.9-11.7-0.9h-2.9V50.3z M198.5,0c-13,0-23.2,3.2-27.1,5.2
      l3.1,10.9c0,0,10-3.6,19.8-3.6c8.4,0,12.7,2.4,12.7,10.6v4.7h-12.7c-21.6,0-28,9.9-28,20.8c0,14.5,11.3,19.1,21.6,19.1
      c12,0,18.9-5,19.8-5c0.7,0,3.1,2.6,14,2.6V19.3C221.7,5.5,213.1,0,198.5,0 M207.1,52.7c-3.1,1.2-10.7,3.7-16.2,3.7
      c-6,0-10.6-2.1-10.6-8.7c0-5.7,3.1-9,11.4-9.3l15.4-0.6V52.7z M32.2,0C19.2,0,9,3.2,5.1,5.2l3.1,10.9c0,0,10-3.6,19.8-3.6
      c8.4,0,12.7,2.4,12.7,10.6v4.7H28c-21.6,0-28,9.9-28,20.8c0,14.5,11.3,19.1,21.6,19.1c12,0,18.9-5,19.8-5c0.7,0,3.1,2.6,14,2.6
      V19.3C55.4,5.5,46.8,0,32.2,0 M40.9,52.7c-3.1,1.2-10.7,3.7-16.2,3.7c-6,0-10.6-2.1-10.6-8.7c0-5.7,3.1-9,11.4-9.3l15.4-0.6V52.7z
        M114,39c-0.4,0.9-0.8,2.7-1.1,3.8c-0.3-1-0.8-2.8-1.1-3.8L99,6.1C96.8,0.7,93.4,0,87.2,0c-2.2,0-6.6,0.3-6.6,0.3L74,66.9h14.6
      l3.2-44.7l13.9,33.8c1,2.6,3.1,3.7,7.3,3.7c4.1,0,6.2-1,7.3-3.7L134,22.2l3.2,44.7h14.6l-6.6-66.6c0,0-4.4-0.3-6.6-0.3
      c-6.2,0-9.6,0.7-11.8,6.1L114,39z M320.8,64.7c0,0,11.2,3.1,25.5,3.1c15.3,0,22.3-1.7,22.3-1.7V54.8c0,0-7.6,1.5-18.3,1.5
      c-9.3,0-15.1-1.2-15.1-1.2v-16h28.9V27.8h-28.9V12.5c0,0,5.7-1,14.1-1c9.5,0,18.1,1.5,18.1,1.5V1.6c0,0-7.5-1.6-19.8-1.6
      c-16.3,0-27,2.9-27,2.9V64.7z"
        />
      </g>
    </svg>
  `, standalone: true, styles: [":root{--df-navbar-height: 3.5rem}:host{min-width:60px}:host svg{margin:10%}:host.icon-primary{fill:var(--df-primary-500)}:host.icon-white{fill:#fff}\n"] }]
        }] });

class DfIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.7", ngImport: i0, type: DfIconModule, imports: [DfAmadeusIconComponent], exports: [DfAmadeusIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfIconModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [DfAmadeusIconComponent],
                    exports: [DfAmadeusIconComponent]
                }]
        }] });

class DfModalService {
    constructor(modalService, rendererFactory) {
        this.modalService = modalService;
        this.rendererFactory = rendererFactory;
        this.renderer = this.rendererFactory.createRenderer(null, null);
    }
    open(content, options) {
        let modalRef;
        if (options && options.container) {
            modalRef = this.modalService.open(content, options);
        }
        else {
            modalRef = this.modalService.open(content, Object.assign({}, { container: 'body' }, options));
        }
        // we add the 'df-modal-open' class to the body when the modal is open. We remove it when the modal is closed/dismissed
        this.renderer.addClass(document.body, 'df-modal-open');
        modalRef.result.then(() => {
            this.renderer.removeClass(document.body, 'df-modal-open');
        }, () => {
            this.renderer.removeClass(document.body, 'df-modal-open');
        });
        return modalRef;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfModalService, deps: [{ token: i1.NgbModal }, { token: i0.RendererFactory2 }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfModalService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfModalService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1.NgbModal }, { type: i0.RendererFactory2 }] });

class DfPopoverConfig extends NgbPopoverConfig {
    constructor() {
        super(...arguments);
        this.autoClose = 'outside';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfPopoverConfig, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfPopoverConfig }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfPopoverConfig, decorators: [{
            type: Injectable
        }] });

class DfPopoverModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfPopoverModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.7", ngImport: i0, type: DfPopoverModule, exports: [NgbPopoverModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfPopoverModule, providers: [{ provide: NgbPopoverConfig, useClass: DfPopoverConfig }], imports: [NgbPopoverModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfPopoverModule, decorators: [{
            type: NgModule,
            args: [{
                    exports: [NgbPopoverModule],
                    providers: [{ provide: NgbPopoverConfig, useClass: DfPopoverConfig }]
                }]
        }] });

class DfProgressbarComponent {
    constructor() {
        this.infiniteAnimation = false;
        /**
         * Current value of the progressBar. If 'maxValue' is not defined, value represents a percentage. Otherwise, progress
         * bar percentage value will be computed based on the ratio of 'value/maxValue'
         */
        this._value = 0;
        this._maxValue = 100;
        /**
         * Text to be displayed on top of the progressbar
         */
        this._text = '';
        this._percentageValue = 0;
        this.percentageValueChange = new EventEmitter();
        this.computedText = '';
        this.displayProgressBar = false;
        this.FADING_DELAY_MS = 1500; // if triggered by observables, number of milliseconds after progress bar disappears once stopped
        this.animationOngoing = false;
    }
    set value(newValue) {
        this._value = newValue;
        this.updatePercentageValue();
        this.updateText();
    }
    get value() {
        return this._value;
    }
    set maxValue(newValue) {
        this._maxValue = newValue;
        this.updatePercentageValue();
        this.updateText();
    }
    get maxValue() {
        return this._maxValue;
    }
    set text(newText) {
        this._text = newText;
        this.updateText();
    }
    get text() {
        return this._text;
    }
    set percentageValue(newValue) {
        this._percentageValue = newValue;
        this.percentageValueChange.emit(newValue);
    }
    get percentageValue() {
        return this._percentageValue;
    }
    ngOnInit() {
        const isTriggeredByObservables = this.start$ && this.end$;
        if (isTriggeredByObservables) {
            this.startSubscription = this.start$.subscribe((_) => {
                this.animationOngoing = true;
                this.stopAutomaticIncrement();
                this.resetProgressBarValues();
                this.displayProgressBar = true;
                this.startAutomaticIncrement();
            });
            this.endSubscription = this.end$.subscribe((_) => {
                if (this.animationOngoing) {
                    this.animationOngoing = false;
                    this.stopAutomaticIncrement();
                    this.value = 100;
                    setTimeout(() => {
                        this.displayProgressBar = false;
                    }, this.FADING_DELAY_MS);
                }
            });
        }
        else {
            this.displayProgressBar = true;
        }
    }
    ngOnDestroy() {
        if (this.startSubscription) {
            this.startSubscription.unsubscribe();
        }
        if (this.endSubscription) {
            this.endSubscription.unsubscribe();
        }
        this.stopAutomaticIncrement();
    }
    updateText() {
        const precentagePlaceholder = '%PERCENTAGE%';
        if (this.text.includes(precentagePlaceholder)) {
            const advancementInPercents = `${Math.floor(this.percentageValue)}%`;
            this.computedText = this.text.replace(precentagePlaceholder, advancementInPercents);
            return;
        }
        this.computedText = this.text;
    }
    updatePercentageValue() {
        if (this.value > this.maxValue) {
            this.percentageValue = 100;
            return;
        }
        this.percentageValue = Math.floor((this.value / this.maxValue) * 100);
    }
    /**
     * Randomly increase the percentage but making sure that it can never reach 100%
     */
    increasePercentage() {
        const maximumReachableValue = 90;
        const remainingPercentage = maximumReachableValue - this.value;
        const averageIncreaseRatio = 0.015;
        const randomIncreaseRatio = Math.random() * 2 * averageIncreaseRatio;
        this.value += randomIncreaseRatio * remainingPercentage;
        this.updatePercentageValue();
        this.updateText();
    }
    /**
     * Clears the 'setInterval' function so that the automatic increase of the progressbar stops
     */
    stopAutomaticIncrement() {
        clearInterval(this.intervalId);
    }
    /**
     * Periodically calls a function which increments the value of the progressbar
     */
    startAutomaticIncrement() {
        this.intervalId = setInterval(this.increasePercentage.bind(this), 50);
    }
    resetProgressBarValues() {
        this.value = 0;
        this.maxValue = 100;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfProgressbarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.7", type: DfProgressbarComponent, isStandalone: true, selector: "df-progressbar", inputs: { infiniteAnimation: "infiniteAnimation", value: "value", maxValue: "maxValue", text: "text", start$: "start$", end$: "end$", ariaLabelledBy: "ariaLabelledBy", ariaLabel: "ariaLabel" }, outputs: { percentageValueChange: "percentageValueChange" }, ngImport: i0, template: "@if (displayProgressBar) {\n  @if (computedText) {\n    <div class=\"progressbar-text mb-2\">{{ computedText }}</div>\n  }\n  @if (infiniteAnimation) {\n    <div\n      class=\"progress\"\n      role=\"progressbar\"\n      aria-valuetext=\"indeterminate\"\n      [attr.aria-labelledby]=\"ariaLabelledBy || null\"\n      [attr.aria-label]=\"!ariaLabelledBy && ariaLabel ? ariaLabel : null\"\n    >\n      <div class=\"progress-bar infinite-animation\"></div>\n    </div>\n  } @else {\n    <div\n      class=\"progress\"\n      role=\"progressbar\"\n      [attr.aria-valuenow]=\"value\"\n      aria-valuemin=\"0\"\n      [attr.aria-valuemax]=\"maxValue\"\n      [attr.aria-valuetext]=\"computedText ? computedText : null\"\n      [attr.aria-labelledby]=\"ariaLabelledBy || null\"\n      [attr.aria-label]=\"!ariaLabelledBy && ariaLabel ? ariaLabel : null\"\n    >\n      <div [style.width.%]=\"percentageValue\" class=\"progress-bar\"></div>\n    </div>\n  }\n}\n" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfProgressbarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'df-progressbar', standalone: true, template: "@if (displayProgressBar) {\n  @if (computedText) {\n    <div class=\"progressbar-text mb-2\">{{ computedText }}</div>\n  }\n  @if (infiniteAnimation) {\n    <div\n      class=\"progress\"\n      role=\"progressbar\"\n      aria-valuetext=\"indeterminate\"\n      [attr.aria-labelledby]=\"ariaLabelledBy || null\"\n      [attr.aria-label]=\"!ariaLabelledBy && ariaLabel ? ariaLabel : null\"\n    >\n      <div class=\"progress-bar infinite-animation\"></div>\n    </div>\n  } @else {\n    <div\n      class=\"progress\"\n      role=\"progressbar\"\n      [attr.aria-valuenow]=\"value\"\n      aria-valuemin=\"0\"\n      [attr.aria-valuemax]=\"maxValue\"\n      [attr.aria-valuetext]=\"computedText ? computedText : null\"\n      [attr.aria-labelledby]=\"ariaLabelledBy || null\"\n      [attr.aria-label]=\"!ariaLabelledBy && ariaLabel ? ariaLabel : null\"\n    >\n      <div [style.width.%]=\"percentageValue\" class=\"progress-bar\"></div>\n    </div>\n  }\n}\n" }]
        }], propDecorators: { infiniteAnimation: [{
                type: Input
            }], value: [{
                type: Input
            }], maxValue: [{
                type: Input
            }], text: [{
                type: Input
            }], start$: [{
                type: Input
            }], end$: [{
                type: Input
            }], ariaLabelledBy: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], percentageValueChange: [{
                type: Output
            }] } });

class DfProgressbarModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfProgressbarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.7", ngImport: i0, type: DfProgressbarModule, imports: [DfProgressbarComponent], exports: [DfProgressbarComponent, NgbProgressbarModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfProgressbarModule, imports: [NgbProgressbarModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfProgressbarModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [DfProgressbarComponent],
                    exports: [DfProgressbarComponent, NgbProgressbarModule]
                }]
        }] });

var DfProgressIndicatorContentType;
(function (DfProgressIndicatorContentType) {
    DfProgressIndicatorContentType["SPINNER"] = "spinner";
    DfProgressIndicatorContentType["PROGRESSBAR"] = "progressbar";
})(DfProgressIndicatorContentType || (DfProgressIndicatorContentType = {}));
class DfProgressIndicatorContentSpinner {
    constructor(spinnerInput) {
        this.type = DfProgressIndicatorContentType.SPINNER;
        this.spinnerInput = spinnerInput;
    }
}
class DfProgressIndicatorContentProgressBar {
    constructor(progressBarInput, position) {
        this.type = DfProgressIndicatorContentType.PROGRESSBAR;
        this.progressBarInput = progressBarInput;
        this.position = position || DfProgressIndicatorContentPosition.TOP;
    }
}
var DfProgressIndicatorContentPosition;
(function (DfProgressIndicatorContentPosition) {
    DfProgressIndicatorContentPosition["TOP"] = "top";
    DfProgressIndicatorContentPosition["BOTTOM"] = "bottom";
    DfProgressIndicatorContentPosition["CENTERED"] = "centered";
})(DfProgressIndicatorContentPosition || (DfProgressIndicatorContentPosition = {}));

class DfProgressIndicatorContainerComponent {
    constructor() {
        this.DEFAULT_CLASS_POSITION = 'df-progressindicator-centered';
        this.positionClass = this.DEFAULT_CLASS_POSITION;
    }
    get classes() {
        return `df-progressindicator-container ${this.containerClass || ''} ${this.positionClass}`;
    }
    set position(newPosition) {
        switch (newPosition) {
            case DfProgressIndicatorContentPosition.TOP:
                this.positionClass = 'df-progressindicator-top';
                break;
            case DfProgressIndicatorContentPosition.BOTTOM:
                this.positionClass = 'df-progressindicator-bottom';
                break;
            default:
                this.positionClass = this.DEFAULT_CLASS_POSITION;
        }
        this._position = newPosition;
    }
    get position() {
        return this._position;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfProgressIndicatorContainerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.7", type: DfProgressIndicatorContainerComponent, isStandalone: true, selector: "df-progressindicator-container", inputs: { containerClass: "containerClass", position: "position" }, host: { properties: { "class": "this.classes" } }, ngImport: i0, template: `
    <ng-content></ng-content>
  `, isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfProgressIndicatorContainerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'df-progressindicator-container',
                    encapsulation: ViewEncapsulation.None,
                    template: `
    <ng-content></ng-content>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    standalone: true
                }]
        }], propDecorators: { classes: [{
                type: HostBinding,
                args: ['class']
            }], containerClass: [{
                type: Input
            }], position: [{
                type: Input
            }] } });

class DfDefaultSpinnerComponent {
    constructor() {
        this.isGlobalSpinner = false;
        this.contentClass = 'spinner-border text-primary';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfDefaultSpinnerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.7", type: DfDefaultSpinnerComponent, isStandalone: true, selector: "df-default-spinner", inputs: { isGlobalSpinner: "isGlobalSpinner", contentClass: "contentClass", ariaLabel: "ariaLabel" }, ngImport: i0, template: "<div [ngClass]=\"contentClass\" [attr.role]=\"isGlobalSpinner ? 'alert' : 'status'\">\n  <span class=\"visually-hidden\">\n    @if (ariaLabel) {\n      {{ ariaLabel }}\n    } @else {\n      <ng-container i18n=\"@@df.progressindicator.default.spinner.ariaLabel\">Loading...</ng-container>\n    }\n  </span>\n</div>\n", dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfDefaultSpinnerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'df-default-spinner', changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, imports: [CommonModule], template: "<div [ngClass]=\"contentClass\" [attr.role]=\"isGlobalSpinner ? 'alert' : 'status'\">\n  <span class=\"visually-hidden\">\n    @if (ariaLabel) {\n      {{ ariaLabel }}\n    } @else {\n      <ng-container i18n=\"@@df.progressindicator.default.spinner.ariaLabel\">Loading...</ng-container>\n    }\n  </span>\n</div>\n" }]
        }], propDecorators: { isGlobalSpinner: [{
                type: Input
            }], contentClass: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }] } });

class DfProgressIndicatorBackdropComponent {
    constructor() {
        this.isGlobalBackdrop = true;
    }
    get classes() {
        return `df-progressindicator-backdrop ${this.backdropClass || ''} ${this.isGlobalBackdrop ? 'df-progressindicator-backdrop-global' : 'df-progressindicator-backdrop-contextual'}`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfProgressIndicatorBackdropComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.7", type: DfProgressIndicatorBackdropComponent, isStandalone: true, selector: "df-progressindicator-backdrop", inputs: { backdropClass: "backdropClass", isGlobalBackdrop: "isGlobalBackdrop" }, host: { properties: { "class": "this.classes" } }, ngImport: i0, template: '', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfProgressIndicatorBackdropComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'df-progressindicator-backdrop',
                    template: '',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    standalone: true
                }]
        }], propDecorators: { classes: [{
                type: HostBinding,
                args: ['class']
            }], backdropClass: [{
                type: Input
            }], isGlobalBackdrop: [{
                type: Input
            }] } });

class DfProgressIndicatorRef {
    constructor(containerRef, contentRef, backdropRef) {
        this.containerRef = containerRef;
        this.contentRef = contentRef;
        this.backdropRef = backdropRef;
    }
}

const noop$1 = () => { };
/**
 * Utility to handle the scrollbar.
 *
 * It allows to compensate the lack of a vertical scrollbar by adding an
 * equivalent padding on the right of the body, and to remove this compensation.
 *
 * It is a copy of https://github.com/ng-bootstrap/ng-bootstrap/blob/master/src/util/scrollbar.ts
 */
class ScrollBar {
    constructor(_document) {
        this._document = _document;
    }
    /**
     * To be called right before a potential vertical scrollbar would be removed:
     *
     * - if there was a scrollbar, adds some compensation padding to the body
     * to keep the same layout as when the scrollbar is there
     * - if there was none, there is nothing to do
     *
     * @return a callback used to revert the compensation (noop if there was none,
     * otherwise a function removing the padding)
     */
    compensate() {
        const width = this._getWidth();
        return !this._isPresent(width) ? noop$1 : this._adjustBody(width);
    }
    /**
     * Adds a padding of the given width on the right of the body.
     *
     * @return a callback used to revert the padding to its previous value
     */
    _adjustBody(scrollbarWidth) {
        const body = this._document.body;
        const userSetPaddingStyle = body.style.paddingRight;
        const paddingRight = 'padding-right';
        const actualPadding = parseFloat(window.getComputedStyle(body)[paddingRight]);
        body.style[paddingRight] = `${actualPadding + scrollbarWidth}px`;
        return () => (body.style[paddingRight] = userSetPaddingStyle);
    }
    /**
     * Tells whether a scrollbar is currently present on the body.
     *
     * @return true if scrollbar is present, false otherwise
     */
    _isPresent(scrollbarWidth) {
        const rect = this._document.body.getBoundingClientRect();
        const bodyToViewportGap = window.innerWidth - (rect.left + rect.right);
        const uncertainty = 0.1 * scrollbarWidth;
        return bodyToViewportGap >= scrollbarWidth - uncertainty;
    }
    /**
     * Calculates and returns the width of a scrollbar.
     *
     * @return the width of a scrollbar on this page
     */
    _getWidth() {
        const measurer = this._document.createElement('div');
        measurer.className = 'modal-scrollbar-measure';
        const body = this._document.body;
        body.appendChild(measurer);
        const width = measurer.getBoundingClientRect().width - measurer.clientWidth;
        body.removeChild(measurer);
        return width;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: ScrollBar, deps: [{ token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: ScrollBar, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: ScrollBar, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }] });

class DfProgressIndicatorService {
    constructor(document, rendererFactory, componentFactoryResolver, appRef, injector, scrollbar) {
        this.document = document;
        this.rendererFactory = rendererFactory;
        this.componentFactoryResolver = componentFactoryResolver;
        this.appRef = appRef;
        this.injector = injector;
        this.scrollbar = scrollbar;
        this.BODY_CLASS = 'df-progressindicator-open';
        // TODO - Remove it when https://github.com/angular/angular/issues/22567
        this.containerAttributes = ['containerClass', 'position'];
        this.backdropAttributes = ['backdropClass', 'isGlobalBackdrop'];
        this.defaultSpinnerAttributes = ['contentClass', 'ariaLabel', 'isGlobalSpinner'];
        this.defaultProgressBarAttributes = ['infiniteAnimation', 'ariaLabel'];
        this.activeInstances = [];
        this.renderer = this.rendererFactory.createRenderer(null, null);
    }
    open(content, options) {
        const containerElt = this.getContainer(this.document, options);
        if (!containerElt) {
            throw new Error(`[Design Factory] The specified progress indicator container "${options?.container ||
                'body'}" was not found in the DOM.`);
        }
        // Create needed components
        const backdropRef = options?.backdrop !== false
            ? this.createComponent(DfProgressIndicatorBackdropComponent, this.componentFactoryResolver, undefined, containerElt)
            : undefined;
        const contentRef = this.getContent(content, this.componentFactoryResolver, containerElt);
        const containerCmpRef = this.createComponent(DfProgressIndicatorContainerComponent, this.componentFactoryResolver, contentRef, containerElt);
        const onDestroyOperations = [];
        // Add aria attributes on the container
        this.renderer.setAttribute(containerElt, 'aria-busy', 'true');
        onDestroyOperations.push(() => {
            this.renderer.removeAttribute(containerElt, 'aria-busy');
        });
        // Override some styles if body is the container
        if (containerElt === this.document.body) {
            const scrollCompensation = this.scrollbar.compensate();
            this.renderer.addClass(this.document.body, this.BODY_CLASS);
            this.renderer.setAttribute(this.document.body, 'aria-busy', 'true');
            onDestroyOperations.push(() => {
                scrollCompensation();
                this.renderer.removeClass(this.document.body, this.BODY_CLASS);
            });
        }
        if (onDestroyOperations.length > 0) {
            containerCmpRef.onDestroy(() => {
                onDestroyOperations.forEach(operation => {
                    operation();
                });
            });
        }
        // Force override of position in case of the use of a progress bar component
        if (content instanceof DfProgressIndicatorContentProgressBar) {
            options = {
                ...options,
                position: content.position
            };
        }
        // Apply options to the created components
        if (backdropRef) {
            this.applyOptions(backdropRef, this.backdropAttributes, { ...options, isGlobalBackdrop: containerElt === this.document.body });
        }
        this.applyOptions(containerCmpRef, this.containerAttributes, options);
        const progressIndicatorRef = new DfProgressIndicatorRef(containerCmpRef, contentRef, backdropRef);
        this.activeInstances.push(progressIndicatorRef);
        return progressIndicatorRef;
    }
    close(progressIndicatorRef) {
        const instanceIndex = this.activeInstances.indexOf(progressIndicatorRef);
        if (instanceIndex > -1) {
            Object.keys(progressIndicatorRef).forEach(key => {
                const propertyValue = progressIndicatorRef[key];
                if (propertyValue && propertyValue instanceof ComponentRef) {
                    propertyValue.destroy();
                    progressIndicatorRef[key] = undefined;
                }
            });
            this.activeInstances.splice(instanceIndex, 1);
        }
        else {
            throw new Error('[Design Factory] The specified progress indicator reference is no longer active and cannot be closed.');
        }
    }
    /**
     * Retrieve container according to the desired options.
     */
    getContainer(document, options) {
        if (options?.container instanceof HTMLElement) {
            return options.container;
        }
        return options?.container ? document.querySelector(options.container) : document.body;
    }
    getContent(content, cfr, container) {
        if (!content) {
            return this.createComponent(DfDefaultSpinnerComponent, cfr);
        }
        else if (content instanceof DfProgressIndicatorContentSpinner) {
            const cmpRef = this.createComponent(DfDefaultSpinnerComponent, cfr);
            this.applyOptions(cmpRef, this.defaultSpinnerAttributes, { ...content.spinnerInput, isGlobalSpinner: container === this.document.body });
            return cmpRef;
        }
        else if (content instanceof DfProgressIndicatorContentProgressBar) {
            const cmpRef = this.createComponent(DfProgressbarComponent, cfr);
            this.applyOptions(cmpRef, this.defaultProgressBarAttributes, { ...content.progressBarInput, infiniteAnimation: true });
            return cmpRef;
        }
        else if (content instanceof TemplateRef) {
            const viewRef = content.createEmbeddedView({});
            this.appRef.attachView(viewRef);
            return viewRef;
        }
        else {
            return this.createComponent(content, cfr);
        }
    }
    createComponent(cmpType, cfr, childrenProjectables, container) {
        const cmpFactory = cfr.resolveComponentFactory(cmpType);
        let cmpRef;
        if (childrenProjectables) {
            if (childrenProjectables instanceof ComponentRef) {
                cmpRef = cmpFactory.create(this.injector, [[childrenProjectables.location.nativeElement]]);
            }
            else {
                cmpRef = cmpFactory.create(this.injector, [childrenProjectables.rootNodes]);
            }
        }
        else {
            cmpRef = cmpFactory.create(this.injector);
        }
        this.appRef.attachView(cmpRef.hostView);
        if (container) {
            container.appendChild(cmpRef.location.nativeElement);
        }
        return cmpRef;
    }
    applyOptions(cmpRef, availableOptions, options) {
        if (options) {
            const cmpInst = cmpRef.instance;
            availableOptions.forEach((option) => {
                const optionValue = options[option];
                if (optionValue !== null && optionValue !== undefined) {
                    cmpInst[option] = optionValue;
                }
            });
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfProgressIndicatorService, deps: [{ token: DOCUMENT }, { token: i0.RendererFactory2 }, { token: i0.ComponentFactoryResolver }, { token: i0.ApplicationRef }, { token: i0.Injector }, { token: ScrollBar }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfProgressIndicatorService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfProgressIndicatorService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i0.RendererFactory2 }, { type: i0.ComponentFactoryResolver }, { type: i0.ApplicationRef }, { type: i0.Injector }, { type: ScrollBar }] });

class DfProgressIndicatorDirective {
    constructor(elementRef, progressService) {
        this.elementRef = elementRef;
        this.progressService = progressService;
    }
    ngOnChanges(changes) {
        if (changes['dfProgressIndicator']) {
            const isProgressing = this.dfProgressIndicator;
            if (isProgressing) {
                const config = {
                    ...this.dfProgressIndicatorConfig?.options,
                    container: this.elementRef.nativeElement
                };
                this.progressIndicatorRef = this.progressService.open(this.dfProgressIndicatorConfig?.content, config);
            }
            else if (!isProgressing && this.progressIndicatorRef) {
                this.progressService.close(this.progressIndicatorRef);
                this.progressIndicatorRef = undefined;
            }
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfProgressIndicatorDirective, deps: [{ token: i0.ElementRef }, { token: DfProgressIndicatorService }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.7", type: DfProgressIndicatorDirective, isStandalone: true, selector: "[dfProgressIndicator]", inputs: { dfProgressIndicator: "dfProgressIndicator", dfProgressIndicatorConfig: "dfProgressIndicatorConfig" }, usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfProgressIndicatorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[dfProgressIndicator]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: DfProgressIndicatorService }], propDecorators: { dfProgressIndicator: [{
                type: Input
            }], dfProgressIndicatorConfig: [{
                type: Input
            }] } });

class DfProgressIndicatorModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfProgressIndicatorModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.7", ngImport: i0, type: DfProgressIndicatorModule, imports: [DfDefaultSpinnerComponent,
            DfProgressbarModule,
            DfProgressIndicatorBackdropComponent,
            DfProgressIndicatorContainerComponent,
            DfProgressIndicatorDirective], exports: [DfProgressbarModule,
            DfProgressIndicatorContainerComponent,
            DfProgressIndicatorDirective,
            DfProgressIndicatorBackdropComponent,
            DfDefaultSpinnerComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfProgressIndicatorModule, imports: [DfDefaultSpinnerComponent,
            DfProgressbarModule, DfProgressbarModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfProgressIndicatorModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        DfDefaultSpinnerComponent,
                        DfProgressbarModule,
                        DfProgressIndicatorBackdropComponent,
                        DfProgressIndicatorContainerComponent,
                        DfProgressIndicatorDirective
                    ],
                    exports: [
                        DfProgressbarModule,
                        DfProgressIndicatorContainerComponent,
                        DfProgressIndicatorDirective,
                        DfProgressIndicatorBackdropComponent,
                        DfDefaultSpinnerComponent
                    ]
                }]
        }] });

/**
 * @deprecated DfSliderDirectionDirective is deprecated. Use {@link https://design-factory-int.app.net6.paas.westeurope.rnd.az.amadeus.net/#/components/slider/agnosui  AgnosUI Slider} instead.
 */
class DfSliderDirectionDirective {
    constructor(elementRef, rtlDirectionService) {
        this.elementRef = elementRef;
        this.rtlDirectionService = rtlDirectionService;
    }
    ngOnInit() {
        if (this.rtlDirectionService) {
            const direction = this.rtlDirectionService.getPageDirection(this.elementRef);
            if (direction === RightToLeftDirectionEnum.RightToLeft) {
                this.host.options.rightToLeft = true;
            }
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfSliderDirectionDirective, deps: [{ token: i0.ElementRef }, { token: DfDirectionDetectionService, optional: true }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.7", type: DfSliderDirectionDirective, isStandalone: true, selector: "[dfSliderDirection]", inputs: { host: ["dfSliderDirection", "host"] }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfSliderDirectionDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[dfSliderDirection]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: DfDirectionDetectionService, decorators: [{
                    type: Optional
                }] }], propDecorators: { host: [{
                type: Input,
                args: ['dfSliderDirection']
            }] } });

/** Label type */
var LabelType;
(function (LabelType) {
    /** Label above low pointer */
    LabelType[LabelType["Low"] = 0] = "Low";
    /** Label above high pointer */
    LabelType[LabelType["High"] = 1] = "High";
    /** Label for minimum slider value */
    LabelType[LabelType["Floor"] = 2] = "Floor";
    /** Label for maximum slider value */
    LabelType[LabelType["Ceil"] = 3] = "Ceil";
    /** Label below legend tick */
    LabelType[LabelType["TickValue"] = 4] = "TickValue";
})(LabelType || (LabelType = {}));
/** Slider options */
class Options {
    constructor() {
        /** Minimum value for a slider.
          Not applicable when using stepsArray. */
        this.floor = 0;
        /** Step between each value.
          Not applicable when using stepsArray. */
        this.step = 1;
        /** Set to true to have a push behavior. When the min handle goes above the max,
          the max is moved as well (and vice-versa). The range between min and max is
          defined by the step option (defaults to 1) and can also be overriden by
          the minRange option. Applies to range slider only. */
        this.pushRange = false;
        /** Set to true to bind the index of the selected item to value model and valueHigh model. */
        this.bindIndexForStepsArray = false;
        /** When set to true and using a range slider, the range can be dragged by the selection bar.
          Applies to range slider only. */
        this.draggableRange = false;
        /** Same as draggableRange but the slider range can't be changed.
          Applies to range slider only. */
        this.draggableRangeOnly = false;
        /** Set to true to always show the selection bar before the slider handle. */
        this.showSelectionBar = false;
        /** Set to true to always show the selection bar after the slider handle. */
        this.showSelectionBarEnd = false;
        /**  Only for range slider. Set to true to visualize in different colour the areas
          on the left/right (top/bottom for vertical range slider) of selection bar between the handles. */
        this.showOuterSelectionBars = false;
        /** Set to true to hide pointer labels */
        this.hidePointerLabels = false;
        /** Set to true to hide min / max labels  */
        this.hideLimitLabels = false;
        /** Set to false to disable the auto-hiding behavior of the limit labels. */
        this.autoHideLimitLabels = true;
        /** Set to true to make the slider read-only. */
        this.readOnly = false;
        /** Set to true to disable the slider. */
        this.disabled = false;
        /** Set to true to display a tick for each step of the slider. */
        this.showTicks = false;
        /** Set to true to display a tick and the step value for each step of the slider.. */
        this.showTicksValues = false;
        /** Set to true to display the slider vertically.
          The slider will take the full height of its parent.
          Changing this value at runtime is not currently supported. */
        this.vertical = false;
        /** Handles are focusable (on click or with tab) and can be modified using the following keyboard controls:
          Left/bottom arrows: -1
          Right/top arrows: +1
          Page-down: -10%
          Page-up: +10%
          Home: minimum value
          End: maximum value
         */
        this.keyboardSupport = true;
        /** If you display the slider in an element that uses transform: scale(0.5), set the scale value to 2
          so that the slider is rendered properly and the events are handled correctly. */
        this.scale = 1;
        /** If you display the slider in an element that uses transform: rotate(90deg), set the rotate value to 90
         so that the slider is rendered properly and the events are handled correctly. Value is in degrees. */
        this.rotate = 0;
        /** Set to true to force the value(s) to be rounded to the step, even when modified from the outside.
          When set to false, if the model values are modified from outside the slider, they are not rounded
          and can be between two steps. */
        this.enforceStep = true;
        /** Set to true to force the value(s) to be normalised to allowed range (floor to ceil), even when modified from the outside.
          When set to false, if the model values are modified from outside the slider, and they are outside allowed range,
          the slider may be rendered incorrectly. However, setting this to false may be useful if you want to perform custom normalisation. */
        this.enforceRange = true;
        /** Set to true to force the value(s) to be rounded to the nearest step value, even when modified from the outside.
          When set to false, if the model values are modified from outside the slider, and they are outside allowed range,
          the slider may be rendered incorrectly. However, setting this to false may be useful if you want to perform custom normalisation. */
        this.enforceStepsArray = true;
        /** Set to true to prevent to user from switching the min and max handles. Applies to range slider only. */
        this.noSwitching = false;
        /** Set to true to only bind events on slider handles. */
        this.onlyBindHandles = false;
        /** Set to true to show graphs right to left.
          If vertical is true it will be from top to bottom and left / right arrow functions reversed. */
        this.rightToLeft = false;
        /** Set to true to reverse keyboard navigation:
          Right/top arrows: -1
          Left/bottom arrows: +1
          Page-up: -10%
          Page-down: +10%
          End: minimum value
          Home: maximum value
         */
        this.reversedControls = false;
        /** Set to true to keep the slider labels inside the slider bounds. */
        this.boundPointerLabels = true;
        /** Set to true to use a logarithmic scale to display the slider.  */
        this.logScale = false;
        /** Precision limit for calculated values.
          Values used in calculations will be rounded to this number of significant digits
          to prevent accumulating small floating-point errors. */
        this.precisionLimit = 12;
        /** Use to add a label directly to the slider for accessibility. Adds the aria-label attribute. */
        this.ariaLabel = 'ngx-slider';
        /** Use to add a label directly to the slider range for accessibility. Adds the aria-label attribute. */
        this.ariaLabelHigh = 'ngx-slider-max';
        /** Enable/disable CSS animations */
        this.animate = true;
        /** Enable/disable CSS animations while moving the slider */
        this.animateOnMove = false;
    }
}

/** Pointer type */
var PointerType;
(function (PointerType) {
    /** Low pointer */
    PointerType[PointerType["Min"] = 0] = "Min";
    /** High pointer */
    PointerType[PointerType["Max"] = 1] = "Max";
})(PointerType || (PointerType = {}));

class ChangeContext {
}

/**
 *  Collection of functions to handle conversions/lookups of values
 */
class ValueHelper {
    static isNullOrUndefined(value) {
        return value === undefined || value === null;
    }
    static areArraysEqual(array1, array2) {
        if (array1.length !== array2.length) {
            return false;
        }
        for (let i = 0; i < array1.length; ++i) {
            if (array1[i] !== array2[i]) {
                return false;
            }
        }
        return true;
    }
    static linearValueToPosition(val, minVal, maxVal) {
        const range = maxVal - minVal;
        return (val - minVal) / range;
    }
    static logValueToPosition(val, minVal, maxVal) {
        val = Math.log(val);
        minVal = Math.log(minVal);
        maxVal = Math.log(maxVal);
        const range = maxVal - minVal;
        return (val - minVal) / range;
    }
    static linearPositionToValue(percent, minVal, maxVal) {
        return percent * (maxVal - minVal) + minVal;
    }
    static logPositionToValue(percent, minVal, maxVal) {
        minVal = Math.log(minVal);
        maxVal = Math.log(maxVal);
        const value = percent * (maxVal - minVal) + minVal;
        return Math.exp(value);
    }
    static findStepIndex(modelValue, stepsArray) {
        const differences = stepsArray.map((step) => Math.abs(modelValue - step.value));
        let minDifferenceIndex = 0;
        for (let index = 0; index < stepsArray.length; index++) {
            if (differences[index] !== differences[minDifferenceIndex] && differences[index] < differences[minDifferenceIndex]) {
                minDifferenceIndex = index;
            }
        }
        return minDifferenceIndex;
    }
}

/** Helper with compatibility functions to support different browsers */
class CompatibilityHelper {
    /** Workaround for TouchEvent constructor sadly not being available on all browsers (e.g. Firefox, Safari) */
    static isTouchEvent(event) {
        if (window.TouchEvent !== undefined) {
            return event instanceof TouchEvent;
        }
        return event.touches !== undefined;
    }
    /** Detect presence of ResizeObserver API */
    static isResizeObserverAvailable() {
        return window.ResizeObserver !== undefined;
    }
}

/** Helper with mathematical functions */
class MathHelper {
    /* Round numbers to a given number of significant digits */
    static roundToPrecisionLimit(value, precisionLimit) {
        return +(value.toPrecision(precisionLimit));
    }
    static isModuloWithinPrecisionLimit(value, modulo, precisionLimit) {
        const limit = Math.pow(10, -precisionLimit);
        return Math.abs(value % modulo) <= limit || Math.abs(Math.abs(value % modulo) - modulo) <= limit;
    }
    static clampToRange(value, floor, ceil) {
        return Math.min(Math.max(value, floor), ceil);
    }
}

class EventListener {
}

const w = typeof window !== 'undefined' ? window : { screen: {}, navigator: {} };
let passiveOptionAccessed = false;
const options = {
    get passive() {
        return (passiveOptionAccessed = true);
    }
};
const noop = () => { };
w.addEventListener && w.addEventListener('p', noop, options);
w.removeEventListener && w.removeEventListener('p', noop, false);
const supportsPassiveEvents = passiveOptionAccessed;
/**
 * Helper class to attach event listeners to DOM elements with debounce support using rxjs
 */
class EventListenerHelper {
    constructor(renderer) {
        this.renderer = renderer;
    }
    attachPassiveEventListener(nativeElement, eventName, callback, throttleInterval) {
        // Only use passive event listeners if the browser supports it
        if (supportsPassiveEvents !== true) {
            return this.attachEventListener(nativeElement, eventName, callback, throttleInterval);
        }
        // Angular doesn't support passive event handlers (yet), so we need to roll our own code using native functions
        const listener = new EventListener();
        listener.eventName = eventName;
        listener.events = new Subject();
        const observerCallback = (event) => {
            listener.events?.next(event);
        };
        nativeElement.addEventListener(eventName, observerCallback, { passive: true, capture: false });
        listener.teardownCallback = () => {
            nativeElement.removeEventListener(eventName, observerCallback, { passive: true, capture: false });
        };
        listener.eventsSubscription = listener.events
            .pipe((!ValueHelper.isNullOrUndefined(throttleInterval))
            ? throttleTime(throttleInterval, undefined, { leading: true, trailing: true })
            : tap(() => { }) // no-op
        )
            .subscribe((event) => {
            callback(event);
        });
        return listener;
    }
    detachEventListener(eventListener) {
        if (!ValueHelper.isNullOrUndefined(eventListener.eventsSubscription)) {
            eventListener.eventsSubscription?.unsubscribe();
        }
        if (!ValueHelper.isNullOrUndefined(eventListener.events)) {
            eventListener.events?.complete();
        }
        if (!ValueHelper.isNullOrUndefined(eventListener.teardownCallback)) {
            eventListener.teardownCallback?.();
        }
    }
    attachEventListener(nativeElement, eventName, callback, throttleInterval) {
        const listener = new EventListener();
        listener.eventName = eventName;
        listener.events = new Subject();
        const observerCallback = (event) => {
            listener.events?.next(event);
        };
        listener.teardownCallback = this.renderer.listen(nativeElement, eventName, observerCallback);
        listener.eventsSubscription = listener.events
            .pipe((!ValueHelper.isNullOrUndefined(throttleInterval))
            ? throttleTime(throttleInterval, undefined, { leading: true, trailing: true })
            : tap(() => { }) // no-op
        )
            .subscribe((event) => { callback(event); });
        return listener;
    }
}

/**
 * @deprecated SliderElementDirective is deprecated. Use {@link https://design-factory-int.app.net6.paas.westeurope.rnd.az.amadeus.net/#/components/slider/agnosui  AgnosUI Slider} instead.
 */
class SliderElementDirective {
    get position() {
        return this._position;
    }
    get dimension() {
        return this._dimension;
    }
    get alwaysHide() {
        return this._alwaysHide;
    }
    get vertical() {
        return this._vertical;
    }
    get scale() {
        return this._scale;
    }
    get rotate() {
        return this._rotate;
    }
    constructor(elemRef, renderer, changeDetectionRef) {
        this.elemRef = elemRef;
        this.renderer = renderer;
        this.changeDetectionRef = changeDetectionRef;
        this._position = 0;
        this._dimension = 0;
        this._alwaysHide = false;
        this._vertical = false;
        this._scale = 1;
        this._rotate = 0;
        this.opacity = 1;
        this.visibility = 'visible';
        this.left = '';
        this.bottom = '';
        this.height = '';
        this.width = '';
        this.transform = '';
        this.eventListeners = [];
        this.eventListenerHelper = new EventListenerHelper(this.renderer);
    }
    setAlwaysHide(hide) {
        this._alwaysHide = hide;
        if (hide) {
            this.visibility = 'hidden';
        }
        else {
            this.visibility = 'visible';
        }
    }
    hide() {
        this.opacity = 0;
    }
    show() {
        if (this.alwaysHide) {
            return;
        }
        this.opacity = 1;
    }
    isVisible() {
        if (this.alwaysHide) {
            return false;
        }
        return this.opacity !== 0;
    }
    setVertical(vertical) {
        this._vertical = vertical;
        if (this._vertical) {
            this.left = '';
            this.width = '';
        }
        else {
            this.bottom = '';
            this.height = '';
        }
    }
    setScale(scale) {
        this._scale = scale;
    }
    setRotate(rotate) {
        this._rotate = rotate;
        this.transform = 'rotate(' + rotate + 'deg)';
    }
    getRotate() {
        return this._rotate;
    }
    // Set element left/top position depending on whether slider is horizontal or vertical
    setPosition(pos) {
        if (this._position !== pos && !this.isRefDestroyed()) {
            this.changeDetectionRef.markForCheck();
        }
        this._position = pos;
        if (this._vertical) {
            this.bottom = Math.round(pos) + 'px';
        }
        else {
            this.left = Math.round(pos) + 'px';
        }
    }
    // Calculate element's width/height depending on whether slider is horizontal or vertical
    calculateDimension() {
        const val = this.getBoundingClientRect();
        if (this.vertical) {
            this._dimension = (val.bottom - val.top) * this.scale;
        }
        else {
            this._dimension = (val.right - val.left) * this.scale;
        }
    }
    // Set element width/height depending on whether slider is horizontal or vertical
    setDimension(dim) {
        if (this._dimension !== dim && !this.isRefDestroyed()) {
            this.changeDetectionRef.markForCheck();
        }
        this._dimension = dim;
        if (this._vertical) {
            this.height = Math.round(dim) + 'px';
        }
        else {
            this.width = Math.round(dim) + 'px';
        }
    }
    getBoundingClientRect() {
        return this.elemRef.nativeElement.getBoundingClientRect();
    }
    on(eventName, callback, debounceInterval) {
        const listener = this.eventListenerHelper.attachEventListener(this.elemRef.nativeElement, eventName, callback, debounceInterval);
        this.eventListeners.push(listener);
    }
    onPassive(eventName, callback, debounceInterval) {
        const listener = this.eventListenerHelper.attachPassiveEventListener(this.elemRef.nativeElement, eventName, callback, debounceInterval);
        this.eventListeners.push(listener);
    }
    off(eventName) {
        let listenersToKeep;
        let listenersToRemove;
        if (!ValueHelper.isNullOrUndefined(eventName)) {
            listenersToKeep = this.eventListeners.filter((event) => event.eventName !== eventName);
            listenersToRemove = this.eventListeners.filter((event) => event.eventName === eventName);
        }
        else {
            listenersToKeep = [];
            listenersToRemove = this.eventListeners;
        }
        for (const listener of listenersToRemove) {
            this.eventListenerHelper.detachEventListener(listener);
        }
        this.eventListeners = listenersToKeep;
    }
    isRefDestroyed() {
        return (ValueHelper.isNullOrUndefined(this.changeDetectionRef) || this.changeDetectionRef['destroyed']);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: SliderElementDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.7", type: SliderElementDirective, isStandalone: true, selector: "[ngxSliderElement]", host: { properties: { "style.opacity": "this.opacity", "style.visibility": "this.visibility", "style.left": "this.left", "style.bottom": "this.bottom", "style.height": "this.height", "style.width": "this.width", "style.transform": "this.transform" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: SliderElementDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: '[ngxSliderElement]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i0.ChangeDetectorRef }], propDecorators: { opacity: [{
                type: HostBinding,
                args: ['style.opacity']
            }], visibility: [{
                type: HostBinding,
                args: ['style.visibility']
            }], left: [{
                type: HostBinding,
                args: ['style.left']
            }], bottom: [{
                type: HostBinding,
                args: ['style.bottom']
            }], height: [{
                type: HostBinding,
                args: ['style.height']
            }], width: [{
                type: HostBinding,
                args: ['style.width']
            }], transform: [{
                type: HostBinding,
                args: ['style.transform']
            }] } });

/**
 * @deprecated SliderHandleDirective is deprecated. Use {@link https://design-factory-int.app.net6.paas.westeurope.rnd.az.amadeus.net/#/components/slider/agnosui  AgnosUI Slider} instead.
 */
class SliderHandleDirective extends SliderElementDirective {
    constructor(elemRef, renderer, changeDetectionRef) {
        super(elemRef, renderer, changeDetectionRef);
        this.active = false;
        this.role = '';
        this.tabindex = '';
        this.ariaOrientation = '';
        this.ariaLabel = '';
        this.ariaLabelledBy = '';
        this.ariaValueNow = '';
        this.ariaValueText = '';
        this.ariaValueMin = '';
        this.ariaValueMax = '';
    }
    focus() {
        this.elemRef.nativeElement.focus();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: SliderHandleDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.7", type: SliderHandleDirective, isStandalone: true, selector: "[ngxSliderHandle]", host: { properties: { "class.ngx-slider-active": "this.active", "attr.role": "this.role", "attr.tabindex": "this.tabindex", "attr.aria-orientation": "this.ariaOrientation", "attr.aria-label": "this.ariaLabel", "attr.aria-labelledby": "this.ariaLabelledBy", "attr.aria-valuenow": "this.ariaValueNow", "attr.aria-valuetext": "this.ariaValueText", "attr.aria-valuemin": "this.ariaValueMin", "attr.aria-valuemax": "this.ariaValueMax" } }, usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: SliderHandleDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: '[ngxSliderHandle]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i0.ChangeDetectorRef }], propDecorators: { active: [{
                type: HostBinding,
                args: ['class.ngx-slider-active']
            }], role: [{
                type: HostBinding,
                args: ['attr.role']
            }], tabindex: [{
                type: HostBinding,
                args: ['attr.tabindex']
            }], ariaOrientation: [{
                type: HostBinding,
                args: ['attr.aria-orientation']
            }], ariaLabel: [{
                type: HostBinding,
                args: ['attr.aria-label']
            }], ariaLabelledBy: [{
                type: HostBinding,
                args: ['attr.aria-labelledby']
            }], ariaValueNow: [{
                type: HostBinding,
                args: ['attr.aria-valuenow']
            }], ariaValueText: [{
                type: HostBinding,
                args: ['attr.aria-valuetext']
            }], ariaValueMin: [{
                type: HostBinding,
                args: ['attr.aria-valuemin']
            }], ariaValueMax: [{
                type: HostBinding,
                args: ['attr.aria-valuemax']
            }] } });

/**
 * @deprecated SliderLabelDirective is deprecated. Use {@link https://design-factory-int.app.net6.paas.westeurope.rnd.az.amadeus.net/#/components/slider/agnosui  AgnosUI Slider} instead.
 */
class SliderLabelDirective extends SliderElementDirective {
    get value() {
        return this._value || '';
    }
    constructor(elemRef, renderer, changeDetectionRef) {
        super(elemRef, renderer, changeDetectionRef);
    }
    setValue(value) {
        let recalculateDimension = false;
        if (!this.alwaysHide &&
            (ValueHelper.isNullOrUndefined(this.value) ||
                this.value.length !== value.length ||
                (this.value.length > 0 && this.dimension === 0))) {
            recalculateDimension = true;
        }
        this._value = value;
        this.elemRef.nativeElement.innerHTML = value;
        // Update dimension only when length of the label have changed
        if (recalculateDimension) {
            this.calculateDimension();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: SliderLabelDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.7", type: SliderLabelDirective, isStandalone: true, selector: "[ngxSliderLabel]", usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: SliderLabelDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: '[ngxSliderLabel]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i0.ChangeDetectorRef }] });

/**
 * @deprecated TooltipWrapperComponent is deprecated. Use {@link https://design-factory-int.app.net6.paas.westeurope.rnd.az.amadeus.net/#/components/slider/agnosui  AgnosUI Slider} instead.
 */
class TooltipWrapperComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: TooltipWrapperComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.7", type: TooltipWrapperComponent, isStandalone: true, selector: "ngx-slider-tooltip-wrapper", inputs: { template: "template", tooltip: "tooltip", placement: "placement", content: "content" }, ngImport: i0, template: "@if (template) {\n  <ng-template\n    *ngTemplateOutlet=\"template; context: { tooltip: tooltip, placement: placement, content: content }\"\n  ></ng-template>\n} @else {\n  <div class=\"ngx-slider-inner-tooltip\" [attr.title]=\"tooltip\" [attr.data-tooltip-placement]=\"placement\">\n    {{ content }}\n  </div>\n}\n", styles: [".ngx-slider-inner-tooltip{height:100%}\n"], dependencies: [{ kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: TooltipWrapperComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ngx-slider-tooltip-wrapper', changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, imports: [NgTemplateOutlet], template: "@if (template) {\n  <ng-template\n    *ngTemplateOutlet=\"template; context: { tooltip: tooltip, placement: placement, content: content }\"\n  ></ng-template>\n} @else {\n  <div class=\"ngx-slider-inner-tooltip\" [attr.title]=\"tooltip\" [attr.data-tooltip-placement]=\"placement\">\n    {{ content }}\n  </div>\n}\n", styles: [".ngx-slider-inner-tooltip{height:100%}\n"] }]
        }], propDecorators: { template: [{
                type: Input
            }], tooltip: [{
                type: Input
            }], placement: [{
                type: Input
            }], content: [{
                type: Input
            }] } });

class Tick {
    constructor() {
        this.selected = false;
        this.style = {};
    }
}
class Dragging {
    constructor() {
        this.active = false;
        this.value = 0;
        this.difference = 0;
        this.position = 0;
        this.lowLimit = 0;
        this.highLimit = 0;
    }
}
class ModelValues {
    static compare(x, y) {
        if (ValueHelper.isNullOrUndefined(x) && ValueHelper.isNullOrUndefined(y)) {
            return false;
        }
        if (ValueHelper.isNullOrUndefined(x) !== ValueHelper.isNullOrUndefined(y)) {
            return false;
        }
        return x.value === y.value && x.highValue === y.highValue;
    }
}
class ModelChange extends ModelValues {
    static compare(x, y) {
        if (ValueHelper.isNullOrUndefined(x) && ValueHelper.isNullOrUndefined(y)) {
            return false;
        }
        if (ValueHelper.isNullOrUndefined(x) !== ValueHelper.isNullOrUndefined(y)) {
            return false;
        }
        return x.value === y.value && x.highValue === y.highValue && x.forceChange === y.forceChange;
    }
}
class InputModelChange extends ModelChange {
}
class OutputModelChange extends ModelChange {
}
const NGX_SLIDER_CONTROL_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    /* tslint:disable-next-line: no-use-before-declare */
    useExisting: forwardRef(() => SliderComponent),
    multi: true
};
/**
 * @deprecated SliderComponent is deprecated. Use {@link https://design-factory-int.app.net6.paas.westeurope.rnd.az.amadeus.net/#/components/slider/agnosui  AgnosUI Slider} instead.
 */
class SliderComponent {
    // Input event that triggers slider refresh (re-positioning of slider elements)
    set manualRefresh(manualRefresh) {
        this.unsubscribeManualRefresh();
        this.manualRefreshSubscription = manualRefresh.subscribe(() => {
            setTimeout(() => this.calculateViewDimensionsAndDetectChanges());
        });
    }
    // Input event that triggers setting focus on given slider handle
    set triggerFocus(triggerFocus) {
        this.unsubscribeTriggerFocus();
        this.triggerFocusSubscription = triggerFocus.subscribe((pointerType) => {
            this.focusPointer(pointerType);
        });
    }
    // Slider type, true means range slider
    get range() {
        return !ValueHelper.isNullOrUndefined(this.value) && !ValueHelper.isNullOrUndefined(this.highValue);
    }
    // Whether to show/hide ticks
    get showTicks() {
        return this.viewOptions.showTicks || false;
    }
    constructor(renderer, elementRef, changeDetectionRef, zone) {
        this.renderer = renderer;
        this.elementRef = elementRef;
        this.changeDetectionRef = changeDetectionRef;
        this.zone = zone;
        this.class = 'ngx-slider';
        // Output for low value slider to support two-way bindings
        this.valueChange = new EventEmitter();
        // Output for high value slider to support two-way bindings
        this.highValueChange = new EventEmitter();
        // An object with all the other options of the slider.
        // Each option can be updated at runtime and the slider will automatically be re-rendered.
        this.options = new Options();
        // Event emitted when user starts interaction with the slider
        this.userChangeStart = new EventEmitter();
        // Event emitted on each change coming from user interaction
        this.userChange = new EventEmitter();
        // Event emitted when user finishes interaction with the slider
        this.userChangeEnd = new EventEmitter();
        // Set to true if init method already executed
        this.initHasRun = false;
        // Changes in model inputs are passed through this subject
        // These are all changes coming in from outside the component through input bindings or reactive form inputs
        this.inputModelChangeSubject = new Subject();
        // Changes to model outputs are passed through this subject
        // These are all changes that need to be communicated to output emitters and registered callbacks
        this.outputModelChangeSubject = new Subject();
        // Options synced to model options, based on defaults
        this.viewOptions = new Options();
        // Half of the width or height of the slider handles
        this.handleHalfDimension = 0;
        // Maximum position the slider handle can have
        this.maxHandlePosition = 0;
        // Used to call onStart on the first keydown event
        this.firstKeyDown = false;
        // Values recorded when first dragging the bar
        this.dragging = new Dragging();
        // Host element class bindings
        this.sliderElementVerticalClass = false;
        this.sliderElementAnimateClass = false;
        this.sliderElementWithLegendClass = false;
        this.sliderElementAriaLabel = 'ngx-slider';
        // CSS styles and class flags
        this.barStyle = {};
        this.minPointerStyle = {};
        this.maxPointerStyle = {};
        this.fullBarTransparentClass = false;
        this.selectionBarDraggableClass = false;
        this.ticksUnderValuesClass = false;
        /* If tickStep is set or ticksArray is specified.
           In this case, ticks values should be displayed below the slider. */
        this.intermediateTicks = false;
        // Ticks array as displayed in view
        this.ticks = [];
        // Whether currently moving the slider (between onStart() and onEnd())
        this.moving = false;
        this.eventListenerHelper = new EventListenerHelper(this.renderer);
    }
    // OnInit interface
    ngOnInit() {
        this.viewOptions = new Options();
        Object.assign(this.viewOptions, this.options);
        // We need to run these two things first, before the rest of the init in ngAfterViewInit(),
        // because these two settings are set through @HostBinding and Angular change detection
        // mechanism doesn't like them changing in ngAfterViewInit()
        this.updateDisabledState();
        this.updateVerticalState();
        this.updateAriaLabel();
    }
    // AfterViewInit interface
    ngAfterViewInit() {
        this.applyOptions();
        this.subscribeInputModelChangeSubject();
        this.subscribeOutputModelChangeSubject();
        // Once we apply options, we need to normalise model values for the first time
        this.renormaliseModelValues();
        this.viewLowValue = this.modelValueToViewValue(this.value);
        if (this.range) {
            this.viewHighValue = this.modelValueToViewValue(this.highValue);
        }
        else {
            this.viewHighValue = undefined;
        }
        this.updateVerticalState(); // need to run this again to cover changes to slider elements
        this.manageElementsStyle();
        this.updateDisabledState();
        this.calculateViewDimensions();
        this.addAccessibility();
        this.updateCeilLabel();
        this.updateFloorLabel();
        this.initHandles();
        this.manageEventsBindings();
        this.updateAriaLabel();
        this.subscribeResizeObserver();
        this.initHasRun = true;
        // Run change detection manually to resolve some issues when init procedure changes values used in the view
        if (!this.isRefDestroyed()) {
            this.changeDetectionRef.detectChanges();
        }
    }
    // OnChanges interface
    ngOnChanges(changes) {
        // Always apply options first
        const options = changes['options'];
        if (!ValueHelper.isNullOrUndefined(options) &&
            JSON.stringify(options.previousValue) !== JSON.stringify(options.currentValue)) {
            this.onChangeOptions();
        }
        // Then value changes
        if (!ValueHelper.isNullOrUndefined(changes['value']) || !ValueHelper.isNullOrUndefined(changes['highValue'])) {
            this.inputModelChangeSubject.next({
                value: this.value,
                highValue: this.highValue,
                forceChange: false,
                internalChange: false
            });
        }
    }
    // OnDestroy interface
    ngOnDestroy() {
        this.unbindEvents();
        this.unsubscribeResizeObserver();
        this.unsubscribeInputModelChangeSubject();
        this.unsubscribeOutputModelChangeSubject();
        this.unsubscribeManualRefresh();
        this.unsubscribeTriggerFocus();
    }
    // ControlValueAccessor interface
    writeValue(obj) {
        if (obj instanceof Array) {
            this.value = obj[0];
            this.highValue = obj[1];
        }
        else {
            this.value = obj;
        }
        // ngOnChanges() is not called in this instance, so we need to communicate the change manually
        this.inputModelChangeSubject.next({
            value: this.value,
            highValue: this.highValue,
            forceChange: false,
            internalChange: false
        });
    }
    // ControlValueAccessor interface
    registerOnChange(onChangeCallback) {
        this.onChangeCallback = onChangeCallback;
    }
    // ControlValueAccessor interface
    registerOnTouched(onTouchedCallback) {
        this.onTouchedCallback = onTouchedCallback;
    }
    // ControlValueAccessor interface
    setDisabledState(isDisabled) {
        this.viewOptions.disabled = isDisabled;
        this.updateDisabledState();
    }
    setAriaLabel(ariaLabel) {
        this.viewOptions.ariaLabel = ariaLabel;
        this.updateAriaLabel();
    }
    onResize(event) {
        this.calculateViewDimensionsAndDetectChanges();
    }
    subscribeInputModelChangeSubject() {
        this.inputModelChangeSubscription = this.inputModelChangeSubject
            .pipe(distinctUntilChanged(ModelChange.compare), 
        // Hack to reset the status of the distinctUntilChanged() - if a "fake" event comes through with forceChange=true,
        // we forcefully by-pass distinctUntilChanged(), but otherwise drop the event
        filter((modelChange) => !modelChange.forceChange && !modelChange.internalChange))
            .subscribe((modelChange) => this.applyInputModelChange(modelChange));
    }
    subscribeOutputModelChangeSubject() {
        this.outputModelChangeSubscription = this.outputModelChangeSubject
            .pipe(distinctUntilChanged(ModelChange.compare))
            .subscribe((modelChange) => this.publishOutputModelChange(modelChange));
    }
    subscribeResizeObserver() {
        if (CompatibilityHelper.isResizeObserverAvailable()) {
            this.resizeObserver = new ResizeObserver(() => this.calculateViewDimensionsAndDetectChanges());
            this.resizeObserver.observe(this.elementRef.nativeElement);
        }
    }
    unsubscribeResizeObserver() {
        if (CompatibilityHelper.isResizeObserverAvailable() && this.resizeObserver !== null) {
            this.resizeObserver?.disconnect();
            this.resizeObserver = undefined;
        }
    }
    unsubscribeOnMove() {
        if (!ValueHelper.isNullOrUndefined(this.onMoveEventListener)) {
            this.eventListenerHelper?.detachEventListener(this.onMoveEventListener);
            this.onMoveEventListener = undefined;
        }
    }
    unsubscribeOnEnd() {
        if (!ValueHelper.isNullOrUndefined(this.onEndEventListener)) {
            this.eventListenerHelper?.detachEventListener(this.onEndEventListener);
            this.onEndEventListener = undefined;
        }
    }
    unsubscribeInputModelChangeSubject() {
        if (!ValueHelper.isNullOrUndefined(this.inputModelChangeSubscription)) {
            this.inputModelChangeSubscription?.unsubscribe();
            this.inputModelChangeSubscription = undefined;
        }
    }
    unsubscribeOutputModelChangeSubject() {
        if (!ValueHelper.isNullOrUndefined(this.outputModelChangeSubscription)) {
            this.outputModelChangeSubscription?.unsubscribe();
            this.outputModelChangeSubscription = undefined;
        }
    }
    unsubscribeManualRefresh() {
        if (!ValueHelper.isNullOrUndefined(this.manualRefreshSubscription)) {
            this.manualRefreshSubscription.unsubscribe();
            this.manualRefreshSubscription = undefined;
        }
    }
    unsubscribeTriggerFocus() {
        if (!ValueHelper.isNullOrUndefined(this.triggerFocusSubscription)) {
            this.triggerFocusSubscription.unsubscribe();
            this.triggerFocusSubscription = null;
        }
    }
    getPointerElement(pointerType) {
        if (pointerType === PointerType.Min) {
            return this.minHandleElement;
        }
        else if (pointerType === PointerType.Max) {
            return this.maxHandleElement;
        }
        return undefined;
    }
    getCurrentTrackingValue() {
        if (this.currentTrackingPointer === PointerType.Min) {
            return this.viewLowValue;
        }
        else if (this.currentTrackingPointer === PointerType.Max) {
            return this.viewHighValue;
        }
        return undefined;
    }
    modelValueToViewValue(modelValue) {
        if (ValueHelper.isNullOrUndefined(modelValue)) {
            return NaN;
        }
        if (!ValueHelper.isNullOrUndefined(this.viewOptions.stepsArray) && !this.viewOptions.bindIndexForStepsArray) {
            return ValueHelper.findStepIndex(+modelValue, this.viewOptions.stepsArray);
        }
        return +modelValue;
    }
    viewValueToModelValue(viewValue) {
        if (!ValueHelper.isNullOrUndefined(this.viewOptions.stepsArray) && !this.viewOptions.bindIndexForStepsArray) {
            return this.getStepValue(viewValue);
        }
        return viewValue;
    }
    getStepValue(sliderValue) {
        const step = this.viewOptions.stepsArray[sliderValue];
        return !ValueHelper.isNullOrUndefined(step) ? step.value : NaN;
    }
    applyViewChange() {
        this.value = this.viewValueToModelValue(this.viewLowValue);
        if (this.range) {
            this.highValue = this.viewValueToModelValue(this.viewHighValue);
        }
        this.outputModelChangeSubject.next({
            value: this.value,
            highValue: this.highValue,
            userEventInitiated: true,
            forceChange: false
        });
        // At this point all changes are applied and outputs are emitted, so we should be done.
        // However, input changes are communicated in different stream and we need to be ready to
        // act on the next input change even if it is exactly the same as last input change.
        // Therefore, we send a special event to reset the stream.
        this.inputModelChangeSubject.next({
            value: this.value,
            highValue: this.highValue,
            forceChange: false,
            internalChange: true
        });
    }
    // Apply model change to the slider view
    applyInputModelChange(modelChange) {
        const normalisedModelChange = this.normaliseModelValues(modelChange);
        // If normalised model change is different, apply the change to the model values
        const normalisationChange = !ModelValues.compare(modelChange, normalisedModelChange);
        if (normalisationChange) {
            this.value = normalisedModelChange.value;
            this.highValue = normalisedModelChange.highValue;
        }
        this.viewLowValue = this.modelValueToViewValue(normalisedModelChange.value);
        if (this.range) {
            this.viewHighValue = this.modelValueToViewValue(normalisedModelChange.highValue);
        }
        else {
            this.viewHighValue = undefined;
        }
        this.updateLowHandle(this.valueToPosition(this.viewLowValue));
        if (this.range) {
            this.updateHighHandle(this.valueToPosition(this.viewHighValue));
        }
        this.updateSelectionBar();
        this.updateTicksScale();
        this.updateAriaAttributes();
        if (this.range) {
            this.updateCombinedLabel();
        }
        // At the end, we need to communicate the model change to the outputs as well
        // Normalisation changes are also always forced out to ensure that subscribers always end up in correct state
        this.outputModelChangeSubject.next({
            value: normalisedModelChange.value,
            highValue: normalisedModelChange.highValue,
            forceChange: normalisationChange,
            userEventInitiated: false
        });
    }
    // Publish model change to output event emitters and registered callbacks
    publishOutputModelChange(modelChange) {
        const emitOutputs = () => {
            this.valueChange.emit(modelChange.value);
            if (this.range) {
                this.highValueChange.emit(modelChange.highValue);
            }
            if (!ValueHelper.isNullOrUndefined(this.onChangeCallback)) {
                if (this.range) {
                    this.onChangeCallback([modelChange.value, modelChange.highValue]);
                }
                else {
                    this.onChangeCallback(modelChange.value);
                }
            }
            if (!ValueHelper.isNullOrUndefined(this.onTouchedCallback)) {
                if (this.range) {
                    this.onTouchedCallback([modelChange.value, modelChange.highValue]);
                }
                else {
                    this.onTouchedCallback(modelChange.value);
                }
            }
        };
        if (modelChange.userEventInitiated) {
            // If this change was initiated by a user event, we can emit outputs in the same tick
            emitOutputs();
            this.userChange.emit(this.getChangeContext());
        }
        else {
            // But, if the change was initated by something else like a change in input bindings,
            // we need to wait until next tick to emit the outputs to keep Angular change detection happy
            setTimeout(() => {
                emitOutputs();
            });
        }
    }
    normaliseModelValues(input) {
        const normalisedInput = new ModelValues();
        normalisedInput.value = input.value;
        normalisedInput.highValue = input.highValue;
        if (!ValueHelper.isNullOrUndefined(this.viewOptions.stepsArray)) {
            // When using steps array, only round to nearest step in the array
            // No other enforcement can be done, as the step array may be out of order, and that is perfectly fine
            if (this.viewOptions.enforceStepsArray) {
                const valueIndex = ValueHelper.findStepIndex(normalisedInput.value, this.viewOptions.stepsArray);
                normalisedInput.value = this.viewOptions.stepsArray[valueIndex].value;
                if (this.range) {
                    const highValueIndex = ValueHelper.findStepIndex(normalisedInput.highValue, this.viewOptions.stepsArray);
                    normalisedInput.highValue = this.viewOptions.stepsArray[highValueIndex].value;
                }
            }
            return normalisedInput;
        }
        if (this.viewOptions.enforceStep) {
            normalisedInput.value = this.roundStep(normalisedInput.value);
            if (this.range) {
                normalisedInput.highValue = this.roundStep(normalisedInput.highValue);
            }
        }
        if (this.viewOptions.enforceRange) {
            normalisedInput.value = MathHelper.clampToRange(normalisedInput.value, this.viewOptions.floor, this.viewOptions.ceil);
            if (this.range) {
                normalisedInput.highValue = MathHelper.clampToRange(normalisedInput.highValue, this.viewOptions.floor, this.viewOptions.ceil);
            }
            // Make sure that range slider invariant (value <= highValue) is always satisfied
            if (this.range && input.value > input.highValue) {
                // We know that both values are now clamped correctly, they may just be in the wrong order
                // So the easy solution is to swap them... except swapping is sometimes disabled in options, so we make the two values the same
                if (this.viewOptions.noSwitching) {
                    normalisedInput.value = normalisedInput.highValue;
                }
                else {
                    const tempValue = input.value;
                    normalisedInput.value = input.highValue;
                    normalisedInput.highValue = tempValue;
                }
            }
        }
        return normalisedInput;
    }
    renormaliseModelValues() {
        const previousModelValues = {
            value: this.value,
            highValue: this.highValue
        };
        const normalisedModelValues = this.normaliseModelValues(previousModelValues);
        if (!ModelValues.compare(normalisedModelValues, previousModelValues)) {
            this.value = normalisedModelValues.value;
            this.highValue = normalisedModelValues.highValue;
            this.outputModelChangeSubject.next({
                value: this.value,
                highValue: this.highValue,
                forceChange: true,
                userEventInitiated: false
            });
        }
    }
    onChangeOptions() {
        if (!this.initHasRun) {
            return;
        }
        const previousOptionsInfluencingEventBindings = this.getOptionsInfluencingEventBindings(this.viewOptions);
        this.applyOptions();
        const newOptionsInfluencingEventBindings = this.getOptionsInfluencingEventBindings(this.viewOptions);
        // Avoid re-binding events in case nothing changes that can influence it
        // It makes it possible to change options while dragging the slider
        const rebindEvents = !ValueHelper.areArraysEqual(previousOptionsInfluencingEventBindings, newOptionsInfluencingEventBindings);
        // With new options, we need to re-normalise model values if necessary
        this.renormaliseModelValues();
        this.viewLowValue = this.modelValueToViewValue(this.value);
        if (this.range) {
            this.viewHighValue = this.modelValueToViewValue(this.highValue);
        }
        else {
            this.viewHighValue = undefined;
        }
        this.resetSlider(rebindEvents);
    }
    // Read the user options and apply them to the slider model
    applyOptions() {
        this.viewOptions = new Options();
        Object.assign(this.viewOptions, this.options);
        this.viewOptions.draggableRange = this.range && this.viewOptions.draggableRange;
        this.viewOptions.draggableRangeOnly = this.range && this.viewOptions.draggableRangeOnly;
        if (this.viewOptions.draggableRangeOnly) {
            this.viewOptions.draggableRange = true;
        }
        this.viewOptions.showTicks =
            this.viewOptions.showTicks ||
                this.viewOptions.showTicksValues ||
                !ValueHelper.isNullOrUndefined(this.viewOptions.ticksArray);
        if (this.viewOptions.showTicks &&
            (!ValueHelper.isNullOrUndefined(this.viewOptions.tickStep) ||
                !ValueHelper.isNullOrUndefined(this.viewOptions.ticksArray))) {
            this.intermediateTicks = true;
        }
        this.viewOptions.showSelectionBar =
            this.viewOptions.showSelectionBar ||
                this.viewOptions.showSelectionBarEnd ||
                !ValueHelper.isNullOrUndefined(this.viewOptions.showSelectionBarFromValue);
        if (!ValueHelper.isNullOrUndefined(this.viewOptions.stepsArray)) {
            this.applyStepsArrayOptions();
        }
        else {
            this.applyFloorCeilOptions();
        }
        if (ValueHelper.isNullOrUndefined(this.viewOptions.combineLabels)) {
            this.viewOptions.combineLabels = (minValue, maxValue) => {
                return minValue + ' - ' + maxValue;
            };
        }
        if (this.viewOptions.logScale && this.viewOptions.floor === 0) {
            throw Error("Can't use floor=0 with logarithmic scale");
        }
    }
    applyStepsArrayOptions() {
        this.viewOptions.floor = 0;
        this.viewOptions.ceil = this.viewOptions.stepsArray.length - 1;
        this.viewOptions.step = 1;
        if (ValueHelper.isNullOrUndefined(this.viewOptions.translate)) {
            this.viewOptions.translate = (modelValue) => {
                if (this.viewOptions.bindIndexForStepsArray) {
                    return String(this.getStepValue(modelValue));
                }
                return String(modelValue);
            };
        }
    }
    applyFloorCeilOptions() {
        if (ValueHelper.isNullOrUndefined(this.viewOptions.step)) {
            this.viewOptions.step = 1;
        }
        else {
            this.viewOptions.step = +this.viewOptions.step;
            if (this.viewOptions.step <= 0) {
                this.viewOptions.step = 1;
            }
        }
        if (ValueHelper.isNullOrUndefined(this.viewOptions.ceil) || ValueHelper.isNullOrUndefined(this.viewOptions.floor)) {
            throw Error('floor and ceil options must be supplied');
        }
        this.viewOptions.ceil = +this.viewOptions.ceil;
        this.viewOptions.floor = +this.viewOptions.floor;
        if (ValueHelper.isNullOrUndefined(this.viewOptions.translate)) {
            this.viewOptions.translate = (value) => String(value);
        }
    }
    // Resets slider
    resetSlider(rebindEvents = true) {
        this.manageElementsStyle();
        this.addAccessibility();
        this.updateCeilLabel();
        this.updateFloorLabel();
        if (rebindEvents) {
            this.unbindEvents();
            this.manageEventsBindings();
        }
        this.updateDisabledState();
        this.updateAriaLabel();
        this.calculateViewDimensions();
        this.refocusPointerIfNeeded();
    }
    // Sets focus on the specified pointer
    focusPointer(pointerType) {
        // If not supplied, use min pointer as default
        if (pointerType !== PointerType.Min && pointerType !== PointerType.Max) {
            pointerType = PointerType.Min;
        }
        if (pointerType === PointerType.Min) {
            this.minHandleElement?.focus();
        }
        else if (this.range && pointerType === PointerType.Max) {
            this.maxHandleElement?.focus();
        }
    }
    refocusPointerIfNeeded() {
        if (!ValueHelper.isNullOrUndefined(this.currentFocusPointer)) {
            this.onPointerFocus(this.currentFocusPointer);
            const element = this.getPointerElement(this.currentFocusPointer);
            element?.focus();
        }
    }
    // Update each elements style based on options
    manageElementsStyle() {
        this.updateScale();
        this.floorLabelElement?.setAlwaysHide(this.viewOptions.showTicksValues || this.viewOptions.hideLimitLabels);
        this.ceilLabelElement?.setAlwaysHide(this.viewOptions.showTicksValues || this.viewOptions.hideLimitLabels);
        const hideLabelsForTicks = this.viewOptions.showTicksValues && !this.intermediateTicks;
        this.minHandleLabelElement?.setAlwaysHide(hideLabelsForTicks || this.viewOptions.hidePointerLabels);
        this.maxHandleLabelElement?.setAlwaysHide(hideLabelsForTicks || !this.range || this.viewOptions.hidePointerLabels);
        this.combinedLabelElement?.setAlwaysHide(hideLabelsForTicks || !this.range || this.viewOptions.hidePointerLabels);
        this.selectionBarElement?.setAlwaysHide(!this.range && !this.viewOptions.showSelectionBar);
        this.leftOuterSelectionBarElement?.setAlwaysHide(!this.range || !this.viewOptions.showOuterSelectionBars);
        this.rightOuterSelectionBarElement?.setAlwaysHide(!this.range || !this.viewOptions.showOuterSelectionBars);
        this.fullBarTransparentClass = this.range && this.viewOptions.showOuterSelectionBars;
        this.selectionBarDraggableClass = this.viewOptions.draggableRange && !this.viewOptions.onlyBindHandles;
        this.ticksUnderValuesClass = this.intermediateTicks && this.options.showTicksValues;
        if (this.sliderElementVerticalClass !== this.viewOptions.vertical) {
            this.updateVerticalState();
            // The above change in host component class will not be applied until the end of this cycle
            // However, functions calculating the slider position expect the slider to be already styled as vertical
            // So as a workaround, we need to reset the slider once again to compute the correct values
            setTimeout(() => {
                this.resetSlider();
            });
        }
        // Changing animate class may interfere with slider reset/initialisation, so we should set it separately,
        // after all is properly set up
        if (this.sliderElementAnimateClass !== this.viewOptions.animate) {
            setTimeout(() => {
                this.sliderElementAnimateClass = this.viewOptions.animate;
            });
        }
        this.updateRotate();
    }
    // Manage the events bindings based on readOnly and disabled options
    manageEventsBindings() {
        if (this.viewOptions.disabled || this.viewOptions.readOnly) {
            this.unbindEvents();
        }
        else {
            this.bindEvents();
        }
    }
    // Set the disabled state based on disabled option
    updateDisabledState() {
        this.sliderElementDisabledAttr = this.viewOptions.disabled ? 'disabled' : undefined;
    }
    // Set the aria-label state based on ariaLabel option
    updateAriaLabel() {
        this.sliderElementAriaLabel = this.viewOptions.ariaLabel || 'nxg-slider';
    }
    // Set vertical state based on vertical option
    updateVerticalState() {
        this.sliderElementVerticalClass = this.viewOptions.vertical;
        for (const element of this.getAllSliderElements()) {
            // This is also called before ngAfterInit, so need to check that view child bindings work
            if (!ValueHelper.isNullOrUndefined(element)) {
                element.setVertical(this.viewOptions.vertical);
            }
        }
    }
    updateScale() {
        for (const element of this.getAllSliderElements()) {
            element.setScale(this.viewOptions.scale);
        }
    }
    updateRotate() {
        for (const element of this.getAllSliderElements()) {
            element.setRotate(this.viewOptions.rotate);
        }
    }
    getAllSliderElements() {
        return [
            this.leftOuterSelectionBarElement,
            this.rightOuterSelectionBarElement,
            this.fullBarElement,
            this.selectionBarElement,
            this.minHandleElement,
            this.maxHandleElement,
            this.floorLabelElement,
            this.ceilLabelElement,
            this.minHandleLabelElement,
            this.maxHandleLabelElement,
            this.combinedLabelElement,
            this.ticksElement
        ];
    }
    // Initialize slider handles positions and labels
    // Run only once during initialization and every time view port changes size
    initHandles() {
        this.updateLowHandle(this.valueToPosition(this.viewLowValue));
        /*
       the order here is important since the selection bar should be
       updated after the high handle but before the combined label
       */
        if (this.range) {
            this.updateHighHandle(this.valueToPosition(this.viewHighValue));
        }
        this.updateSelectionBar();
        if (this.range) {
            this.updateCombinedLabel();
        }
        this.updateTicksScale();
    }
    // Adds accessibility attributes, run only once during initialization
    addAccessibility() {
        this.updateAriaAttributes();
        this.minHandleElement.role = 'slider';
        if (this.viewOptions.keyboardSupport && !(this.viewOptions.readOnly || this.viewOptions.disabled)) {
            this.minHandleElement.tabindex = '0';
        }
        else {
            this.minHandleElement.tabindex = '';
        }
        this.minHandleElement.ariaOrientation =
            this.viewOptions.vertical || this.viewOptions.rotate !== 0 ? 'vertical' : 'horizontal';
        if (!ValueHelper.isNullOrUndefined(this.viewOptions.ariaLabel)) {
            this.minHandleElement.ariaLabel = this.viewOptions.ariaLabel;
        }
        else if (!ValueHelper.isNullOrUndefined(this.viewOptions.ariaLabelledBy)) {
            this.minHandleElement.ariaLabelledBy = this.viewOptions.ariaLabelledBy;
        }
        if (this.range) {
            this.maxHandleElement.role = 'slider';
            if (this.viewOptions.keyboardSupport && !(this.viewOptions.readOnly || this.viewOptions.disabled)) {
                this.maxHandleElement.tabindex = '0';
            }
            else {
                this.maxHandleElement.tabindex = '';
            }
            this.maxHandleElement.ariaOrientation =
                this.viewOptions.vertical || this.viewOptions.rotate !== 0 ? 'vertical' : 'horizontal';
            if (!ValueHelper.isNullOrUndefined(this.viewOptions.ariaLabelHigh)) {
                this.maxHandleElement.ariaLabel = this.viewOptions.ariaLabelHigh;
            }
            else if (!ValueHelper.isNullOrUndefined(this.viewOptions.ariaLabelledByHigh)) {
                this.maxHandleElement.ariaLabelledBy = this.viewOptions.ariaLabelledByHigh;
            }
        }
    }
    // Updates aria attributes according to current values
    updateAriaAttributes() {
        this.minHandleElement.ariaValueNow = (+this.value).toString();
        this.minHandleElement.ariaValueText = this.viewOptions.translate(+this.value, LabelType.Low);
        this.minHandleElement.ariaValueMin = this.viewOptions.floor.toString();
        this.minHandleElement.ariaValueMax = this.viewOptions.ceil.toString();
        if (this.range) {
            this.maxHandleElement.ariaValueNow = (+this.highValue).toString();
            this.maxHandleElement.ariaValueText = this.viewOptions.translate(+this.highValue, LabelType.High);
            this.maxHandleElement.ariaValueMin = this.viewOptions.floor.toString();
            this.maxHandleElement.ariaValueMax = this.viewOptions.ceil.toString();
        }
    }
    // Calculate dimensions that are dependent on view port size
    // Run once during initialization and every time view port changes size.
    calculateViewDimensions() {
        if (!ValueHelper.isNullOrUndefined(this.viewOptions.handleDimension)) {
            this.minHandleElement.setDimension(this.viewOptions.handleDimension);
        }
        else {
            this.minHandleElement.calculateDimension();
        }
        const handleWidth = this.minHandleElement.dimension;
        this.handleHalfDimension = handleWidth / 2;
        if (!ValueHelper.isNullOrUndefined(this.viewOptions.barDimension)) {
            this.fullBarElement.setDimension(this.viewOptions.barDimension);
        }
        else {
            this.fullBarElement.calculateDimension();
        }
        this.maxHandlePosition = this.fullBarElement.dimension - handleWidth;
        if (this.initHasRun) {
            this.updateFloorLabel();
            this.updateCeilLabel();
            this.initHandles();
        }
    }
    calculateViewDimensionsAndDetectChanges() {
        this.calculateViewDimensions();
        if (!this.isRefDestroyed()) {
            this.changeDetectionRef.detectChanges();
        }
    }
    /**
     * If the slider reference is already destroyed
     * @returns boolean - true if ref is destroyed
     */
    isRefDestroyed() {
        return this.changeDetectionRef['destroyed'];
    }
    // Update the ticks position
    updateTicksScale() {
        if (!this.viewOptions.showTicks) {
            setTimeout(() => {
                this.sliderElementWithLegendClass = false;
            });
            return;
        }
        const ticksArray = !ValueHelper.isNullOrUndefined(this.viewOptions.ticksArray)
            ? this.viewOptions.ticksArray
            : this.getTicksArray();
        const translate = this.viewOptions.vertical ? 'translateY' : 'translateX';
        if (this.viewOptions.rightToLeft) {
            ticksArray.reverse();
        }
        const tickValueStep = !ValueHelper.isNullOrUndefined(this.viewOptions.tickValueStep)
            ? this.viewOptions.tickValueStep
            : !ValueHelper.isNullOrUndefined(this.viewOptions.tickStep)
                ? this.viewOptions.tickStep
                : this.viewOptions.step;
        let hasAtLeastOneLegend = false;
        const newTicks = ticksArray.map((value) => {
            let position = this.valueToPosition(value);
            if (this.viewOptions.vertical) {
                position = this.maxHandlePosition - position;
            }
            const translation = translate + '(' + Math.round(position) + 'px)';
            const tick = new Tick();
            tick.selected = this.isTickSelected(value);
            tick.style = {
                '-webkit-transform': translation,
                '-moz-transform': translation,
                '-o-transform': translation,
                '-ms-transform': translation,
                transform: translation
            };
            if (tick.selected && !ValueHelper.isNullOrUndefined(this.viewOptions.getSelectionBarColor)) {
                tick.style['background-color'] = this.getSelectionBarColor();
            }
            if (!tick.selected && !ValueHelper.isNullOrUndefined(this.viewOptions.getTickColor)) {
                tick.style['background-color'] = this.getTickColor(value);
            }
            if (!ValueHelper.isNullOrUndefined(this.viewOptions.ticksTooltip)) {
                tick.tooltip = this.viewOptions.ticksTooltip(value);
                tick.tooltipPlacement = this.viewOptions.vertical ? 'right' : 'top';
            }
            if (this.viewOptions.showTicksValues &&
                !ValueHelper.isNullOrUndefined(tickValueStep) &&
                MathHelper.isModuloWithinPrecisionLimit(value, tickValueStep, this.viewOptions.precisionLimit)) {
                tick.value = this.getDisplayValue(value, LabelType.TickValue);
                if (!ValueHelper.isNullOrUndefined(this.viewOptions.ticksValuesTooltip)) {
                    tick.valueTooltip = this.viewOptions.ticksValuesTooltip(value);
                    tick.valueTooltipPlacement = this.viewOptions.vertical ? 'right' : 'top';
                }
            }
            let legend;
            if (!ValueHelper.isNullOrUndefined(this.viewOptions.stepsArray)) {
                const step = this.viewOptions.stepsArray[value];
                if (!ValueHelper.isNullOrUndefined(this.viewOptions.getStepLegend)) {
                    legend = this.viewOptions.getStepLegend(step);
                }
                else if (!ValueHelper.isNullOrUndefined(step)) {
                    legend = step.legend;
                }
            }
            else if (!ValueHelper.isNullOrUndefined(this.viewOptions.getLegend)) {
                legend = this.viewOptions.getLegend(value);
            }
            if (!ValueHelper.isNullOrUndefined(legend)) {
                tick.legend = legend;
                hasAtLeastOneLegend = true;
            }
            return tick;
        });
        setTimeout(() => {
            this.sliderElementWithLegendClass = hasAtLeastOneLegend;
        });
        // We should avoid re-creating the ticks array if possible
        // This both improves performance and makes CSS animations work correctly
        if (!ValueHelper.isNullOrUndefined(this.ticks) && this.ticks.length === newTicks.length) {
            for (let i = 0; i < newTicks.length; ++i) {
                Object.assign(this.ticks[i], newTicks[i]);
            }
        }
        else {
            this.ticks = newTicks;
        }
        if (!this.isRefDestroyed()) {
            this.changeDetectionRef.detectChanges();
        }
    }
    getTicksArray() {
        const step = !ValueHelper.isNullOrUndefined(this.viewOptions.tickStep)
            ? this.viewOptions.tickStep
            : this.viewOptions.step;
        const ticksArray = [];
        const numberOfValues = 1 +
            Math.floor(MathHelper.roundToPrecisionLimit(Math.abs(this.viewOptions.ceil - this.viewOptions.floor) / step, this.viewOptions.precisionLimit));
        for (let index = 0; index < numberOfValues; ++index) {
            ticksArray.push(MathHelper.roundToPrecisionLimit(this.viewOptions.floor + step * index, this.viewOptions.precisionLimit));
        }
        return ticksArray;
    }
    isTickSelected(value) {
        if (!this.range) {
            if (!ValueHelper.isNullOrUndefined(this.viewOptions.showSelectionBarFromValue)) {
                const center = this.viewOptions.showSelectionBarFromValue;
                if (this.viewLowValue > center && value >= center && value <= this.viewLowValue) {
                    return true;
                }
                else if (this.viewLowValue < center && value <= center && value >= this.viewLowValue) {
                    return true;
                }
            }
            else if (this.viewOptions.showSelectionBarEnd) {
                if (value >= this.viewLowValue) {
                    return true;
                }
            }
            else if (this.viewOptions.showSelectionBar && value <= this.viewLowValue) {
                return true;
            }
        }
        if (this.range && value >= this.viewLowValue && value <= this.viewHighValue) {
            return true;
        }
        return false;
    }
    // Update position of the floor label
    updateFloorLabel() {
        if (!this.floorLabelElement.alwaysHide) {
            this.floorLabelElement.setValue(this.getDisplayValue(this.viewOptions.floor, LabelType.Floor));
            this.floorLabelElement.calculateDimension();
            const position = this.viewOptions.rightToLeft
                ? this.fullBarElement.dimension - this.floorLabelElement.dimension
                : 0;
            this.floorLabelElement.setPosition(position);
        }
    }
    // Update position of the ceiling label
    updateCeilLabel() {
        if (!this.ceilLabelElement.alwaysHide) {
            this.ceilLabelElement.setValue(this.getDisplayValue(this.viewOptions.ceil, LabelType.Ceil));
            this.ceilLabelElement.calculateDimension();
            const position = this.viewOptions.rightToLeft
                ? 0
                : this.fullBarElement.dimension - this.ceilLabelElement.dimension;
            this.ceilLabelElement.setPosition(position);
        }
    }
    // Update slider handles and label positions
    updateHandles(which, newPos) {
        if (which === PointerType.Min) {
            this.updateLowHandle(newPos);
        }
        else if (which === PointerType.Max) {
            this.updateHighHandle(newPos);
        }
        this.updateSelectionBar();
        this.updateTicksScale();
        if (this.range) {
            this.updateCombinedLabel();
        }
    }
    // Helper function to work out the position for handle labels depending on RTL or not
    getHandleLabelPos(labelType, newPos) {
        const labelDimension = labelType === PointerType.Min ? this.minHandleLabelElement.dimension : this.maxHandleLabelElement.dimension;
        const nearHandlePos = newPos - labelDimension / 2 + this.handleHalfDimension;
        const endOfBarPos = this.fullBarElement.dimension - labelDimension;
        if (!this.viewOptions.boundPointerLabels) {
            return nearHandlePos;
        }
        if ((this.viewOptions.rightToLeft && labelType === PointerType.Min) ||
            (!this.viewOptions.rightToLeft && labelType === PointerType.Max)) {
            return Math.min(nearHandlePos, endOfBarPos);
        }
        else {
            return Math.min(Math.max(nearHandlePos, 0), endOfBarPos);
        }
    }
    // Update low slider handle position and label
    updateLowHandle(newPos) {
        this.minHandleElement.setPosition(newPos);
        this.minHandleLabelElement.setValue(this.getDisplayValue(this.viewLowValue, LabelType.Low));
        this.minHandleLabelElement.setPosition(this.getHandleLabelPos(PointerType.Min, newPos));
        if (!ValueHelper.isNullOrUndefined(this.viewOptions.getPointerColor)) {
            this.minPointerStyle = {
                backgroundColor: this.getPointerColor(PointerType.Min)
            };
        }
        if (this.viewOptions.autoHideLimitLabels) {
            this.updateFloorAndCeilLabelsVisibility();
        }
    }
    // Update high slider handle position and label
    updateHighHandle(newPos) {
        this.maxHandleElement.setPosition(newPos);
        this.maxHandleLabelElement.setValue(this.getDisplayValue(this.viewHighValue, LabelType.High));
        this.maxHandleLabelElement.setPosition(this.getHandleLabelPos(PointerType.Max, newPos));
        if (!ValueHelper.isNullOrUndefined(this.viewOptions.getPointerColor)) {
            this.maxPointerStyle = {
                backgroundColor: this.getPointerColor(PointerType.Max)
            };
        }
        if (this.viewOptions.autoHideLimitLabels) {
            this.updateFloorAndCeilLabelsVisibility();
        }
    }
    // Show/hide floor/ceiling label
    updateFloorAndCeilLabelsVisibility() {
        // Show based only on hideLimitLabels if pointer labels are hidden
        if (this.viewOptions.hidePointerLabels) {
            return;
        }
        let floorLabelHidden = false;
        let ceilLabelHidden = false;
        const isMinLabelAtFloor = this.isLabelBelowFloorLabel(this.minHandleLabelElement);
        const isMinLabelAtCeil = this.isLabelAboveCeilLabel(this.minHandleLabelElement);
        const isMaxLabelAtCeil = this.isLabelAboveCeilLabel(this.maxHandleLabelElement);
        const isCombinedLabelAtFloor = this.isLabelBelowFloorLabel(this.combinedLabelElement);
        const isCombinedLabelAtCeil = this.isLabelAboveCeilLabel(this.combinedLabelElement);
        if (isMinLabelAtFloor) {
            floorLabelHidden = true;
            this.floorLabelElement.hide();
        }
        else {
            floorLabelHidden = false;
            this.floorLabelElement.show();
        }
        if (isMinLabelAtCeil) {
            ceilLabelHidden = true;
            this.ceilLabelElement.hide();
        }
        else {
            ceilLabelHidden = false;
            this.ceilLabelElement.show();
        }
        if (this.range) {
            const hideCeil = this.combinedLabelElement.isVisible() ? isCombinedLabelAtCeil : isMaxLabelAtCeil;
            const hideFloor = this.combinedLabelElement.isVisible() ? isCombinedLabelAtFloor : isMinLabelAtFloor;
            if (hideCeil) {
                this.ceilLabelElement.hide();
            }
            else if (!ceilLabelHidden) {
                this.ceilLabelElement.show();
            }
            // Hide or show floor label
            if (hideFloor) {
                this.floorLabelElement.hide();
            }
            else if (!floorLabelHidden) {
                this.floorLabelElement.show();
            }
        }
    }
    isLabelBelowFloorLabel(label) {
        const pos = label.position;
        const dim = label.dimension;
        const floorPos = this.floorLabelElement.position;
        const floorDim = this.floorLabelElement.dimension;
        return this.viewOptions.rightToLeft ? pos + dim >= floorPos - 2 : pos <= floorPos + floorDim + 2;
    }
    isLabelAboveCeilLabel(label) {
        const pos = label.position;
        const dim = label.dimension;
        const ceilPos = this.ceilLabelElement.position;
        const ceilDim = this.ceilLabelElement.dimension;
        return this.viewOptions.rightToLeft ? pos <= ceilPos + ceilDim + 2 : pos + dim >= ceilPos - 2;
    }
    // Update slider selection bar, combined label and range label
    updateSelectionBar() {
        let position = 0;
        let dimension = 0;
        const isSelectionBarFromRight = this.viewOptions.rightToLeft
            ? !this.viewOptions.showSelectionBarEnd
            : this.viewOptions.showSelectionBarEnd;
        const positionForRange = this.viewOptions.rightToLeft
            ? this.maxHandleElement.position + this.handleHalfDimension
            : this.minHandleElement.position + this.handleHalfDimension;
        if (this.range) {
            dimension = Math.abs(this.maxHandleElement.position - this.minHandleElement.position);
            position = positionForRange;
        }
        else {
            if (!ValueHelper.isNullOrUndefined(this.viewOptions.showSelectionBarFromValue)) {
                const center = this.viewOptions.showSelectionBarFromValue;
                const centerPosition = this.valueToPosition(center);
                const isModelGreaterThanCenter = this.viewOptions.rightToLeft
                    ? this.viewLowValue <= center
                    : this.viewLowValue > center;
                if (isModelGreaterThanCenter) {
                    dimension = this.minHandleElement.position - centerPosition;
                    position = centerPosition + this.handleHalfDimension;
                }
                else {
                    dimension = centerPosition - this.minHandleElement.position;
                    position = this.minHandleElement.position + this.handleHalfDimension;
                }
            }
            else if (isSelectionBarFromRight) {
                dimension = Math.ceil(Math.abs(this.maxHandlePosition - this.minHandleElement.position) + this.handleHalfDimension);
                position = Math.floor(this.minHandleElement.position + this.handleHalfDimension);
            }
            else {
                dimension = this.minHandleElement.position + this.handleHalfDimension;
                position = 0;
            }
        }
        this.selectionBarElement.setDimension(dimension);
        this.selectionBarElement.setPosition(position);
        if (this.range && this.viewOptions.showOuterSelectionBars) {
            if (this.viewOptions.rightToLeft) {
                this.rightOuterSelectionBarElement.setDimension(position);
                this.rightOuterSelectionBarElement.setPosition(0);
                this.fullBarElement.calculateDimension();
                this.leftOuterSelectionBarElement.setDimension(this.fullBarElement.dimension - (position + dimension));
                this.leftOuterSelectionBarElement.setPosition(position + dimension);
            }
            else {
                this.leftOuterSelectionBarElement.setDimension(position);
                this.leftOuterSelectionBarElement.setPosition(0);
                this.fullBarElement.calculateDimension();
                this.rightOuterSelectionBarElement.setDimension(this.fullBarElement.dimension - (position + dimension));
                this.rightOuterSelectionBarElement.setPosition(position + dimension);
            }
        }
        if (!ValueHelper.isNullOrUndefined(this.viewOptions.getSelectionBarColor)) {
            const color = this.getSelectionBarColor();
            this.barStyle = {
                backgroundColor: color
            };
        }
        else if (!ValueHelper.isNullOrUndefined(this.viewOptions.selectionBarGradient)) {
            const offset = !ValueHelper.isNullOrUndefined(this.viewOptions.showSelectionBarFromValue)
                ? this.valueToPosition(this.viewOptions.showSelectionBarFromValue)
                : 0;
            const reversed = (offset - position > 0 && !isSelectionBarFromRight) || (offset - position <= 0 && isSelectionBarFromRight);
            const direction = this.viewOptions.vertical ? (reversed ? 'bottom' : 'top') : reversed ? 'left' : 'right';
            this.barStyle = {
                backgroundImage: 'linear-gradient(to ' +
                    direction +
                    ', ' +
                    this.viewOptions.selectionBarGradient.from +
                    ' 0%,' +
                    this.viewOptions.selectionBarGradient.to +
                    ' 100%)'
            };
            if (this.viewOptions.vertical) {
                this.barStyle.backgroundPosition =
                    'center ' + (offset + dimension + position + (reversed ? -this.handleHalfDimension : 0)) + 'px';
                this.barStyle.backgroundSize = '100% ' + (this.fullBarElement.dimension - this.handleHalfDimension) + 'px';
            }
            else {
                this.barStyle.backgroundPosition = offset - position + (reversed ? this.handleHalfDimension : 0) + 'px center';
                this.barStyle.backgroundSize = this.fullBarElement.dimension - this.handleHalfDimension + 'px 100%';
            }
        }
    }
    // Wrapper around the getSelectionBarColor of the user to pass to correct parameters
    getSelectionBarColor() {
        if (this.range) {
            return this.viewOptions.getSelectionBarColor(this.value, this.highValue);
        }
        return this.viewOptions.getSelectionBarColor(this.value);
    }
    // Wrapper around the getPointerColor of the user to pass to  correct parameters
    getPointerColor(pointerType) {
        if (pointerType === PointerType.Max) {
            return this.viewOptions.getPointerColor(this.highValue, pointerType);
        }
        return this.viewOptions.getPointerColor(this.value, pointerType);
    }
    // Wrapper around the getTickColor of the user to pass to correct parameters
    getTickColor(value) {
        return this.viewOptions.getTickColor(value);
    }
    // Update combined label position and value
    updateCombinedLabel() {
        let isLabelOverlap = false;
        if (this.viewOptions.rightToLeft) {
            isLabelOverlap =
                this.minHandleLabelElement.position - this.minHandleLabelElement.dimension - 10 <=
                    this.maxHandleLabelElement.position;
        }
        else {
            isLabelOverlap =
                this.minHandleLabelElement.position + this.minHandleLabelElement.dimension + 10 >=
                    this.maxHandleLabelElement.position;
        }
        if (isLabelOverlap) {
            const lowDisplayValue = this.getDisplayValue(this.viewLowValue, LabelType.Low);
            const highDisplayValue = this.getDisplayValue(this.viewHighValue, LabelType.High);
            const combinedLabelValue = this.viewOptions.rightToLeft
                ? this.viewOptions.combineLabels(highDisplayValue, lowDisplayValue)
                : this.viewOptions.combineLabels(lowDisplayValue, highDisplayValue);
            this.combinedLabelElement.setValue(combinedLabelValue);
            const pos = this.viewOptions.boundPointerLabels
                ? Math.min(Math.max(this.selectionBarElement.position +
                    this.selectionBarElement.dimension / 2 -
                    this.combinedLabelElement.dimension / 2, 0), this.fullBarElement.dimension - this.combinedLabelElement.dimension)
                : this.selectionBarElement.position +
                    this.selectionBarElement.dimension / 2 -
                    this.combinedLabelElement.dimension / 2;
            this.combinedLabelElement.setPosition(pos);
            this.minHandleLabelElement.hide();
            this.maxHandleLabelElement.hide();
            this.combinedLabelElement.show();
        }
        else {
            this.updateHighHandle(this.valueToPosition(this.viewHighValue));
            this.updateLowHandle(this.valueToPosition(this.viewLowValue));
            this.maxHandleLabelElement.show();
            this.minHandleLabelElement.show();
            this.combinedLabelElement.hide();
        }
        if (this.viewOptions.autoHideLimitLabels) {
            this.updateFloorAndCeilLabelsVisibility();
        }
    }
    // Return the translated value if a translate function is provided else the original value
    getDisplayValue(value, which) {
        if (!ValueHelper.isNullOrUndefined(this.viewOptions.stepsArray) && !this.viewOptions.bindIndexForStepsArray) {
            value = this.getStepValue(value);
        }
        return this.viewOptions.translate(value, which);
    }
    // Round value to step and precision based on minValue
    roundStep(value, customStep) {
        const step = !ValueHelper.isNullOrUndefined(customStep) ? customStep : this.viewOptions.step;
        let steppedDifference = MathHelper.roundToPrecisionLimit((value - this.viewOptions.floor) / step, this.viewOptions.precisionLimit);
        steppedDifference = Math.round(steppedDifference) * step;
        return MathHelper.roundToPrecisionLimit(this.viewOptions.floor + steppedDifference, this.viewOptions.precisionLimit);
    }
    // Translate value to pixel position
    valueToPosition(val) {
        let fn = ValueHelper.linearValueToPosition;
        if (!ValueHelper.isNullOrUndefined(this.viewOptions.customValueToPosition)) {
            fn = this.viewOptions.customValueToPosition;
        }
        else if (this.viewOptions.logScale) {
            fn = ValueHelper.logValueToPosition;
        }
        val = MathHelper.clampToRange(val, this.viewOptions.floor, this.viewOptions.ceil);
        let percent = fn(val, this.viewOptions.floor, this.viewOptions.ceil);
        if (ValueHelper.isNullOrUndefined(percent)) {
            percent = 0;
        }
        if (this.viewOptions.rightToLeft) {
            percent = 1 - percent;
        }
        return percent * this.maxHandlePosition;
    }
    // Translate position to model value
    positionToValue(position) {
        let percent = position / this.maxHandlePosition;
        if (this.viewOptions.rightToLeft) {
            percent = 1 - percent;
        }
        let fn = ValueHelper.linearPositionToValue;
        if (!ValueHelper.isNullOrUndefined(this.viewOptions.customPositionToValue)) {
            fn = this.viewOptions.customPositionToValue;
        }
        else if (this.viewOptions.logScale) {
            fn = ValueHelper.logPositionToValue;
        }
        const value = fn(percent, this.viewOptions.floor, this.viewOptions.ceil);
        return !ValueHelper.isNullOrUndefined(value) ? value : 0;
    }
    // Get the X-coordinate or Y-coordinate of an event
    getEventXY(event, targetTouchId) {
        if (event instanceof MouseEvent) {
            return this.viewOptions.vertical || this.viewOptions.rotate !== 0 ? event.clientY : event.clientX;
        }
        let touchIndex = 0;
        const touches = event.touches;
        if (!ValueHelper.isNullOrUndefined(targetTouchId)) {
            for (let i = 0; i < touches.length; i++) {
                if (touches[i].identifier === targetTouchId) {
                    touchIndex = i;
                    break;
                }
            }
        }
        // Return the target touch or if the target touch was not found in the event
        // returns the coordinates of the first touch
        return this.viewOptions.vertical || this.viewOptions.rotate !== 0
            ? touches[touchIndex].clientY
            : touches[touchIndex].clientX;
    }
    // Compute the event position depending on whether the slider is horizontal or vertical
    getEventPosition(event, targetTouchId) {
        const sliderElementBoundingRect = this.elementRef.nativeElement.getBoundingClientRect();
        const sliderPos = this.viewOptions.vertical || this.viewOptions.rotate !== 0
            ? sliderElementBoundingRect.bottom
            : sliderElementBoundingRect.left;
        let eventPos = 0;
        if (this.viewOptions.vertical || this.viewOptions.rotate !== 0) {
            eventPos = -this.getEventXY(event, targetTouchId) + sliderPos;
        }
        else {
            eventPos = this.getEventXY(event, targetTouchId) - sliderPos;
        }
        return eventPos * this.viewOptions.scale - this.handleHalfDimension;
    }
    // Get the handle closest to an event
    getNearestHandle(event) {
        if (!this.range) {
            return PointerType.Min;
        }
        const position = this.getEventPosition(event);
        const distanceMin = Math.abs(position - this.minHandleElement.position);
        const distanceMax = Math.abs(position - this.maxHandleElement.position);
        if (distanceMin < distanceMax) {
            return PointerType.Min;
        }
        else if (distanceMin > distanceMax) {
            return PointerType.Max;
        }
        else if (!this.viewOptions.rightToLeft) {
            // if event is at the same distance from min/max then if it's at left of minH, we return minH else maxH
            return position < this.minHandleElement.position ? PointerType.Min : PointerType.Max;
        }
        // reverse in rtl
        return position > this.minHandleElement.position ? PointerType.Min : PointerType.Max;
    }
    // Bind mouse and touch events to slider handles
    bindEvents() {
        const draggableRange = this.viewOptions.draggableRange;
        if (!this.viewOptions.onlyBindHandles) {
            this.selectionBarElement.on('mousedown', (event) => this.onBarStart(draggableRange, event, true, true, undefined, true));
        }
        if (this.viewOptions.draggableRangeOnly) {
            this.minHandleElement.on('mousedown', (event) => this.onBarStart(draggableRange, event, true, true, PointerType.Min));
            this.maxHandleElement.on('mousedown', (event) => this.onBarStart(draggableRange, event, true, true, PointerType.Max));
        }
        else {
            this.minHandleElement.on('mousedown', (event) => this.onStart(event, true, true, PointerType.Min));
            if (this.range) {
                this.maxHandleElement.on('mousedown', (event) => this.onStart(event, true, true, PointerType.Max));
            }
            if (!this.viewOptions.onlyBindHandles) {
                this.fullBarElement.on('mousedown', (event) => this.onStart(event, true, true, undefined, true));
                this.ticksElement.on('mousedown', (event) => this.onStart(event, true, true, undefined, true, true));
            }
        }
        if (!this.viewOptions.onlyBindHandles) {
            this.selectionBarElement.onPassive('touchstart', (event) => this.onBarStart(draggableRange, event, true, true, undefined, true));
        }
        if (this.viewOptions.draggableRangeOnly) {
            this.minHandleElement.onPassive('touchstart', (event) => this.onBarStart(draggableRange, event, true, true, PointerType.Min));
            this.maxHandleElement.onPassive('touchstart', (event) => this.onBarStart(draggableRange, event, true, true, PointerType.Max));
        }
        else {
            this.minHandleElement.onPassive('touchstart', (event) => this.onStart(event, true, true, PointerType.Min));
            if (this.range) {
                this.maxHandleElement.onPassive('touchstart', (event) => this.onStart(event, true, true, PointerType.Max));
            }
            if (!this.viewOptions.onlyBindHandles) {
                this.fullBarElement.onPassive('touchstart', (event) => this.onStart(event, true, true, undefined, true));
                this.ticksElement.onPassive('touchstart', (event) => this.onStart(event, false, false, undefined, true, true));
            }
        }
        if (this.viewOptions.keyboardSupport) {
            this.minHandleElement.on('focus', () => this.onPointerFocus(PointerType.Min));
            if (this.range) {
                this.maxHandleElement.on('focus', () => this.onPointerFocus(PointerType.Max));
            }
        }
    }
    getOptionsInfluencingEventBindings(options) {
        return [
            options.disabled,
            options.readOnly,
            options.draggableRange,
            options.draggableRangeOnly,
            options.onlyBindHandles,
            options.keyboardSupport
        ];
    }
    // Unbind mouse and touch events to slider handles
    unbindEvents() {
        this.unsubscribeOnMove();
        this.unsubscribeOnEnd();
        for (const element of this.getAllSliderElements()) {
            if (!ValueHelper.isNullOrUndefined(element)) {
                element.off();
            }
        }
    }
    onBarStart(draggableRange, event, bindMove, bindEnd, pointerType, simulateImmediateMove, simulateImmediateEnd) {
        if (draggableRange) {
            this.onDragStart(event, bindMove, bindEnd, pointerType);
        }
        else {
            this.onStart(event, bindMove, bindEnd, pointerType, simulateImmediateMove, simulateImmediateEnd);
        }
    }
    // onStart event handler
    onStart(event, bindMove, bindEnd, pointerType, simulateImmediateMove, simulateImmediateEnd) {
        event.stopPropagation();
        // Only call preventDefault() when handling non-passive events (passive events don't need it)
        if (!CompatibilityHelper.isTouchEvent(event) && !supportsPassiveEvents) {
            event.preventDefault();
        }
        this.moving = false;
        // We have to do this in case the HTML where the sliders are on
        // have been animated into view.
        this.calculateViewDimensions();
        if (ValueHelper.isNullOrUndefined(pointerType)) {
            pointerType = this.getNearestHandle(event);
        }
        this.currentTrackingPointer = pointerType;
        const pointerElement = this.getPointerElement(pointerType);
        pointerElement.active = true;
        if (this.viewOptions.keyboardSupport) {
            pointerElement.focus();
        }
        if (bindMove) {
            this.unsubscribeOnMove();
            const onMoveCallback = (e) => this.dragging.active ? this.onDragMove(e) : this.onMove(e);
            if (CompatibilityHelper.isTouchEvent(event)) {
                this.onMoveEventListener = this.eventListenerHelper.attachPassiveEventListener(document, 'touchmove', onMoveCallback);
            }
            else {
                this.onMoveEventListener = this.eventListenerHelper.attachEventListener(document, 'mousemove', onMoveCallback);
            }
        }
        if (bindEnd) {
            this.unsubscribeOnEnd();
            const onEndCallback = (e) => this.onEnd(e);
            if (CompatibilityHelper.isTouchEvent(event)) {
                this.onEndEventListener = this.eventListenerHelper.attachPassiveEventListener(document, 'touchend', onEndCallback);
            }
            else {
                this.onEndEventListener = this.eventListenerHelper.attachEventListener(document, 'mouseup', onEndCallback);
            }
        }
        this.userChangeStart.emit(this.getChangeContext());
        if (CompatibilityHelper.isTouchEvent(event) &&
            !ValueHelper.isNullOrUndefined(event.changedTouches)) {
            // Store the touch identifier
            if (ValueHelper.isNullOrUndefined(this.touchId)) {
                this.touchId = event.changedTouches[0].identifier;
            }
        }
        // Click events, either with mouse or touch gesture are weird. Sometimes they result in full
        // start, move, end sequence, and sometimes, they don't - they only invoke mousedown
        // As a workaround, we simulate the first move event and the end event if it's necessary
        if (simulateImmediateMove) {
            this.onMove(event, true);
        }
        if (simulateImmediateEnd) {
            this.onEnd(event);
        }
    }
    // onMove event handler
    onMove(event, fromTick) {
        let touchForThisSlider;
        if (CompatibilityHelper.isTouchEvent(event)) {
            const changedTouches = event.changedTouches;
            for (let i = 0; i < changedTouches.length; i++) {
                if (changedTouches[i].identifier === this.touchId) {
                    touchForThisSlider = changedTouches[i];
                    break;
                }
            }
            if (ValueHelper.isNullOrUndefined(touchForThisSlider)) {
                return;
            }
        }
        if (this.viewOptions.animate && !this.viewOptions.animateOnMove) {
            if (this.moving) {
                this.sliderElementAnimateClass = false;
            }
        }
        this.moving = true;
        const newPos = !ValueHelper.isNullOrUndefined(touchForThisSlider)
            ? this.getEventPosition(event, touchForThisSlider.identifier)
            : this.getEventPosition(event);
        let newValue;
        const ceilValue = this.viewOptions.rightToLeft ? this.viewOptions.floor : this.viewOptions.ceil;
        const floorValue = this.viewOptions.rightToLeft ? this.viewOptions.ceil : this.viewOptions.floor;
        if (newPos <= 0) {
            newValue = floorValue;
        }
        else if (newPos >= this.maxHandlePosition) {
            newValue = ceilValue;
        }
        else {
            newValue = this.positionToValue(newPos);
            if (fromTick && !ValueHelper.isNullOrUndefined(this.viewOptions.tickStep)) {
                newValue = this.roundStep(newValue, this.viewOptions.tickStep);
            }
            else {
                newValue = this.roundStep(newValue);
            }
        }
        this.positionTrackingHandle(newValue);
    }
    onEnd(event) {
        if (CompatibilityHelper.isTouchEvent(event)) {
            const changedTouches = event.changedTouches;
            if (changedTouches[0].identifier !== this.touchId) {
                return;
            }
        }
        this.moving = false;
        if (this.viewOptions.animate) {
            this.sliderElementAnimateClass = true;
        }
        this.touchId = undefined;
        if (!this.viewOptions.keyboardSupport) {
            this.minHandleElement.active = false;
            this.maxHandleElement.active = false;
            this.currentTrackingPointer = undefined;
        }
        this.dragging.active = false;
        this.unsubscribeOnMove();
        this.unsubscribeOnEnd();
        this.userChangeEnd.emit(this.getChangeContext());
    }
    onPointerFocus(pointerType) {
        const pointerElement = this.getPointerElement(pointerType);
        pointerElement.on('blur', () => this.onPointerBlur(pointerElement));
        pointerElement.on('keydown', (event) => this.onKeyboardEvent(event));
        pointerElement.on('keyup', () => this.onKeyUp());
        pointerElement.active = true;
        this.currentTrackingPointer = pointerType;
        this.currentFocusPointer = pointerType;
        this.firstKeyDown = true;
    }
    onKeyUp() {
        this.firstKeyDown = true;
        this.userChangeEnd.emit(this.getChangeContext());
    }
    onPointerBlur(pointer) {
        pointer.off('blur');
        pointer.off('keydown');
        pointer.off('keyup');
        pointer.active = false;
        if (ValueHelper.isNullOrUndefined(this.touchId)) {
            this.currentTrackingPointer = undefined;
            this.currentFocusPointer = undefined;
        }
    }
    getKeyActions(currentValue) {
        const valueRange = this.viewOptions.ceil - this.viewOptions.floor;
        let increaseStep = currentValue + this.viewOptions.step;
        let decreaseStep = currentValue - this.viewOptions.step;
        let increasePage = currentValue + valueRange / 10;
        let decreasePage = currentValue - valueRange / 10;
        if (this.viewOptions.reversedControls) {
            increaseStep = currentValue - this.viewOptions.step;
            decreaseStep = currentValue + this.viewOptions.step;
            increasePage = currentValue - valueRange / 10;
            decreasePage = currentValue + valueRange / 10;
        }
        // Left to right default actions
        const actions = {
            UP: increaseStep,
            DOWN: decreaseStep,
            LEFT: decreaseStep,
            RIGHT: increaseStep,
            PAGEUP: increasePage,
            PAGEDOWN: decreasePage,
            HOME: this.viewOptions.reversedControls ? this.viewOptions.ceil : this.viewOptions.floor,
            END: this.viewOptions.reversedControls ? this.viewOptions.floor : this.viewOptions.ceil
        };
        // right to left means swapping right and left arrows
        if (this.viewOptions.rightToLeft) {
            actions['LEFT'] = increaseStep;
            actions['RIGHT'] = decreaseStep;
            // right to left and vertical means we also swap up and down
            if (this.viewOptions.vertical || this.viewOptions.rotate !== 0) {
                actions['UP'] = decreaseStep;
                actions['DOWN'] = increaseStep;
            }
        }
        return actions;
    }
    onKeyboardEvent(event) {
        const currentValue = this.getCurrentTrackingValue();
        const keyCode = !ValueHelper.isNullOrUndefined(event.keyCode) ? event.keyCode : event.which;
        const keys = {
            38: 'UP',
            40: 'DOWN',
            37: 'LEFT',
            39: 'RIGHT',
            33: 'PAGEUP',
            34: 'PAGEDOWN',
            36: 'HOME',
            35: 'END'
        };
        const actions = this.getKeyActions(currentValue);
        const key = keys[keyCode];
        const action = actions[key];
        if (ValueHelper.isNullOrUndefined(action) || ValueHelper.isNullOrUndefined(this.currentTrackingPointer)) {
            return;
        }
        event.preventDefault();
        if (this.firstKeyDown) {
            this.firstKeyDown = false;
            this.userChangeStart.emit(this.getChangeContext());
        }
        const actionValue = MathHelper.clampToRange(action, this.viewOptions.floor, this.viewOptions.ceil);
        const newValue = this.roundStep(actionValue);
        if (!this.viewOptions.draggableRangeOnly) {
            this.positionTrackingHandle(newValue);
        }
        else {
            const difference = this.viewHighValue - this.viewLowValue;
            let newMinValue;
            let newMaxValue;
            if (this.currentTrackingPointer === PointerType.Min) {
                newMinValue = newValue;
                newMaxValue = newValue + difference;
                if (newMaxValue > this.viewOptions.ceil) {
                    newMaxValue = this.viewOptions.ceil;
                    newMinValue = newMaxValue - difference;
                }
            }
            else if (this.currentTrackingPointer === PointerType.Max) {
                newMaxValue = newValue;
                newMinValue = newValue - difference;
                if (newMinValue < this.viewOptions.floor) {
                    newMinValue = this.viewOptions.floor;
                    newMaxValue = newMinValue + difference;
                }
            }
            this.positionTrackingBar(newMinValue, newMaxValue);
        }
    }
    // onDragStart event handler, handles dragging of the middle bar
    onDragStart(event, bindMove, bindEnd, pointerType) {
        const position = this.getEventPosition(event);
        this.dragging = new Dragging();
        this.dragging.active = true;
        this.dragging.value = this.positionToValue(position);
        this.dragging.difference = this.viewHighValue - this.viewLowValue;
        this.dragging.lowLimit = this.viewOptions.rightToLeft
            ? this.minHandleElement.position - position
            : position - this.minHandleElement.position;
        this.dragging.highLimit = this.viewOptions.rightToLeft
            ? position - this.maxHandleElement.position
            : this.maxHandleElement.position - position;
        this.onStart(event, bindMove, bindEnd, pointerType);
    }
    /** Get min value depending on whether the newPos is outOfBounds above or below the bar and rightToLeft */
    getMinValue(newPos, outOfBounds, isAbove) {
        const isRTL = this.viewOptions.rightToLeft;
        let value;
        if (outOfBounds) {
            if (isAbove) {
                value = isRTL ? this.viewOptions.floor : this.viewOptions.ceil - this.dragging.difference;
            }
            else {
                value = isRTL ? this.viewOptions.ceil - this.dragging.difference : this.viewOptions.floor;
            }
        }
        else {
            value = isRTL
                ? this.positionToValue(newPos + this.dragging.lowLimit)
                : this.positionToValue(newPos - this.dragging.lowLimit);
        }
        return this.roundStep(value);
    }
    /** Get max value depending on whether the newPos is outOfBounds above or below the bar and rightToLeft */
    getMaxValue(newPos, outOfBounds, isAbove) {
        const isRTL = this.viewOptions.rightToLeft;
        let value;
        if (outOfBounds) {
            if (isAbove) {
                value = isRTL ? this.viewOptions.floor + this.dragging.difference : this.viewOptions.ceil;
            }
            else {
                value = isRTL ? this.viewOptions.ceil : this.viewOptions.floor + this.dragging.difference;
            }
        }
        else {
            if (isRTL) {
                value = this.positionToValue(newPos + this.dragging.lowLimit) + this.dragging.difference;
            }
            else {
                value = this.positionToValue(newPos - this.dragging.lowLimit) + this.dragging.difference;
            }
        }
        return this.roundStep(value);
    }
    onDragMove(event) {
        const newPos = this.getEventPosition(event);
        if (this.viewOptions.animate && !this.viewOptions.animateOnMove) {
            if (this.moving) {
                this.sliderElementAnimateClass = false;
            }
        }
        this.moving = true;
        let ceilLimit, floorLimit, floorHandleElement, ceilHandleElement;
        if (this.viewOptions.rightToLeft) {
            ceilLimit = this.dragging.lowLimit;
            floorLimit = this.dragging.highLimit;
            floorHandleElement = this.maxHandleElement;
            ceilHandleElement = this.minHandleElement;
        }
        else {
            ceilLimit = this.dragging.highLimit;
            floorLimit = this.dragging.lowLimit;
            floorHandleElement = this.minHandleElement;
            ceilHandleElement = this.maxHandleElement;
        }
        const isUnderFloorLimit = newPos <= floorLimit;
        const isOverCeilLimit = newPos >= this.maxHandlePosition - ceilLimit;
        let newMinValue;
        let newMaxValue;
        if (isUnderFloorLimit) {
            if (floorHandleElement.position === 0) {
                return;
            }
            newMinValue = this.getMinValue(newPos, true, false);
            newMaxValue = this.getMaxValue(newPos, true, false);
        }
        else if (isOverCeilLimit) {
            if (ceilHandleElement.position === this.maxHandlePosition) {
                return;
            }
            newMaxValue = this.getMaxValue(newPos, true, true);
            newMinValue = this.getMinValue(newPos, true, true);
        }
        else {
            newMinValue = this.getMinValue(newPos, false, false);
            newMaxValue = this.getMaxValue(newPos, false, false);
        }
        this.positionTrackingBar(newMinValue, newMaxValue);
    }
    // Set the new value and position for the entire bar
    positionTrackingBar(newMinValue, newMaxValue) {
        if (!ValueHelper.isNullOrUndefined(this.viewOptions.minLimit) && newMinValue < this.viewOptions.minLimit) {
            newMinValue = this.viewOptions.minLimit;
            newMaxValue = MathHelper.roundToPrecisionLimit(newMinValue + this.dragging.difference, this.viewOptions.precisionLimit);
        }
        if (!ValueHelper.isNullOrUndefined(this.viewOptions.maxLimit) && newMaxValue > this.viewOptions.maxLimit) {
            newMaxValue = this.viewOptions.maxLimit;
            newMinValue = MathHelper.roundToPrecisionLimit(newMaxValue - this.dragging.difference, this.viewOptions.precisionLimit);
        }
        this.viewLowValue = newMinValue;
        this.viewHighValue = newMaxValue;
        this.applyViewChange();
        this.updateHandles(PointerType.Min, this.valueToPosition(newMinValue));
        this.updateHandles(PointerType.Max, this.valueToPosition(newMaxValue));
    }
    // Set the new value and position to the current tracking handle
    positionTrackingHandle(newValue) {
        newValue = this.applyMinMaxLimit(newValue);
        if (this.range) {
            if (this.viewOptions.pushRange) {
                newValue = this.applyPushRange(newValue);
            }
            else {
                if (this.viewOptions.noSwitching) {
                    if (this.currentTrackingPointer === PointerType.Min && newValue > this.viewHighValue) {
                        newValue = this.applyMinMaxRange(this.viewHighValue);
                    }
                    else if (this.currentTrackingPointer === PointerType.Max && newValue < this.viewLowValue) {
                        newValue = this.applyMinMaxRange(this.viewLowValue);
                    }
                }
                newValue = this.applyMinMaxRange(newValue);
                /* This is to check if we need to switch the min and max handles */
                if (this.currentTrackingPointer === PointerType.Min && newValue > this.viewHighValue) {
                    this.viewLowValue = this.viewHighValue;
                    this.applyViewChange();
                    this.updateHandles(PointerType.Min, this.maxHandleElement.position);
                    this.updateAriaAttributes();
                    this.currentTrackingPointer = PointerType.Max;
                    this.minHandleElement.active = false;
                    this.maxHandleElement.active = true;
                    if (this.viewOptions.keyboardSupport) {
                        this.maxHandleElement.focus();
                    }
                }
                else if (this.currentTrackingPointer === PointerType.Max && newValue < this.viewLowValue) {
                    this.viewHighValue = this.viewLowValue;
                    this.applyViewChange();
                    this.updateHandles(PointerType.Max, this.minHandleElement.position);
                    this.updateAriaAttributes();
                    this.currentTrackingPointer = PointerType.Min;
                    this.maxHandleElement.active = false;
                    this.minHandleElement.active = true;
                    if (this.viewOptions.keyboardSupport) {
                        this.minHandleElement.focus();
                    }
                }
            }
        }
        if (this.getCurrentTrackingValue() !== newValue) {
            if (this.currentTrackingPointer === PointerType.Min) {
                this.viewLowValue = newValue;
                this.applyViewChange();
            }
            else if (this.currentTrackingPointer === PointerType.Max) {
                this.viewHighValue = newValue;
                this.applyViewChange();
            }
            this.updateHandles(this.currentTrackingPointer, this.valueToPosition(newValue));
            this.updateAriaAttributes();
        }
    }
    applyMinMaxLimit(newValue) {
        if (!ValueHelper.isNullOrUndefined(this.viewOptions.minLimit) && newValue < this.viewOptions.minLimit) {
            return this.viewOptions.minLimit;
        }
        if (!ValueHelper.isNullOrUndefined(this.viewOptions.maxLimit) && newValue > this.viewOptions.maxLimit) {
            return this.viewOptions.maxLimit;
        }
        return newValue;
    }
    applyMinMaxRange(newValue) {
        const oppositeValue = this.currentTrackingPointer === PointerType.Min ? this.viewHighValue : this.viewLowValue;
        const difference = Math.abs(newValue - oppositeValue);
        if (!ValueHelper.isNullOrUndefined(this.viewOptions.minRange)) {
            if (difference < this.viewOptions.minRange) {
                if (this.currentTrackingPointer === PointerType.Min) {
                    return MathHelper.roundToPrecisionLimit(this.viewHighValue - this.viewOptions.minRange, this.viewOptions.precisionLimit);
                }
                else if (this.currentTrackingPointer === PointerType.Max) {
                    return MathHelper.roundToPrecisionLimit(this.viewLowValue + this.viewOptions.minRange, this.viewOptions.precisionLimit);
                }
            }
        }
        if (!ValueHelper.isNullOrUndefined(this.viewOptions.maxRange)) {
            if (difference > this.viewOptions.maxRange) {
                if (this.currentTrackingPointer === PointerType.Min) {
                    return MathHelper.roundToPrecisionLimit(this.viewHighValue - this.viewOptions.maxRange, this.viewOptions.precisionLimit);
                }
                else if (this.currentTrackingPointer === PointerType.Max) {
                    return MathHelper.roundToPrecisionLimit(this.viewLowValue + this.viewOptions.maxRange, this.viewOptions.precisionLimit);
                }
            }
        }
        return newValue;
    }
    applyPushRange(newValue) {
        const difference = this.currentTrackingPointer === PointerType.Min ? this.viewHighValue - newValue : newValue - this.viewLowValue;
        const minRange = !ValueHelper.isNullOrUndefined(this.viewOptions.minRange)
            ? this.viewOptions.minRange
            : this.viewOptions.step;
        const maxRange = this.viewOptions.maxRange;
        // if smaller than minRange
        if (difference < minRange) {
            if (this.currentTrackingPointer === PointerType.Min) {
                this.viewHighValue = MathHelper.roundToPrecisionLimit(Math.min(newValue + minRange, this.viewOptions.ceil), this.viewOptions.precisionLimit);
                newValue = MathHelper.roundToPrecisionLimit(this.viewHighValue - minRange, this.viewOptions.precisionLimit);
                this.applyViewChange();
                this.updateHandles(PointerType.Max, this.valueToPosition(this.viewHighValue));
            }
            else if (this.currentTrackingPointer === PointerType.Max) {
                this.viewLowValue = MathHelper.roundToPrecisionLimit(Math.max(newValue - minRange, this.viewOptions.floor), this.viewOptions.precisionLimit);
                newValue = MathHelper.roundToPrecisionLimit(this.viewLowValue + minRange, this.viewOptions.precisionLimit);
                this.applyViewChange();
                this.updateHandles(PointerType.Min, this.valueToPosition(this.viewLowValue));
            }
            this.updateAriaAttributes();
        }
        else if (!ValueHelper.isNullOrUndefined(maxRange) && difference > maxRange) {
            // if greater than maxRange
            if (this.currentTrackingPointer === PointerType.Min) {
                this.viewHighValue = MathHelper.roundToPrecisionLimit(newValue + maxRange, this.viewOptions.precisionLimit);
                this.applyViewChange();
                this.updateHandles(PointerType.Max, this.valueToPosition(this.viewHighValue));
            }
            else if (this.currentTrackingPointer === PointerType.Max) {
                this.viewLowValue = MathHelper.roundToPrecisionLimit(newValue - maxRange, this.viewOptions.precisionLimit);
                this.applyViewChange();
                this.updateHandles(PointerType.Min, this.valueToPosition(this.viewLowValue));
            }
            this.updateAriaAttributes();
        }
        return newValue;
    }
    getChangeContext() {
        const changeContext = new ChangeContext();
        changeContext.pointerType = this.currentTrackingPointer;
        changeContext.value = +this.value;
        if (this.range) {
            changeContext.highValue = +this.highValue;
        }
        return changeContext;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: SliderComponent, deps: [{ token: i0.Renderer2 }, { token: i0.ElementRef }, { token: i0.ChangeDetectorRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.7", type: SliderComponent, isStandalone: true, selector: "ngx-slider", inputs: { value: "value", highValue: "highValue", options: "options", manualRefresh: "manualRefresh", triggerFocus: "triggerFocus" }, outputs: { valueChange: "valueChange", highValueChange: "highValueChange", userChangeStart: "userChangeStart", userChange: "userChange", userChangeEnd: "userChangeEnd" }, host: { listeners: { "window:resize": "onResize($event)" }, properties: { "class": "this.class", "class.vertical": "this.sliderElementVerticalClass", "class.animate": "this.sliderElementAnimateClass", "class.with-legend": "this.sliderElementWithLegendClass", "attr.disabled": "this.sliderElementDisabledAttr", "attr.aria-label": "this.sliderElementAriaLabel" } }, providers: [NGX_SLIDER_CONTROL_VALUE_ACCESSOR], queries: [{ propertyName: "tooltipTemplate", first: true, predicate: ["tooltipTemplate"], descendants: true }], viewQueries: [{ propertyName: "leftOuterSelectionBarElement", first: true, predicate: ["leftOuterSelectionBar"], descendants: true, read: SliderElementDirective }, { propertyName: "rightOuterSelectionBarElement", first: true, predicate: ["rightOuterSelectionBar"], descendants: true, read: SliderElementDirective }, { propertyName: "fullBarElement", first: true, predicate: ["fullBar"], descendants: true, read: SliderElementDirective }, { propertyName: "selectionBarElement", first: true, predicate: ["selectionBar"], descendants: true, read: SliderElementDirective }, { propertyName: "minHandleElement", first: true, predicate: ["minHandle"], descendants: true, read: SliderHandleDirective }, { propertyName: "maxHandleElement", first: true, predicate: ["maxHandle"], descendants: true, read: SliderHandleDirective }, { propertyName: "floorLabelElement", first: true, predicate: ["floorLabel"], descendants: true, read: SliderLabelDirective }, { propertyName: "ceilLabelElement", first: true, predicate: ["ceilLabel"], descendants: true, read: SliderLabelDirective }, { propertyName: "minHandleLabelElement", first: true, predicate: ["minHandleLabel"], descendants: true, read: SliderLabelDirective }, { propertyName: "maxHandleLabelElement", first: true, predicate: ["maxHandleLabel"], descendants: true, read: SliderLabelDirective }, { propertyName: "combinedLabelElement", first: true, predicate: ["combinedLabel"], descendants: true, read: SliderLabelDirective }, { propertyName: "ticksElement", first: true, predicate: ["ticksElement"], descendants: true, read: SliderElementDirective }], usesOnChanges: true, ngImport: i0, template: "<!-- // 0 Left selection bar outside two handles -->\n<span\n  ngxSliderElement\n  #leftOuterSelectionBar\n  class=\"ngx-slider-span ngx-slider-bar-wrapper ngx-slider-left-out-selection\"\n>\n  <span class=\"ngx-slider-span ngx-slider-bar\"></span>\n</span>\n<!-- // 1 Right selection bar outside two handles -->\n<span\n  ngxSliderElement\n  #rightOuterSelectionBar\n  class=\"ngx-slider-span ngx-slider-bar-wrapper ngx-slider-right-out-selection\"\n>\n  <span class=\"ngx-slider-span ngx-slider-bar\"></span>\n</span>\n<!-- // 2 The whole slider bar -->\n<span\n  ngxSliderElement\n  #fullBar\n  [class.ngx-slider-transparent]=\"fullBarTransparentClass\"\n  class=\"ngx-slider-span ngx-slider-bar-wrapper ngx-slider-full-bar\"\n>\n  <span class=\"ngx-slider-span ngx-slider-bar\"></span>\n</span>\n<!-- // 3 Selection bar between two handles -->\n<span\n  ngxSliderElement\n  #selectionBar\n  [class.ngx-slider-draggable]=\"selectionBarDraggableClass\"\n  class=\"ngx-slider-span ngx-slider-bar-wrapper ngx-slider-selection-bar\"\n>\n  <span class=\"ngx-slider-span ngx-slider-bar ngx-slider-selection\" [ngStyle]=\"barStyle\"></span>\n</span>\n<!-- // 4 Low slider handle -->\n<span\n  ngxSliderHandle\n  #minHandle\n  class=\"ngx-slider-span ngx-slider-pointer ngx-slider-pointer-min\"\n  [ngStyle]=\"minPointerStyle\"\n></span>\n<!-- // 5 High slider handle -->\n<span\n  ngxSliderHandle\n  #maxHandle\n  [style.display]=\"range ? 'inherit' : 'none'\"\n  class=\"ngx-slider-span ngx-slider-pointer ngx-slider-pointer-max\"\n  [ngStyle]=\"maxPointerStyle\"\n></span>\n<!-- // 6 Floor label -->\n<span ngxSliderLabel #floorLabel class=\"ngx-slider-span ngx-slider-bubble ngx-slider-limit ngx-slider-floor\"></span>\n<!-- // 7 Ceiling label -->\n<span ngxSliderLabel #ceilLabel class=\"ngx-slider-span ngx-slider-bubble ngx-slider-limit ngx-slider-ceil\"></span>\n<!-- // 8 Label above the low slider handle -->\n<span ngxSliderLabel #minHandleLabel class=\"ngx-slider-span ngx-slider-bubble ngx-slider-model-value\"></span>\n<!-- // 9 Label above the high slider handle -->\n<span ngxSliderLabel #maxHandleLabel class=\"ngx-slider-span ngx-slider-bubble ngx-slider-model-high\"></span>\n<!-- // 10 Combined range label when the slider handles are close ex. 15 - 17 -->\n<span ngxSliderLabel #combinedLabel class=\"ngx-slider-span ngx-slider-bubble ngx-slider-combined\"></span>\n<!-- // 11 The ticks -->\n<span\n  ngxSliderElement\n  #ticksElement\n  [hidden]=\"!showTicks\"\n  [class.ngx-slider-ticks-values-under]=\"ticksUnderValuesClass\"\n  class=\"ngx-slider-ticks\"\n>\n  @for (t of ticks; track t) {\n    <span class=\"ngx-slider-tick\" [ngClass]=\"{ 'ngx-slider-selected': t.selected }\" [ngStyle]=\"t.style\">\n      <ngx-slider-tooltip-wrapper\n        [template]=\"tooltipTemplate\"\n        [tooltip]=\"t.tooltip\"\n        [placement]=\"t.tooltipPlacement\"\n      ></ngx-slider-tooltip-wrapper>\n      @if (t.value !== null) {\n        <ngx-slider-tooltip-wrapper\n          class=\"ngx-slider-span ngx-slider-tick-value\"\n          [template]=\"tooltipTemplate\"\n          [tooltip]=\"t.valueTooltip\"\n          [placement]=\"t.valueTooltipPlacement\"\n          [content]=\"t.value\"\n        ></ngx-slider-tooltip-wrapper>\n      }\n      @if (t.legend !== null) {\n        <span class=\"ngx-slider-span ngx-slider-tick-legend\" [innerHTML]=\"t.legend\"></span>\n      }\n    </span>\n  }\n</span>\n", dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "component", type: TooltipWrapperComponent, selector: "ngx-slider-tooltip-wrapper", inputs: ["template", "tooltip", "placement", "content"] }, { kind: "directive", type: SliderLabelDirective, selector: "[ngxSliderLabel]" }, { kind: "directive", type: SliderHandleDirective, selector: "[ngxSliderHandle]" }, { kind: "directive", type: SliderElementDirective, selector: "[ngxSliderElement]" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: SliderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ngx-slider', providers: [NGX_SLIDER_CONTROL_VALUE_ACCESSOR], standalone: true, imports: [CommonModule, TooltipWrapperComponent, SliderLabelDirective, SliderHandleDirective, SliderElementDirective], template: "<!-- // 0 Left selection bar outside two handles -->\n<span\n  ngxSliderElement\n  #leftOuterSelectionBar\n  class=\"ngx-slider-span ngx-slider-bar-wrapper ngx-slider-left-out-selection\"\n>\n  <span class=\"ngx-slider-span ngx-slider-bar\"></span>\n</span>\n<!-- // 1 Right selection bar outside two handles -->\n<span\n  ngxSliderElement\n  #rightOuterSelectionBar\n  class=\"ngx-slider-span ngx-slider-bar-wrapper ngx-slider-right-out-selection\"\n>\n  <span class=\"ngx-slider-span ngx-slider-bar\"></span>\n</span>\n<!-- // 2 The whole slider bar -->\n<span\n  ngxSliderElement\n  #fullBar\n  [class.ngx-slider-transparent]=\"fullBarTransparentClass\"\n  class=\"ngx-slider-span ngx-slider-bar-wrapper ngx-slider-full-bar\"\n>\n  <span class=\"ngx-slider-span ngx-slider-bar\"></span>\n</span>\n<!-- // 3 Selection bar between two handles -->\n<span\n  ngxSliderElement\n  #selectionBar\n  [class.ngx-slider-draggable]=\"selectionBarDraggableClass\"\n  class=\"ngx-slider-span ngx-slider-bar-wrapper ngx-slider-selection-bar\"\n>\n  <span class=\"ngx-slider-span ngx-slider-bar ngx-slider-selection\" [ngStyle]=\"barStyle\"></span>\n</span>\n<!-- // 4 Low slider handle -->\n<span\n  ngxSliderHandle\n  #minHandle\n  class=\"ngx-slider-span ngx-slider-pointer ngx-slider-pointer-min\"\n  [ngStyle]=\"minPointerStyle\"\n></span>\n<!-- // 5 High slider handle -->\n<span\n  ngxSliderHandle\n  #maxHandle\n  [style.display]=\"range ? 'inherit' : 'none'\"\n  class=\"ngx-slider-span ngx-slider-pointer ngx-slider-pointer-max\"\n  [ngStyle]=\"maxPointerStyle\"\n></span>\n<!-- // 6 Floor label -->\n<span ngxSliderLabel #floorLabel class=\"ngx-slider-span ngx-slider-bubble ngx-slider-limit ngx-slider-floor\"></span>\n<!-- // 7 Ceiling label -->\n<span ngxSliderLabel #ceilLabel class=\"ngx-slider-span ngx-slider-bubble ngx-slider-limit ngx-slider-ceil\"></span>\n<!-- // 8 Label above the low slider handle -->\n<span ngxSliderLabel #minHandleLabel class=\"ngx-slider-span ngx-slider-bubble ngx-slider-model-value\"></span>\n<!-- // 9 Label above the high slider handle -->\n<span ngxSliderLabel #maxHandleLabel class=\"ngx-slider-span ngx-slider-bubble ngx-slider-model-high\"></span>\n<!-- // 10 Combined range label when the slider handles are close ex. 15 - 17 -->\n<span ngxSliderLabel #combinedLabel class=\"ngx-slider-span ngx-slider-bubble ngx-slider-combined\"></span>\n<!-- // 11 The ticks -->\n<span\n  ngxSliderElement\n  #ticksElement\n  [hidden]=\"!showTicks\"\n  [class.ngx-slider-ticks-values-under]=\"ticksUnderValuesClass\"\n  class=\"ngx-slider-ticks\"\n>\n  @for (t of ticks; track t) {\n    <span class=\"ngx-slider-tick\" [ngClass]=\"{ 'ngx-slider-selected': t.selected }\" [ngStyle]=\"t.style\">\n      <ngx-slider-tooltip-wrapper\n        [template]=\"tooltipTemplate\"\n        [tooltip]=\"t.tooltip\"\n        [placement]=\"t.tooltipPlacement\"\n      ></ngx-slider-tooltip-wrapper>\n      @if (t.value !== null) {\n        <ngx-slider-tooltip-wrapper\n          class=\"ngx-slider-span ngx-slider-tick-value\"\n          [template]=\"tooltipTemplate\"\n          [tooltip]=\"t.valueTooltip\"\n          [placement]=\"t.valueTooltipPlacement\"\n          [content]=\"t.value\"\n        ></ngx-slider-tooltip-wrapper>\n      }\n      @if (t.legend !== null) {\n        <span class=\"ngx-slider-span ngx-slider-tick-legend\" [innerHTML]=\"t.legend\"></span>\n      }\n    </span>\n  }\n</span>\n" }]
        }], ctorParameters: () => [{ type: i0.Renderer2 }, { type: i0.ElementRef }, { type: i0.ChangeDetectorRef }, { type: i0.NgZone }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], value: [{
                type: Input
            }], valueChange: [{
                type: Output
            }], highValue: [{
                type: Input
            }], highValueChange: [{
                type: Output
            }], options: [{
                type: Input
            }], userChangeStart: [{
                type: Output
            }], userChange: [{
                type: Output
            }], userChangeEnd: [{
                type: Output
            }], manualRefresh: [{
                type: Input
            }], triggerFocus: [{
                type: Input
            }], leftOuterSelectionBarElement: [{
                type: ViewChild,
                args: ['leftOuterSelectionBar', { read: SliderElementDirective }]
            }], rightOuterSelectionBarElement: [{
                type: ViewChild,
                args: ['rightOuterSelectionBar', { read: SliderElementDirective }]
            }], fullBarElement: [{
                type: ViewChild,
                args: ['fullBar', { read: SliderElementDirective }]
            }], selectionBarElement: [{
                type: ViewChild,
                args: ['selectionBar', { read: SliderElementDirective }]
            }], minHandleElement: [{
                type: ViewChild,
                args: ['minHandle', { read: SliderHandleDirective }]
            }], maxHandleElement: [{
                type: ViewChild,
                args: ['maxHandle', { read: SliderHandleDirective }]
            }], floorLabelElement: [{
                type: ViewChild,
                args: ['floorLabel', { read: SliderLabelDirective }]
            }], ceilLabelElement: [{
                type: ViewChild,
                args: ['ceilLabel', { read: SliderLabelDirective }]
            }], minHandleLabelElement: [{
                type: ViewChild,
                args: ['minHandleLabel', { read: SliderLabelDirective }]
            }], maxHandleLabelElement: [{
                type: ViewChild,
                args: ['maxHandleLabel', { read: SliderLabelDirective }]
            }], combinedLabelElement: [{
                type: ViewChild,
                args: ['combinedLabel', { read: SliderLabelDirective }]
            }], ticksElement: [{
                type: ViewChild,
                args: ['ticksElement', { read: SliderElementDirective }]
            }], tooltipTemplate: [{
                type: ContentChild,
                args: ['tooltipTemplate']
            }], sliderElementVerticalClass: [{
                type: HostBinding,
                args: ['class.vertical']
            }], sliderElementAnimateClass: [{
                type: HostBinding,
                args: ['class.animate']
            }], sliderElementWithLegendClass: [{
                type: HostBinding,
                args: ['class.with-legend']
            }], sliderElementDisabledAttr: [{
                type: HostBinding,
                args: ['attr.disabled']
            }], sliderElementAriaLabel: [{
                type: HostBinding,
                args: ['attr.aria-label']
            }], onResize: [{
                type: HostListener,
                args: ['window:resize', ['$event']]
            }] } });

/**
 * @deprecated DfSliderModule is deprecated.
 * Use {@link https://design-factory-int.app.net6.paas.westeurope.rnd.az.amadeus.net/#/components/slider/agnosui  AgnosUI Slider} instead.
 */
class DfSliderModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfSliderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.7", ngImport: i0, type: DfSliderModule, imports: [SliderComponent,
            SliderElementDirective,
            SliderHandleDirective,
            SliderLabelDirective,
            TooltipWrapperComponent,
            DfSliderDirectionDirective], exports: [DfSliderDirectionDirective, SliderComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfSliderModule, imports: [SliderComponent] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfSliderModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        SliderComponent,
                        SliderElementDirective,
                        SliderHandleDirective,
                        SliderLabelDirective,
                        TooltipWrapperComponent,
                        DfSliderDirectionDirective
                    ],
                    exports: [DfSliderDirectionDirective, SliderComponent]
                }]
        }] });

/**
 * A configuration service for the [DfSideNavService](#/components/sidenav/api#DfSideNavComponent) component.
 *
 * You can inject this service, typically in your root component, and customize its properties
 * to provide default values for your sidenav used in the application.
 */
class DfSideNavConfig {
    constructor() {
        this.isMinmized = false;
        this.isCollapsed = false;
        this.isPresent = false;
        this.isInMenuOverlay = true;
        this.isAppOverlay = false;
        this.isAppOverlayMode = true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfSideNavConfig, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfSideNavConfig, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfSideNavConfig, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

var DfNavItemType;
(function (DfNavItemType) {
    DfNavItemType[DfNavItemType["subMenus"] = 0] = "subMenus";
    DfNavItemType[DfNavItemType["actionButton"] = 1] = "actionButton";
    DfNavItemType[DfNavItemType["routerLink"] = 2] = "routerLink";
    DfNavItemType[DfNavItemType["link"] = 3] = "link";
    DfNavItemType[DfNavItemType["externalLink"] = 4] = "externalLink";
    DfNavItemType[DfNavItemType["separator"] = 5] = "separator";
    DfNavItemType[DfNavItemType["template"] = 6] = "template";
    DfNavItemType[DfNavItemType["title"] = 7] = "title";
})(DfNavItemType || (DfNavItemType = {}));
/**
 * A service for managing the sidenav panel.
 * This service is included at root level
 *
 * Creating a sidenav is straightforward: use [DfSideNavComponent](#/components/sidenav/api#DfSideNavComponent) component
 */
class DfSideNavService {
    constructor(config) {
        this.config = config;
        this.type = DfNavItemType;
        this._items = [];
        this.subscription = new Subscription();
        this._sideNavItems$ = new BehaviorSubject([]);
        this._isMinmized$ = new BehaviorSubject(config.isMinmized);
        this._isCollapsed$ = new BehaviorSubject(config.isCollapsed);
        this._isPresent$ = new BehaviorSubject(config.isPresent);
        this._isInMenuOverlay$ = new BehaviorSubject(config.isInMenuOverlay);
        this._isAppOverlay$ = new BehaviorSubject(config.isAppOverlay);
        this._activeItem$ = new BehaviorSubject(null);
        this._isAppOverlayMode = config.isAppOverlayMode;
        this.subscription.add(this._isCollapsed$.subscribe(() => {
            this.updateOverLay();
        }));
    }
    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
    /**
     * Use this to init the list of Elements from your sidenav
     *
     *
     * Second option withRouter is to be set to false if you are not passing link string for the angular Router
     * Here we are not deep copying the array --> the function will change it
     */
    initItems(items, withRouter = true) {
        this._items = items;
        this.addNavItemType(withRouter);
        this.setActiveItem(this._items);
        this.dispatchSideNavItems();
    }
    /**
     * Use this to inactivate the list of Elements from your sidenav
     * isActive properties of all items will be set to false recursively
     *
     */
    inactiveItems(list) {
        list.forEach((item) => {
            item.isActive = false;
            if (item.subMenus) {
                this.inactiveItems(item.subMenus);
            }
        });
        this.dispatchSideNavItems();
    }
    /**
     * Use this to active the item pass as input
     * This function inactivate all the other items of the service
     * It will then dispatch the list of items of the sideNav to activeItem$
     *
     */
    activeItem(item) {
        this.inactiveItems(this._items);
        item.isActive = true;
        this._activeItem$.next(item);
        this.dispatchSideNavItems();
    }
    /**
     * Getter to know when to put/remove the overlay.
     * `True` in case of overlay needed
     *
     */
    get isAppOverlay$() {
        return this._isAppOverlay$.asObservable();
    }
    /**
     * Getter to know if the sidenav is in an overlay panel or not
     * `True` in case of overlay panel
     *
     */
    get isInMenuOverlay$() {
        return this._isInMenuOverlay$.asObservable();
    }
    /**
     * Getter to know the current activeItem a user clicked on
     *
     */
    get activeItem$() {
        return this._activeItem$.asObservable();
    }
    /**
     * Getter to know if the sideNav is present in the page
     * SideNav may be collapsed here or not
     *
     */
    get isPresent$() {
        return this._isPresent$.asObservable();
    }
    /**
     * Future Options
     */
    get isMinimized$() {
        return this._isMinmized$.asObservable();
    }
    /**
     * Getter to know if the SideNav is collapsed or not
     * If `true` the SideNav is collapsed
     *
     */
    get isCollapsed$() {
        return this._isCollapsed$.asObservable();
    }
    /**
     * Getter of the SideNav Items after they have beens changed
     *
     */
    get sideNavItems$() {
        return this._sideNavItems$.asObservable();
    }
    /**
     * Getter to know the mode Apply for the SideNav
     * If `true` the SideNav can be in overlay (but not necessary is).
     *
     */
    get isAppOverlayMode() {
        return this._isAppOverlayMode;
    }
    // initItems must be used to push new items to the service
    get items() {
        return this._items;
    }
    /**
     * Use this to toggle a SideNav Item (either from expand to collapse or the other way around).
     * isCollapsed attribute of the input item is changed.
     * All items are push again to the stream of items.
     * This is used internally by the SideNav in case of click and Enter on an Item with subMenus
     */
    toggle(itemInput) {
        itemInput.isCollapsed = !itemInput.isCollapsed;
        this.dispatchSideNavItems();
    }
    /**
     *
     * @param itemInput
     */
    collapseItem(itemInput) {
        this.collapse(itemInput, this._items);
        this.dispatchSideNavItems();
    }
    /**
     * Use this to collapseAll items
     */
    collapseAllItems() {
        this.collapseAll(this._items);
        this.dispatchSideNavItems();
    }
    /**
     * Use this to set/unset the SideNav in an Overlay Menu
     * If input is `True` then the SideNav will be in an Overlay Menu
     * (when not collapsed)
     *
     */
    setInMenuSideNav(on) {
        this._isInMenuOverlay$.next(on);
    }
    /**
     * Use this to collapse the SideNav
     *
     */
    hideSideNav() {
        this._isCollapsed$.next(true);
    }
    /**
     * Use this to make the SideNav visible (uncollapse)
     *
     */
    showSideNav() {
        this._isCollapsed$.next(false);
    }
    /**
     * Use this to toggle the SideNav status collapse/visible
     *
     */
    toggleSideNav() {
        this._isCollapsed$.next(!this._isCollapsed$.value);
    }
    // internal usage from the SideNav. Call when component is destroy
    removeSideBar() {
        this._isCollapsed$.next(this.config.isCollapsed);
        this._isPresent$.next(false);
    }
    /**
     * Use this to set the App Overlay Mode
     * Input `True` if you want to set the AppOverlay
     * An event is send to isAppOverlay$ depending on your status
     * if Input = `True` and isCollapsed = `False` then isAppOverlay$
     * will be `True`
     *
     */
    setAppOverlayMode(on) {
        this._isAppOverlayMode = on;
        this.updateOverLay();
    }
    // Internal usage to the sidenav component
    // Do not use
    setSideNav() {
        this._isPresent$.next(true);
    }
    /**
     *
     * @param itemInput
     * @param itemsList
     */
    collapse(itemInput, itemsList) {
        for (const item of itemsList) {
            if (item === itemInput && item.subMenus) {
                item.isCollapsed = true;
                return true; // propagate the collapse up
            }
            else if (item === itemInput) {
                return true;
            }
            else if (item.subMenus) {
                const needcol = this.collapse(itemInput, item.subMenus);
                if (needcol) {
                    item.isCollapsed = true; // collapse it
                    return true; // propagate the collapse up
                }
            }
        }
        return false;
    }
    /**
     *
     * @param itemsList
     */
    collapseAll(itemsList) {
        itemsList.forEach((item) => {
            if (item.subMenus) {
                item.isCollapsed = true;
                this.collapseAll(item.subMenus);
            }
        });
    }
    // Only one item at the time must be active
    // Active flag in item is only valid for action button and link
    // Router link does not use this active flag to work
    // It is up to you to inactivateItems when needed when you mix actionButton and router link
    // To avoid more than one active state visible
    setActiveItem(items, foundAnActive = false) {
        let foundActive = foundAnActive;
        items.forEach((item) => {
            if (item.isActive && foundAnActive) {
                throw Error('DfSideNavService must be init with only one isActive item');
            }
            else if (item.isActive) {
                this._activeItem$.next(item);
                foundActive = true;
            }
            if (item.subMenus) {
                foundActive = this.setActiveItem(item.subMenus, foundActive);
            }
        });
        return foundActive;
    }
    addNavItemType(withRouter, items = this._items) {
        items.forEach((item) => {
            if (!item.type) {
                if (item.subMenus) {
                    item.type = DfNavItemType.subMenus;
                }
                else if (item.isExternal) {
                    item.type = DfNavItemType.externalLink;
                }
                else if (withRouter) {
                    item.type = DfNavItemType.routerLink;
                }
                else {
                    item.type = DfNavItemType.actionButton;
                }
            }
            if (item.subMenus) {
                this.addNavItemType(withRouter, item.subMenus);
            }
        });
    }
    dispatchSideNavItems() {
        this._sideNavItems$.next(this._items);
    }
    updateOverLay() {
        this._isAppOverlay$.next(this.isAppOverlayMode && this._isCollapsed$.value === false);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfSideNavService, deps: [{ token: DfSideNavConfig }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfSideNavService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfSideNavService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: DfSideNavConfig }] });

const environment = {
    animation: true,
    transitionTimerDelayMs: 5
};

/**
 * Creates an observable where all callbacks are executed inside a given zone
 *
 * @param zone Angular Zone element
 */
function runInZone(zone) {
    return source => {
        return new Observable(observer => {
            const onNext = (value) => zone.run(() => observer.next(value));
            const onError = (e) => zone.run(() => observer.error(e));
            const onComplete = () => zone.run(() => observer.complete());
            return source.subscribe(onNext, onError, onComplete);
        });
    };
}
function getTransitionDurationMs(element) {
    const { transitionDelay, transitionDuration } = window.getComputedStyle(element);
    const transitionDelaySec = parseFloat(transitionDelay);
    const transitionDurationSec = parseFloat(transitionDuration);
    return (transitionDelaySec + transitionDurationSec) * 1000;
}
/**
 * Force a browser reflow
 * @param element element where to apply the reflow
 */
function reflow(element) {
    return (element || document.body).getBoundingClientRect();
}

const noopFn = () => { };
const { transitionTimerDelayMs } = environment;
const runningTransitions = new Map();
const ngbRunTransition = (zone, element, startFn, options) => {
    // Getting initial context from options
    let context = options.context || {};
    // Checking if there are already running transitions on the given element.
    const running = runningTransitions.get(element);
    if (running) {
        switch (options.runningTransition) {
            // If there is one running and we want for it to 'continue' to run, we have to cancel the new one.
            // We're not emitting any values, but simply completing the observable (EMPTY).
            case 'continue':
                return EMPTY;
            // If there is one running and we want for it to 'stop', we have to complete the running one.
            // We're simply completing the running one and not emitting any values and merging newly provided context
            // with the one coming from currently running transition.
            case 'stop':
                zone.run(() => running.transition$.complete());
                context = Object.assign(running.context, context);
                runningTransitions.delete(element);
        }
    }
    // Running the start function
    const endFn = startFn(element, options.animation, context) || noopFn;
    // If 'prefer-reduced-motion' is enabled, the 'transition' will be set to 'none'.
    // If animations are disabled, we have to emit a value and complete the observable
    // In this case we have to call the end function, but can finish immediately by emitting a value,
    // completing the observable and executing end functions synchronously.
    if (!options.animation || window.getComputedStyle(element).transitionProperty === 'none') {
        zone.run(() => endFn());
        return of(undefined).pipe(runInZone(zone));
    }
    // Starting a new transition
    const transition$ = new Subject();
    const finishTransition$ = new Subject();
    const stop$ = transition$.pipe(endWith(true));
    runningTransitions.set(element, {
        transition$,
        complete: () => {
            finishTransition$.next({});
            finishTransition$.complete();
        },
        context
    });
    const transitionDurationMs = getTransitionDurationMs(element);
    // 1. We have to both listen for the 'transitionend' event and have a 'just-in-case' timer,
    // because 'transitionend' event might not be fired in some browsers, if the transitioning
    // element becomes invisible (ex. when scrolling, making browser tab inactive, etc.). The timer
    // guarantees, that we'll release the DOM element and complete 'ngbRunTransition'.
    // 2. We need to filter transition end events, because they might bubble from shorter transitions
    // on inner DOM elements. We're only interested in the transition on the 'element' itself.
    zone.runOutsideAngular(() => {
        const transitionEnd$ = fromEvent(element, 'transitionend').pipe(takeUntil(stop$), filter(({ target }) => target === element));
        const timer$ = timer(transitionDurationMs + transitionTimerDelayMs).pipe(takeUntil(stop$));
        race(timer$, transitionEnd$, finishTransition$)
            .pipe(takeUntil(stop$))
            .subscribe(() => {
            runningTransitions.delete(element);
            zone.run(() => {
                endFn();
                transition$.next({});
                transition$.complete();
            });
        });
    });
    return transition$.asObservable();
};
const ngbCompleteTransition = (element) => {
    runningTransitions.get(element)?.complete();
};

function measureCollapsingElementWidthPx(element) {
    // SSR fix for without injecting the PlatformId
    if (typeof navigator === 'undefined') {
        return '0px';
    }
    const { classList } = element;
    const hasShownClass = classList.contains('df-show');
    if (!hasShownClass) {
        classList.add('df-show');
    }
    element.style.width = '';
    const width = `${element.getBoundingClientRect().width}px`;
    if (!hasShownClass) {
        classList.remove('df-show');
    }
    return width;
}
const DfCollapsingTransition = (element, animation, context) => {
    const { direction, minimized } = context;
    let { maxWidth } = context;
    const { classList } = element;
    const dfcollapsingClass = 'df-collapsing';
    const dfminimizingClass = 'df-minimizing';
    function setInitialClasses() {
        if (minimized) {
            classList.add('df-minimized');
        }
        else {
            classList.add('df-collapse');
        }
        if (direction === 'show') {
            classList.add('df-show');
        }
        else {
            classList.remove('df-show');
        }
    }
    // without animations we just need to set initial classes
    if (!animation) {
        setInitialClasses();
        return () => { };
    }
    // No maxWidth -> running the transition for the first time
    if (!maxWidth) {
        maxWidth = measureCollapsingElementWidthPx(element);
        context.maxWidth = maxWidth;
        // Fix the width before starting the animation
        element.style.width = direction !== 'show' ? maxWidth : minimized ? '50px' : '0px';
        classList.remove('df-collapse');
        classList.remove('df-minimized'); // add
        classList.remove(dfcollapsingClass);
        classList.remove(dfminimizingClass);
        classList.remove('df-show');
        reflow(element);
        // Start the animation
        if (minimized) {
            classList.add(dfminimizingClass);
        }
        else {
            classList.add(dfcollapsingClass);
        }
    }
    // Start or revert the animation
    element.style.width = direction === 'show' ? maxWidth : minimized ? '50px' : '0px';
    return () => {
        setInitialClasses();
        classList.remove(dfcollapsingClass);
        classList.remove(dfminimizingClass);
        element.style.width = '';
    };
};

/**
 * A configuration service for the [DfSideNavCollapseDirective](#/components/sidenav/api#DfSideNavCollapseDirective) component.
 *
 * You can inject this service, typically in your root component, and customize its properties
 * to provide default values for the sideNav used in the application.
 */
class DfSideNavCollapseConfig {
    constructor(_ngbConfig) {
        this._ngbConfig = _ngbConfig;
    }
    get animation() {
        return this._animation === undefined ? this._ngbConfig.animation : this._animation;
    }
    set animation(animation) {
        this._animation = animation;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfSideNavCollapseConfig, deps: [{ token: i1.NgbConfig }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfSideNavCollapseConfig, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfSideNavCollapseConfig, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i1.NgbConfig }] });

/**
 * A service for managing the sidenav panel animation.
 * This service is included at root level
 *
 * Creating a sidenav is straightforward: use [DfSideNavComponent](#/components/sidenav/api#DfSideNavComponent) component
 */
class DfSideNavCollapseService {
    constructor(_config) {
        this._config = _config;
        this._sideNavAnimation$ = new BehaviorSubject(_config.animation);
    }
    get sideNavAnimation$() {
        return this._sideNavAnimation$.asObservable();
    }
    /**
     * Use this to activate or desactivate the animation for the sideNav
     * If input is `True` it will activate the animation.
     * Default value for animation is set in by DfSideNavCollapseConfig
     *
     */
    setAnimation(on) {
        this._sideNavAnimation$.next(on);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfSideNavCollapseService, deps: [{ token: DfSideNavCollapseConfig }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfSideNavCollapseService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfSideNavCollapseService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: DfSideNavCollapseConfig }] });

/**
 * A directive to provide a simple way of hiding and showing the sideNav on the page.
 */
class DfSideNavCollapseDirective {
    constructor(_element, _sideNavCollapseService, _zone) {
        this._element = _element;
        this._sideNavCollapseService = _sideNavCollapseService;
        this._zone = _zone;
        this.dfSideNavCollapseChange = new EventEmitter();
        /**
         * An event emitted when the collapse element is shown, after the transition. It has no payload.
         *
         * @since 9.1.0
         */
        this.shown = new EventEmitter();
        /**
         * An event emitted when the collapse element is hidden, after the transition. It has no payload.
         *
         * @since 9.1.0
         */
        this.hidden = new EventEmitter();
        this.minimized = false;
        this.animation = false;
        this.subscription = new Subscription();
        this.subscription.add(_sideNavCollapseService.sideNavAnimation$.subscribe((value) => {
            this.animation = value;
        }));
    }
    ngOnInit() {
        this._runTransition(this.collapsed, false, this.minimized);
    }
    ngOnChanges(simpleChanges) {
        if (simpleChanges['collapsed'] && !simpleChanges['collapsed'].firstChange) {
            this._runTransitionWithEvents(this.collapsed, this.animation, this.minimized);
        }
    }
    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
    /**
     * Triggers collapsing programmatically.
     *
     * If there is a collapsing transition running already, it will be reversed.
     * If the animations are turned off this happens synchronously.
     *
     * @since 9.1.0
     */
    toggle(open = this.collapsed) {
        this.collapsed = !open;
        this.dfSideNavCollapseChange.next(this.collapsed);
        this._runTransitionWithEvents(this.collapsed, this.animation, this.minimized);
    }
    _runTransition(collapsed, animation, minimizedInput) {
        return ngbRunTransition(this._zone, this._element.nativeElement, DfCollapsingTransition, {
            animation,
            runningTransition: 'stop',
            context: { direction: collapsed ? 'hide' : 'show', minimized: minimizedInput }
        });
    }
    _runTransitionWithEvents(collapsed, animation, minimized) {
        this._runTransition(collapsed, animation, minimized).subscribe(() => {
            if (collapsed) {
                this.hidden.emit();
            }
            else {
                this.shown.emit();
            }
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfSideNavCollapseDirective, deps: [{ token: i0.ElementRef }, { token: DfSideNavCollapseService }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.7", type: DfSideNavCollapseDirective, isStandalone: true, selector: "[dfSideNavCollapse]", inputs: { collapsed: ["dfSideNavCollapse", "collapsed"], minimized: "minimized" }, outputs: { dfSideNavCollapseChange: "dfSideNavCollapseChange", shown: "shown", hidden: "hidden" }, exportAs: ["dfSideNavCollapse"], usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfSideNavCollapseDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[dfSideNavCollapse]', exportAs: 'dfSideNavCollapse', standalone: true }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: DfSideNavCollapseService }, { type: i0.NgZone }], propDecorators: { collapsed: [{
                type: Input,
                args: ['dfSideNavCollapse']
            }], dfSideNavCollapseChange: [{
                type: Output
            }], shown: [{
                type: Output
            }], hidden: [{
                type: Output
            }], minimized: [{
                type: Input
            }] } });

class DfSideNavCollapseModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfSideNavCollapseModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.7", ngImport: i0, type: DfSideNavCollapseModule, imports: [DfSideNavCollapseDirective], exports: [DfSideNavCollapseDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfSideNavCollapseModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfSideNavCollapseModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [DfSideNavCollapseDirective],
                    exports: [DfSideNavCollapseDirective]
                }]
        }] });

class DfManageSideNavDirective {
    constructor(element, renderer) {
        this.element = element;
        this.renderer = renderer;
    }
    ngAfterViewChecked() {
        if (this.element.nativeElement.querySelector('.active')) {
            this.renderer.addClass(this.element.nativeElement, 'df-sidenav-list-withactivated');
        }
        else {
            this.renderer.removeClass(this.element.nativeElement, 'df-sidenav-list-withactivated');
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfManageSideNavDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.7", type: DfManageSideNavDirective, isStandalone: true, selector: "[dfManageSideNav]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfManageSideNavDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[dfManageSideNav]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }] });

/**
 * @param element the HTML element
 * @returns whether the element is overflowing
 */
function hasOverflow(element) {
    return element?.offsetHeight < element?.scrollHeight || element?.offsetWidth < element?.scrollWidth;
}

/**
 * DfTitleTruncate directive
 * It adds the title attribute with the element's inner text as value
 * when the element is truncated.
 */
class DfTitleTruncateDirective {
    constructor(elementRef, renderer) {
        this.elementRef = elementRef;
        this.renderer = renderer;
    }
    ngOnInit() {
        this.renderer.addClass(this.elementRef.nativeElement, 'text-truncate');
    }
    ngDoCheck() {
        const { nativeElement } = this.elementRef;
        const titleAttributeName = 'title';
        if (hasOverflow(nativeElement)) {
            const value = nativeElement.innerText;
            nativeElement.setAttribute(titleAttributeName, value);
        }
        else if (nativeElement.getAttribute(titleAttributeName)) {
            nativeElement.removeAttribute(titleAttributeName);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfTitleTruncateDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.7", type: DfTitleTruncateDirective, isStandalone: true, selector: "[dfTitleTruncate]", exportAs: ["dfTitleTruncate"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfTitleTruncateDirective, decorators: [{
            type: Directive,
            args: [{
                    exportAs: 'dfTitleTruncate',
                    selector: '[dfTitleTruncate]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }] });

let uniqueId = 0;
/**
 * SideNav is a component to provide navigation feature with a panel on the side of your page
 *
 * It supports several options as:
 *  - Being in an overlay menu or not
 *  - Being collapsed or not
 *  - Can be used with or without Angular Router
 *  - Can be used with media queries to adjust behavior to different breakpoints (see media section)
 */
class DfSideNavListComponent {
    constructor() {
        this.controlledUniqueId = this.getControlledUniqueId();
        this.sideNavService = inject(DfSideNavService);
    }
    getControlledUniqueId() {
        uniqueId++;
        return `df-sidenav-id-${uniqueId};`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfSideNavListComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.7", type: DfSideNavListComponent, isStandalone: true, selector: "df-sidenavlist", inputs: { items: "items", tplIcon: "tplIcon", tplItem: "tplItem", currentItem: "currentItem", id: "id" }, ngImport: i0, template: "<ul class=\"df-sidenav-list\" [ngbCollapse]=\"currentItem?.isCollapsed ?? false\" [attr.id]=\"id\">\n  @for (item of items; track item; let i = $index) {\n    <li dfManageSideNav>\n      <ng-template #dfSideNavItemContent>\n        @if (tplIcon && item.icon) {\n          <ng-template\n            [ngTemplateOutlet]=\"tplIcon.templateRef\"\n            [ngTemplateOutletContext]=\"{ $implicit: item }\"\n          ></ng-template>\n        } @else if (item.icon) {\n          <span aria-hidden=\"true\" class=\"{{ item.icon }} df-sidenav-icon me-3\"></span>\n        }\n        <span class=\"flex-grow-1 text-nowrap\" dfTitleTruncate>{{ item.title }}</span>\n      </ng-template>\n      @switch (item.type) {\n        @case (sideNavService.type.subMenus) {\n          <ng-template #subMenus>\n            <button\n              class=\"df-sidenav-item d-flex align-items-center\"\n              (click)=\"sideNavService.toggle(item)\"\n              type=\"button\"\n              [attr.aria-expanded]=\"!item.isCollapsed\"\n              [attr.aria-controls]=\"'sidenavControl' + controlledUniqueId.toString() + '-' + i\"\n              [class.df-sidenav-item-withicon]=\"item.icon\"\n            >\n              @if (tplIcon && item.icon) {\n                <ng-template\n                  [ngTemplateOutlet]=\"tplIcon.templateRef\"\n                  [ngTemplateOutletContext]=\"{ $implicit: item }\"\n                ></ng-template>\n              } @else if (item.icon) {\n                <span aria-hidden=\"true\" class=\"{{ item.icon }} df-sidenav-icon me-3\"></span>\n              }\n              <span class=\"flex-grow-1 text-nowrap\" dfTitleTruncate>{{ item.title }}</span>\n              <span\n                class=\"ms-3 df-sidenav-collapse-icon fa-light\"\n                [class.fa-chevron-down]=\"item.isCollapsed\"\n                [class.fa-chevron-up]=\"!item.isCollapsed\"\n              ></span>\n              <!-- put the chevron as icon directly in the menu -->\n              <!-- todo the icon and the minimized -->\n            </button>\n            <!-- we don't pass the icons as they are not suppose to have ones ??-->\n            <df-sidenavlist\n              [items]=\"item.subMenus ?? []\"\n              [tplItem]=\"tplItem\"\n              [currentItem]=\"item\"\n              [id]=\"'sidenavControl' + controlledUniqueId.toString() + '-' + i\"\n            ></df-sidenavlist>\n          </ng-template>\n          @if (!item.breakpoints) {\n            <ng-container *ngTemplateOutlet=\"subMenus\"></ng-container>\n          } @else {\n            <ng-container *dfIfMedia=\"item.breakpoints!\">\n              <ng-container *ngTemplateOutlet=\"subMenus\"></ng-container>\n            </ng-container>\n          }\n        }\n        @case (sideNavService.type.actionButton) {\n          <ng-template #dfSideNavItemButton>\n            <button\n              class=\"df-sidenav-item d-flex align-items-center flex-nowrap\"\n              (click)=\"sideNavService.activeItem(item)\"\n              [class.active]=\"item.isActive\"\n            >\n              <ng-container *ngTemplateOutlet=\"dfSideNavItemContent\"></ng-container>\n            </button>\n          </ng-template>\n          @if (!item.breakpoints) {\n            <ng-container *ngTemplateOutlet=\"dfSideNavItemButton\"></ng-container>\n          } @else {\n            <ng-container *dfIfMedia=\"item.breakpoints!\">\n              <ng-container *ngTemplateOutlet=\"dfSideNavItemButton\"></ng-container>\n            </ng-container>\n          }\n        }\n        @case (sideNavService.type.routerLink) {\n          <ng-template #dfRouterTemplate>\n            <a\n              class=\"df-sidenav-item d-flex align-items-center flex-nowrap\"\n              routerLinkActive=\"active\"\n              routerLink=\"{{ item.value }}\"\n              ariaCurrentWhenActive=\"page\"\n            >\n              <ng-container *ngTemplateOutlet=\"dfSideNavItemContent\"></ng-container>\n            </a>\n          </ng-template>\n          @if (!item.breakpoints) {\n            <ng-container *ngTemplateOutlet=\"dfRouterTemplate\"></ng-container>\n          } @else {\n            <ng-container *dfIfMedia=\"item.breakpoints!\">\n              <ng-container *ngTemplateOutlet=\"dfRouterTemplate\"></ng-container>\n            </ng-container>\n          }\n        }\n        @case (sideNavService.type.separator) {\n          <ng-template #dfSeparatorTmp>\n            <hr class=\"spacing-01\" />\n          </ng-template>\n          @if (!item.breakpoints) {\n            <ng-container *ngTemplateOutlet=\"dfSeparatorTmp\"></ng-container>\n          } @else {\n            <ng-container *dfIfMedia=\"item.breakpoints!\">\n              <ng-container *ngTemplateOutlet=\"dfSeparatorTmp\"></ng-container>\n            </ng-container>\n          }\n        }\n        @case (sideNavService.type.template) {\n          @if (tplItem) {\n            <ng-template\n              [ngTemplateOutlet]=\"tplItem.templateRef\"\n              [ngTemplateOutletContext]=\"{ $implicit: item }\"\n            ></ng-template>\n          }\n        }\n        @case (sideNavService.type.title) {\n          <ng-template #dfTitleTmp>\n            <div class=\"df-sidenav-item df-sidenav-title d-flex align-items-center flex-grow-1\">\n              <span class=\"text-nowrap text-uppercase text-muted\" dfTitleTruncate>{{ item.title }}</span>\n            </div>\n          </ng-template>\n          @if (!item.breakpoints) {\n            <ng-container *ngTemplateOutlet=\"dfTitleTmp\"></ng-container>\n          } @else {\n            <ng-container *dfIfMedia=\"item.breakpoints!\">\n              <ng-container *ngTemplateOutlet=\"dfTitleTmp\"></ng-container>\n            </ng-container>\n          }\n        }\n        @default {\n          <ng-template #dfLinkDefaultTmp>\n            <a\n              class=\"df-sidenav-item d-flex align-items-center flex-nowrap\"\n              href=\"{{ item.value }}\"\n              [class.active]=\"item.isActive && item.type === sideNavService.type.link\"\n              [attr.target]=\"item.type === sideNavService.type.externalLink ? '_blank' : ''\"\n            >\n              <ng-container *ngTemplateOutlet=\"dfSideNavItemContent\"></ng-container>\n            </a>\n          </ng-template>\n          @if (!item.breakpoints) {\n            <ng-container *ngTemplateOutlet=\"dfLinkDefaultTmp\"></ng-container>\n          } @else {\n            <ng-container *dfIfMedia=\"item.breakpoints!\">\n              <ng-container *ngTemplateOutlet=\"dfLinkDefaultTmp\"></ng-container>\n            </ng-container>\n          }\n        }\n      }\n    </li>\n  }\n</ul>\n", dependencies: [{ kind: "component", type: DfSideNavListComponent, selector: "df-sidenavlist", inputs: ["items", "tplIcon", "tplItem", "currentItem", "id"] }, { kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1$1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "ngmodule", type: NgbCollapseModule }, { kind: "directive", type: i1.NgbCollapse, selector: "[ngbCollapse]", inputs: ["animation", "ngbCollapse", "horizontal"], outputs: ["ngbCollapseChange", "shown", "hidden"], exportAs: ["ngbCollapse"] }, { kind: "ngmodule", type: RouterModule }, { kind: "directive", type: i3.RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "info", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }, { kind: "directive", type: i3.RouterLinkActive, selector: "[routerLinkActive]", inputs: ["routerLinkActiveOptions", "ariaCurrentWhenActive", "routerLinkActive"], outputs: ["isActiveChange"], exportAs: ["routerLinkActive"] }, { kind: "ngmodule", type: DfMediaModule }, { kind: "directive", type: DfIfMediaDirective, selector: "[dfIfMedia]", inputs: ["dfIfMedia"] }, { kind: "ngmodule", type: DfSideNavCollapseModule }, { kind: "directive", type: DfManageSideNavDirective, selector: "[dfManageSideNav]" }, { kind: "directive", type: DfTitleTruncateDirective, selector: "[dfTitleTruncate]", exportAs: ["dfTitleTruncate"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfSideNavListComponent, decorators: [{
            type: Component,
            args: [{ selector: 'df-sidenavlist', standalone: true, imports: [
                        CommonModule,
                        NgbCollapseModule,
                        RouterModule,
                        DfMediaModule,
                        DfSideNavCollapseModule,
                        DfManageSideNavDirective,
                        DfTitleTruncateDirective
                    ], template: "<ul class=\"df-sidenav-list\" [ngbCollapse]=\"currentItem?.isCollapsed ?? false\" [attr.id]=\"id\">\n  @for (item of items; track item; let i = $index) {\n    <li dfManageSideNav>\n      <ng-template #dfSideNavItemContent>\n        @if (tplIcon && item.icon) {\n          <ng-template\n            [ngTemplateOutlet]=\"tplIcon.templateRef\"\n            [ngTemplateOutletContext]=\"{ $implicit: item }\"\n          ></ng-template>\n        } @else if (item.icon) {\n          <span aria-hidden=\"true\" class=\"{{ item.icon }} df-sidenav-icon me-3\"></span>\n        }\n        <span class=\"flex-grow-1 text-nowrap\" dfTitleTruncate>{{ item.title }}</span>\n      </ng-template>\n      @switch (item.type) {\n        @case (sideNavService.type.subMenus) {\n          <ng-template #subMenus>\n            <button\n              class=\"df-sidenav-item d-flex align-items-center\"\n              (click)=\"sideNavService.toggle(item)\"\n              type=\"button\"\n              [attr.aria-expanded]=\"!item.isCollapsed\"\n              [attr.aria-controls]=\"'sidenavControl' + controlledUniqueId.toString() + '-' + i\"\n              [class.df-sidenav-item-withicon]=\"item.icon\"\n            >\n              @if (tplIcon && item.icon) {\n                <ng-template\n                  [ngTemplateOutlet]=\"tplIcon.templateRef\"\n                  [ngTemplateOutletContext]=\"{ $implicit: item }\"\n                ></ng-template>\n              } @else if (item.icon) {\n                <span aria-hidden=\"true\" class=\"{{ item.icon }} df-sidenav-icon me-3\"></span>\n              }\n              <span class=\"flex-grow-1 text-nowrap\" dfTitleTruncate>{{ item.title }}</span>\n              <span\n                class=\"ms-3 df-sidenav-collapse-icon fa-light\"\n                [class.fa-chevron-down]=\"item.isCollapsed\"\n                [class.fa-chevron-up]=\"!item.isCollapsed\"\n              ></span>\n              <!-- put the chevron as icon directly in the menu -->\n              <!-- todo the icon and the minimized -->\n            </button>\n            <!-- we don't pass the icons as they are not suppose to have ones ??-->\n            <df-sidenavlist\n              [items]=\"item.subMenus ?? []\"\n              [tplItem]=\"tplItem\"\n              [currentItem]=\"item\"\n              [id]=\"'sidenavControl' + controlledUniqueId.toString() + '-' + i\"\n            ></df-sidenavlist>\n          </ng-template>\n          @if (!item.breakpoints) {\n            <ng-container *ngTemplateOutlet=\"subMenus\"></ng-container>\n          } @else {\n            <ng-container *dfIfMedia=\"item.breakpoints!\">\n              <ng-container *ngTemplateOutlet=\"subMenus\"></ng-container>\n            </ng-container>\n          }\n        }\n        @case (sideNavService.type.actionButton) {\n          <ng-template #dfSideNavItemButton>\n            <button\n              class=\"df-sidenav-item d-flex align-items-center flex-nowrap\"\n              (click)=\"sideNavService.activeItem(item)\"\n              [class.active]=\"item.isActive\"\n            >\n              <ng-container *ngTemplateOutlet=\"dfSideNavItemContent\"></ng-container>\n            </button>\n          </ng-template>\n          @if (!item.breakpoints) {\n            <ng-container *ngTemplateOutlet=\"dfSideNavItemButton\"></ng-container>\n          } @else {\n            <ng-container *dfIfMedia=\"item.breakpoints!\">\n              <ng-container *ngTemplateOutlet=\"dfSideNavItemButton\"></ng-container>\n            </ng-container>\n          }\n        }\n        @case (sideNavService.type.routerLink) {\n          <ng-template #dfRouterTemplate>\n            <a\n              class=\"df-sidenav-item d-flex align-items-center flex-nowrap\"\n              routerLinkActive=\"active\"\n              routerLink=\"{{ item.value }}\"\n              ariaCurrentWhenActive=\"page\"\n            >\n              <ng-container *ngTemplateOutlet=\"dfSideNavItemContent\"></ng-container>\n            </a>\n          </ng-template>\n          @if (!item.breakpoints) {\n            <ng-container *ngTemplateOutlet=\"dfRouterTemplate\"></ng-container>\n          } @else {\n            <ng-container *dfIfMedia=\"item.breakpoints!\">\n              <ng-container *ngTemplateOutlet=\"dfRouterTemplate\"></ng-container>\n            </ng-container>\n          }\n        }\n        @case (sideNavService.type.separator) {\n          <ng-template #dfSeparatorTmp>\n            <hr class=\"spacing-01\" />\n          </ng-template>\n          @if (!item.breakpoints) {\n            <ng-container *ngTemplateOutlet=\"dfSeparatorTmp\"></ng-container>\n          } @else {\n            <ng-container *dfIfMedia=\"item.breakpoints!\">\n              <ng-container *ngTemplateOutlet=\"dfSeparatorTmp\"></ng-container>\n            </ng-container>\n          }\n        }\n        @case (sideNavService.type.template) {\n          @if (tplItem) {\n            <ng-template\n              [ngTemplateOutlet]=\"tplItem.templateRef\"\n              [ngTemplateOutletContext]=\"{ $implicit: item }\"\n            ></ng-template>\n          }\n        }\n        @case (sideNavService.type.title) {\n          <ng-template #dfTitleTmp>\n            <div class=\"df-sidenav-item df-sidenav-title d-flex align-items-center flex-grow-1\">\n              <span class=\"text-nowrap text-uppercase text-muted\" dfTitleTruncate>{{ item.title }}</span>\n            </div>\n          </ng-template>\n          @if (!item.breakpoints) {\n            <ng-container *ngTemplateOutlet=\"dfTitleTmp\"></ng-container>\n          } @else {\n            <ng-container *dfIfMedia=\"item.breakpoints!\">\n              <ng-container *ngTemplateOutlet=\"dfTitleTmp\"></ng-container>\n            </ng-container>\n          }\n        }\n        @default {\n          <ng-template #dfLinkDefaultTmp>\n            <a\n              class=\"df-sidenav-item d-flex align-items-center flex-nowrap\"\n              href=\"{{ item.value }}\"\n              [class.active]=\"item.isActive && item.type === sideNavService.type.link\"\n              [attr.target]=\"item.type === sideNavService.type.externalLink ? '_blank' : ''\"\n            >\n              <ng-container *ngTemplateOutlet=\"dfSideNavItemContent\"></ng-container>\n            </a>\n          </ng-template>\n          @if (!item.breakpoints) {\n            <ng-container *ngTemplateOutlet=\"dfLinkDefaultTmp\"></ng-container>\n          } @else {\n            <ng-container *dfIfMedia=\"item.breakpoints!\">\n              <ng-container *ngTemplateOutlet=\"dfLinkDefaultTmp\"></ng-container>\n            </ng-container>\n          }\n        }\n      }\n    </li>\n  }\n</ul>\n" }]
        }], propDecorators: { items: [{
                type: Input
            }], tplIcon: [{
                type: Input
            }], tplItem: [{
                type: Input
            }], currentItem: [{
                type: Input
            }], id: [{
                type: Input
            }] } });

class DfSideNavHeaderDirective {
    constructor(templateRef) {
        this.templateRef = templateRef;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfSideNavHeaderDirective, deps: [{ token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.7", type: DfSideNavHeaderDirective, isStandalone: true, selector: "ng-template[dfSideNavHeader]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfSideNavHeaderDirective, decorators: [{
            type: Directive,
            args: [{ selector: 'ng-template[dfSideNavHeader]', standalone: true }]
        }], ctorParameters: () => [{ type: i0.TemplateRef }] });
class DfSideNavIconDirective {
    constructor(templateRef) {
        this.templateRef = templateRef;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfSideNavIconDirective, deps: [{ token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.7", type: DfSideNavIconDirective, isStandalone: true, selector: "ng-template[dfSideNavIcon]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfSideNavIconDirective, decorators: [{
            type: Directive,
            args: [{ selector: 'ng-template[dfSideNavIcon]', standalone: true }]
        }], ctorParameters: () => [{ type: i0.TemplateRef }] });
class DfSideNavItemDirective {
    constructor(templateRef) {
        this.templateRef = templateRef;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfSideNavItemDirective, deps: [{ token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.7", type: DfSideNavItemDirective, isStandalone: true, selector: "ng-template[dfSideNavItem]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfSideNavItemDirective, decorators: [{
            type: Directive,
            args: [{ selector: 'ng-template[dfSideNavItem]', standalone: true }]
        }], ctorParameters: () => [{ type: i0.TemplateRef }] });
/**
 * SideNav is a component to provide navigation feature with a panel on the side of your page
 *
 * It supports several options as:
 *  - Being in an overlay menu or not
 *  - Being collapsed or not
 *  - Can be used with or without Angular Router
 *  - Can be used with media queries to adjust behavior to different breakpoints (see media section)
 */
class DfSideNavComponent {
    constructor() {
        this.elements = 'df-sidenav-light d-flex flex-column flex-shrink-0';
        this.subscription = new Subscription();
        this.sideNavService = inject(DfSideNavService);
        this.subscription.add(this.sideNavService.isInMenuOverlay$.subscribe((value) => {
            this.overlayClass = value;
        }));
        setTimeout(() => this.sideNavService.setSideNav());
    }
    ngOnDestroy() {
        this.subscription.unsubscribe();
        this.sideNavService.removeSideBar();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfSideNavComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.7", type: DfSideNavComponent, isStandalone: true, selector: "df-sidenav", host: { properties: { "class.df-sidenav-menu-overlay": "this.overlayClass", "class": "this.elements" } }, queries: [{ propertyName: "tplHeader", first: true, predicate: DfSideNavHeaderDirective, descendants: true }, { propertyName: "tplIcon", first: true, predicate: DfSideNavIconDirective, descendants: true }, { propertyName: "tplItem", first: true, predicate: DfSideNavItemDirective, descendants: true }], ngImport: i0, template: "@if (tplHeader) {\n  <ng-template [ngTemplateOutlet]=\"tplHeader.templateRef\"></ng-template>\n}\n<df-sidenavlist\n  [items]=\"(sideNavService.sideNavItems$ | async) ?? []\"\n  [tplIcon]=\"tplIcon\"\n  [tplItem]=\"tplItem\"\n></df-sidenavlist>\n", dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1$1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "pipe", type: i1$1.AsyncPipe, name: "async" }, { kind: "component", type: DfSideNavListComponent, selector: "df-sidenavlist", inputs: ["items", "tplIcon", "tplItem", "currentItem", "id"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfSideNavComponent, decorators: [{
            type: Component,
            args: [{ selector: 'df-sidenav', standalone: true, imports: [CommonModule, DfSideNavListComponent], template: "@if (tplHeader) {\n  <ng-template [ngTemplateOutlet]=\"tplHeader.templateRef\"></ng-template>\n}\n<df-sidenavlist\n  [items]=\"(sideNavService.sideNavItems$ | async) ?? []\"\n  [tplIcon]=\"tplIcon\"\n  [tplItem]=\"tplItem\"\n></df-sidenavlist>\n" }]
        }], ctorParameters: () => [], propDecorators: { tplHeader: [{
                type: ContentChild,
                args: [DfSideNavHeaderDirective, { static: false }]
            }], tplIcon: [{
                type: ContentChild,
                args: [DfSideNavIconDirective, { static: false }]
            }], tplItem: [{
                type: ContentChild,
                args: [DfSideNavItemDirective, { static: false }]
            }], overlayClass: [{
                type: HostBinding,
                args: ['class.df-sidenav-menu-overlay']
            }], elements: [{
                type: HostBinding,
                args: ['class']
            }] } });

/**
 * The directive to catch focusin event and put the focus at a defined position in the page
 *
 * @since 9.1.0
 */
class DfExcludeTrapDirective {
    constructor(zone, element) {
        this.zone = zone;
        this.element = element;
        this.subscription = new Subscription();
    }
    init() {
        this.subscription = fromEvent(this.element.nativeElement, 'focusin').subscribe(() => {
            const el = document.querySelector(this.dfExcludeTrapSelector);
            if (el) {
                el.focus();
            }
        });
    }
    ngOnInit() {
        this.zone.runOutsideAngular(() => {
            this.subscriptionInput = this.dfExcludeTrap.subscribe(isPresent => {
                if (isPresent) {
                    this.init();
                }
                else {
                    this.subscription.unsubscribe();
                }
            });
        });
    }
    ngOnDestroy() {
        this.subscription.unsubscribe();
        if (this.subscriptionInput) {
            this.subscriptionInput.unsubscribe();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfExcludeTrapDirective, deps: [{ token: i0.NgZone }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.7", type: DfExcludeTrapDirective, isStandalone: true, selector: "[dfExcludeTrap]", inputs: { dfExcludeTrapSelector: "dfExcludeTrapSelector", dfExcludeTrap: "dfExcludeTrap" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfExcludeTrapDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[dfExcludeTrap]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.NgZone }, { type: i0.ElementRef }], propDecorators: { dfExcludeTrapSelector: [{
                type: Input
            }], dfExcludeTrap: [{
                type: Input
            }] } });

const DF_SELECT_CLASS = 'df-ym';

class DfManageNavSelectDirective {
    constructor(select, element, rtlDirectionService, renderer) {
        this.select = select;
        this.element = element;
        this.rtlDirectionService = rtlDirectionService;
        this.renderer = renderer;
    }
    ngOnInit() {
        this.direction = this.rtlDirectionService.getPageDirection(this.element);
    }
    handleKeyDown(event) {
        if (this.direction === RightToLeftDirectionEnum.LeftToRight) {
            this.arrowLeftInnerHandler(event);
        }
        else {
            this.arrowRightInnerHandler(event);
        }
    }
    handleKeyDownBackspace(event) {
        // Clear on backspace is already handled by ng select when clearable is true
        if (!this.select.clearable && !this.select.searchTerm) {
            const listBadge = this.element.nativeElement.querySelectorAll(`.${DF_SELECT_CLASS}`);
            if (listBadge.length > 0) {
                listBadge[listBadge.length - 1].click();
            }
        }
    }
    handleKeyDownRight(event) {
        if (this.direction === RightToLeftDirectionEnum.LeftToRight) {
            this.arrowRightInnerHandler(event);
        }
        else {
            this.arrowLeftInnerHandler(event);
        }
    }
    arrowRightInnerHandler(event) {
        const listBadge = this.element.nativeElement.querySelectorAll(`.${DF_SELECT_CLASS}`);
        const currentPos = Array.prototype.indexOf.call(listBadge, document.activeElement);
        // Test if the focus is in the list (otherwise it is in the input and we do nothing)
        if (currentPos !== -1) {
            if (currentPos === listBadge.length - 1) {
                this.select.focus();
            }
            else {
                (listBadge[currentPos + 1] || document.activeElement).focus();
            }
        }
        else if (event.target.selectionStart === event.target.selectionEnd &&
            event.target.nodeName === 'INPUT' &&
            event.target.selectionStart === event.target.value.length) {
            const clearEl = this.element.nativeElement.querySelector('.ng-clear-wrapper') ||
                this.element.nativeElement.querySelector('.ng-clear');
            if (clearEl) {
                // Event if it can be added multiple time it is not an issue I think.
                this.renderer.listen(clearEl, 'keydown.Enter', (e) => {
                    e.stopPropagation(); // kill the event to forbid default ng-select behavior (open the dropdown)
                    this.select.handleClearClick();
                });
                clearEl.setAttribute('tabindex', '1'); // To be able to focus the element in chrome
                clearEl.focus();
            }
        }
    }
    arrowLeftInnerHandler(event) {
        const listBadge = this.element.nativeElement.querySelectorAll(`.${DF_SELECT_CLASS}`);
        if (event.target.selectionStart === event.target.selectionEnd &&
            event.target.selectionStart === 0 &&
            event.target.nodeName === 'INPUT') {
            if (listBadge.length > 0) {
                listBadge[listBadge.length - 1].focus();
            }
        }
        else if (event.target.classList.contains('ng-clear-wrapper') || event.target.classList.contains('ng-clear')) {
            this.select.focus();
        }
        else {
            const currentPos = Array.prototype.indexOf.call(listBadge, document.activeElement);
            (listBadge[currentPos - 1] || document.activeElement).focus();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfManageNavSelectDirective, deps: [{ token: i1$2.NgSelectComponent, host: true }, { token: i0.ElementRef }, { token: DfDirectionDetectionService }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.7", type: DfManageNavSelectDirective, isStandalone: true, selector: "[dfManageNavSelect]", host: { listeners: { "keydown.arrowLeft": "handleKeyDown($event)", "keydown.Backspace": "handleKeyDownBackspace($event)", "keydown.arrowRight": "handleKeyDownRight($event)" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfManageNavSelectDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[dfManageNavSelect]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i1$2.NgSelectComponent, decorators: [{
                    type: Host
                }] }, { type: i0.ElementRef }, { type: DfDirectionDetectionService }, { type: i0.Renderer2 }], propDecorators: { handleKeyDown: [{
                type: HostListener,
                args: ['keydown.arrowLeft', ['$event']]
            }], handleKeyDownBackspace: [{
                type: HostListener,
                args: ['keydown.Backspace', ['$event']]
            }], handleKeyDownRight: [{
                type: HostListener,
                args: ['keydown.arrowRight', ['$event']]
            }] } });

// Copy of https://github.com/ng-select/ng-select/blob/master/src/ng-option-highlight/lib/ng-option-highlight.directive.ts
class DfOptionHighlightDirective {
    constructor(elementRef, renderer) {
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.element = this.elementRef.nativeElement;
    }
    ngOnChanges() {
        if (this.canHighlight) {
            this.highlightLabel();
        }
        else if (this.label) { // unhighlight if there was some
            this.setInnerHtml(this.label);
        }
    }
    ngAfterViewInit() {
        this.label = this.element.innerHTML;
        if (this.canHighlight) {
            this.highlightLabel();
        }
    }
    escapeRegExp(str) {
        return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }
    highlightLabel() {
        const label = this.label;
        if (!this.term) {
            this.setInnerHtml(label);
            return;
        }
        const alternationString = this.escapeRegExp(this.term).replace(' ', '|');
        const termRegex = new RegExp(alternationString, 'gi');
        this.setInnerHtml(label.replace(termRegex, `<span class=\"highlighted\">$&</span>`));
    }
    get canHighlight() {
        return this.isDefined(this.term) && this.isDefined(this.label);
    }
    setInnerHtml(html) {
        this.renderer.setProperty(this.elementRef.nativeElement, 'innerHTML', html);
    }
    isDefined(value) {
        return value !== undefined && value !== null;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfOptionHighlightDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.7", type: DfOptionHighlightDirective, isStandalone: true, selector: "[dfOptionHighlight]", inputs: { term: ["dfOptionHighlight", "term"] }, usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfOptionHighlightDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[dfOptionHighlight]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }], propDecorators: { term: [{
                type: Input,
                args: ['dfOptionHighlight']
            }] } });

// Put to avoid opening of the select after removal. See why in file:
// https://github.com/ng-select/ng-select/blob/57c6671a972d23beddcca2b6acc9418544c17a2e/src/ng-select/ng-select.component.ts#L307
const NG_SELECT_CLASS_IDENTIFIER = 'ng-value-icon';
class DfManageBadgeEventsDirective {
    constructor(renderer, element) {
        this.renderer = renderer;
        this.element = element;
        this.tabindex = '-1';
        this.renderer.addClass(this.element.nativeElement, DF_SELECT_CLASS);
        this.renderer.addClass(this.element.nativeElement, NG_SELECT_CLASS_IDENTIFIER);
    }
    ngAfterViewInit() {
        const childs = this.element.nativeElement.children;
        const map = Array.from(childs);
        map.forEach((element) => {
            this.renderer.addClass(element, NG_SELECT_CLASS_IDENTIFIER);
        });
    }
    handleKeyDownBackspace(event) {
        this.dfManageBadgeEventsSelect[0](this.dfManageBadgeEventsSelect[1]);
        event.stopPropagation();
    }
    handleClick(event) {
        this.dfManageBadgeEventsSelect[0](this.dfManageBadgeEventsSelect[1]);
        event.stopPropagation();
    }
    handleKeyDownEnter(event) {
        this.dfManageBadgeEventsSelect[0](this.dfManageBadgeEventsSelect[1]);
        event.stopPropagation();
    }
    handleKeyDownDelete(event) {
        this.dfManageBadgeEventsSelect[0](this.dfManageBadgeEventsSelect[1]);
        event.stopPropagation();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfManageBadgeEventsDirective, deps: [{ token: i0.Renderer2 }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.7", type: DfManageBadgeEventsDirective, isStandalone: true, selector: "[dfManageBadgeEventsSelect]", inputs: { dfManageBadgeEventsSelect: "dfManageBadgeEventsSelect" }, host: { listeners: { "keydown.Backspace": "handleKeyDownBackspace($event)", "click": "handleClick($event)", "keydown.Enter": "handleKeyDownEnter($event)", "keydown.Delete": "handleKeyDownDelete($event)" }, properties: { "attr.tabindex": "this.tabindex" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfManageBadgeEventsDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[dfManageBadgeEventsSelect]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.Renderer2 }, { type: i0.ElementRef }], propDecorators: { dfManageBadgeEventsSelect: [{
                type: Input
            }], tabindex: [{
                type: HostBinding,
                args: ['attr.tabindex']
            }], handleKeyDownBackspace: [{
                type: HostListener,
                args: ['keydown.Backspace', ['$event']]
            }], handleClick: [{
                type: HostListener,
                args: ['click', ['$event']]
            }], handleKeyDownEnter: [{
                type: HostListener,
                args: ['keydown.Enter', ['$event']]
            }], handleKeyDownDelete: [{
                type: HostListener,
                args: ['keydown.Delete', ['$event']]
            }] } });

class DfSelectModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfSelectModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.7", ngImport: i0, type: DfSelectModule, imports: [DfManageNavSelectDirective, DfManageBadgeEventsDirective, DfOptionHighlightDirective], exports: [DfManageNavSelectDirective, DfManageBadgeEventsDirective, DfOptionHighlightDirective, NgSelectModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfSelectModule, imports: [NgSelectModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfSelectModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [DfManageNavSelectDirective, DfManageBadgeEventsDirective, DfOptionHighlightDirective],
                    exports: [DfManageNavSelectDirective, DfManageBadgeEventsDirective, DfOptionHighlightDirective, NgSelectModule]
                }]
        }] });

class DfStepperService {
    constructor() {
        this.setFocus = false;
        this._stepperState$ = new BehaviorSubject({
            steps: [],
            active: 0,
            isLinear: false,
            isInline: false,
            isVertical: false
        });
        this._state = {
            steps: [],
            active: 0,
            isLinear: false,
            isInline: false,
            isVertical: false
        };
        this._completedLabel = 'Completed';
        this._warningLabel = 'Warning';
        this._stepperAriaLabel = 'Stepper';
    }
    get stepperState$() {
        return this._stepperState$.asObservable();
    }
    // initState must be used to push new Steps to the service
    get state() {
        return this._state;
    }
    dispatchStepperState() {
        this._stepperState$.next(this._state);
    }
    updateStepperState() {
        this._state = this.normalizeState(this._state);
        this.dispatchStepperState();
    }
    // API
    /**
     * Use this to init the list of Elements from your stepper
     *
     * Here we are not deep copying the array --> the function will change it
     */
    initStepperState(state) {
        // normalize the steps here
        this._state = this.normalizeState(state);
        this.dispatchStepperState();
    }
    /**
     * Use this to normalize the state of the stepper (completed, warning, current, future)
     * @param state
     * @returns
     */
    normalizeState(state) {
        const stepsList = [];
        for (let i = 0; i < state.steps.length; i++) {
            if (state.steps[i].warning) {
                stepsList.push({ ...state.steps[i], type: 'warning' });
            }
            else if (state.steps[i].completed) {
                stepsList.push({ ...state.steps[i], type: 'completed' });
            }
            else if (i === state.active || state.steps[i].visited) {
                stepsList.push({ ...state.steps[i], type: 'visited', visited: true });
            }
            else {
                stepsList.push({ ...state.steps[i], type: 'future', visited: false });
            }
        }
        return { ...state, steps: stepsList };
    }
    /**
     * Internal to get the stepper aria label in the template
     */
    get stepperAriaLabel() {
        return this._stepperAriaLabel;
    }
    /**
     * Use this to set the stepper aria label (i18n)
     */
    set stepperAriaLabel(label) {
        this._stepperAriaLabel = label;
    }
    /**
     * Internal to get the completed label in the template
     */
    get completedLabel() {
        return this._completedLabel;
    }
    /**
     * Internal to get the waring label in the template
     */
    get warningLabel() {
        return this._warningLabel;
    }
    /**
     * Use this to set the completed label (i18n)
     */
    set completedLabel(label) {
        this._completedLabel = label;
    }
    /**
     * Use this to set the warning label (i18n)
     */
    set warningLabel(label) {
        this._warningLabel = label;
    }
    /**
     * TODO make it customizable
     * @param index - index of the step
     * @returns - string of the step number to display
     */
    getStepNumberLabel(index) {
        return `${index + 1}`;
    }
    resetFocus() {
        this.setFocus = false;
    }
    /**
     *
     * @param isLinear - true if the stepper is linear
     */
    setLinear(isLinear) {
        this._state.isLinear = isLinear;
        this.dispatchStepperState();
    }
    /**
     *
     * @param isInline - true if the stepper is inline
     */
    setInline(isInline) {
        this._state.isInline = isInline;
        this.dispatchStepperState();
    }
    /**
     *
     * @param index - index of the step to select start at 0
     */
    selectStep(index) {
        if (index < 0 || index >= this._state.steps.length) {
            return;
        }
        this._state.active = index;
        this.updateStepperState();
    }
    /**
     * Same as selectStep but with focus on the step selected
     * @param index - index of the step to select start at 0
     */
    selectWithFocus(index) {
        if (index < 0 || index >= this._state.steps.length) {
            return;
        }
        this.setFocus = true;
        this.selectStep(index);
    }
    /**
     *
     * @param index - index of the step to complete start at 0
     */
    completeStep(index) {
        if (index < 0 || index >= this._state.steps.length) {
            return;
        }
        this._state.steps[index].completed = true;
        this._state.steps[index].warning = false;
        this.updateStepperState();
    }
    /**
     *
     * @param index - index of the step to complete start at 0
     */
    completeStepAndGoNext(index) {
        this.completeStep(index);
        this.selectStep(index + 1);
    }
    /**
     * Attention if the step was completed it will be uncompleted after.
     * @param index - index of the step to make Warning start at 0
     * @param warningLabel - label to display in the warning by default it will take the warningLabel if it exists or the label
     */
    makeWarning(index, warningLabel) {
        if (index < 0 || index >= this._state.steps.length) {
            return;
        }
        this._state.steps[index].warning = true;
        this._state.steps[index].completed = false;
        this._state.steps[index].warningLabel = warningLabel
            ? warningLabel
            : this._state.steps[index].warningLabel
                ? this._state.steps[index].warningLabel
                : this._state.steps[index].label;
        this.updateStepperState();
    }
    /**
     *
     * @param index - index of the step to remove Warning start at 0
     */
    removeWarning(index) {
        if (index < 0 || index >= this._state.steps.length) {
            return;
        }
        this._state.steps[index].warning = false;
        this.updateStepperState();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfStepperService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfStepperService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfStepperService, decorators: [{
            type: Injectable
        }] });

const DF_STEP_CLASS = 'df-stepper-step';
class DfStepperStepDirective {
    constructor() {
        this.unlistener = [];
        this.renderer2 = inject(Renderer2);
        this.element = inject(ElementRef);
    }
    ngOnInit() {
        this.unlistener.push(this.renderer2.listen(this.element.nativeElement, 'focus', () => {
            this.handleFocusTabIndex();
        }));
        this.unlistener.push(this.renderer2.listen(this.element.nativeElement, 'blur', (event) => {
            this.handleBlurTabIndex(event);
        }));
    }
    handleFocusTabIndex() {
        this.element.nativeElement.tabIndex = 0;
    }
    handleBlurTabIndex(event) {
        if (event.relatedTarget &&
            'classList' in event.relatedTarget &&
            event.relatedTarget.classList.contains(DF_STEP_CLASS)) {
            this.element.nativeElement.tabIndex = -1;
        }
    }
    ngOnDestroy() {
        this.unlistener.forEach((ul) => ul());
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfStepperStepDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.7", type: DfStepperStepDirective, isStandalone: true, selector: "[dfStepperStep]", exportAs: ["dfStepperStep"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfStepperStepDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[dfStepperStep]',
                    exportAs: 'dfStepperStep',
                    standalone: true
                }]
        }] });
class DfStepperDirective {
    constructor() {
        this.stepperService = inject(DfStepperService);
        this.element = inject(ElementRef);
        this.rtlDirectionService = inject(DfDirectionDetectionService);
    }
    ngOnInit() {
        this.direction = this.rtlDirectionService.getPageDirection(this.element);
    }
    handleEnterDown() {
        this.handleSelect();
    }
    handleSpaceDown() {
        this.handleSelect();
    }
    handleGoPrevious() {
        if (this.direction === RightToLeftDirectionEnum.LeftToRight) {
            this.arrowLeftInnerHandler();
        }
        else {
            this.arrowRightInnerHandler();
        }
    }
    handleGoNext() {
        if (this.direction === RightToLeftDirectionEnum.LeftToRight) {
            this.arrowRightInnerHandler();
        }
        else {
            this.arrowLeftInnerHandler();
        }
    }
    handleKeyDownRight(event) {
        if (!this.stepperService.state.isVertical) {
            this.handleGoNext();
            event.preventDefault();
        }
    }
    handleKeyDown(event) {
        if (!this.stepperService.state.isVertical) {
            this.handleGoPrevious();
            event.preventDefault();
        }
    }
    handleKeyDownUp(event) {
        if (this.stepperService.state.isVertical) {
            this.handleGoNext();
            event.preventDefault();
        }
    }
    handleKeyDownDown(event) {
        if (this.stepperService.state.isVertical) {
            this.handleGoPrevious();
            event.preventDefault();
        }
    }
    getSteps() {
        return this.element.nativeElement.querySelectorAll(`:scope .${DF_STEP_CLASS}`);
    }
    handleSelect() {
        const listStep = this.getSteps();
        const currentPos = Array.prototype.indexOf.call(listStep, document.activeElement);
        if (currentPos !== -1) {
            listStep[currentPos].click();
        }
    }
    arrowRightInnerHandler() {
        const listStep = this.getSteps();
        const currentPos = Array.prototype.indexOf.call(listStep, document.activeElement);
        // Test if the focus is in the list (otherwise we do nothing)
        if (currentPos !== -1) {
            // we put the focus on the next element.
            if (currentPos === listStep.length - 1) {
                listStep[0].focus();
            }
            else {
                listStep[currentPos + 1].focus();
            }
        }
    }
    arrowLeftInnerHandler() {
        const listStep = this.getSteps();
        const currentPos = Array.prototype.indexOf.call(listStep, document.activeElement);
        // Test if the focus is in the list (otherwise we do nothing)
        if (currentPos !== -1) {
            // we put the focus on the next element.
            if (currentPos === 0) {
                listStep[listStep.length - 1].focus();
            }
            else {
                listStep[currentPos - 1].focus();
            }
        }
    }
    ngAfterViewChecked() {
        // Put back the focus on the active elements
        if (this.stepperService.setFocus) {
            const listStep = this.getSteps();
            listStep[this.stepperService.state.active].focus();
            this.stepperService.resetFocus();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfStepperDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.7", type: DfStepperDirective, isStandalone: true, selector: "[dfStepperLogic]", host: { listeners: { "keydown.Enter": "handleEnterDown()", "keydown.Space": "handleSpaceDown()", "keydown.arrowRight": "handleKeyDownRight($event)", "keydown.arrowLeft": "handleKeyDown($event)", "keydown.arrowDown": "handleKeyDownUp($event)", "keydown.arrowUp": "handleKeyDownDown($event)" } }, exportAs: ["dfStepperLogic"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfStepperDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[dfStepperLogic]',
                    exportAs: 'dfStepperLogic',
                    standalone: true
                }]
        }], propDecorators: { handleEnterDown: [{
                type: HostListener,
                args: ['keydown.Enter']
            }], handleSpaceDown: [{
                type: HostListener,
                args: ['keydown.Space']
            }], handleKeyDownRight: [{
                type: HostListener,
                args: ['keydown.arrowRight', ['$event']]
            }], handleKeyDown: [{
                type: HostListener,
                args: ['keydown.arrowLeft', ['$event']]
            }], handleKeyDownUp: [{
                type: HostListener,
                args: ['keydown.arrowDown', ['$event']]
            }], handleKeyDownDown: [{
                type: HostListener,
                args: ['keydown.arrowUp', ['$event']]
            }] } });

class DfStepperStepContentDirective {
    constructor() {
        this.templateRef = inject(TemplateRef);
        /**
         * The step index
         * -1 means that the input index is not set
         */
        // eslint-disable-next-line @angular-eslint/no-input-rename
        this.dfStepperStepContent = -1;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfStepperStepContentDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "16.1.0", version: "18.2.7", type: DfStepperStepContentDirective, isStandalone: true, selector: "ng-template[dfStepperStepContent]", inputs: { dfStepperStepContent: ["dfStepperStepContent", "dfStepperStepContent", (value) => numberAttribute(value, -1)] }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfStepperStepContentDirective, decorators: [{
            type: Directive,
            args: [{ selector: 'ng-template[dfStepperStepContent]', standalone: true }]
        }], propDecorators: { dfStepperStepContent: [{
                type: Input,
                args: [{ transform: (value) => numberAttribute(value, -1) }]
            }] } });
/**
 * Stepper component is a component to provide a step by step navigation
 * You have two ways to navigate through the steps:
 * - Using the linear navigation, where you can only go to the next step
 * - Using the non-linear navigation, where you can go to any step
 */
class DfStepperComponent {
    constructor() {
        this.stepperService = inject(DfStepperService);
        this.mapStepToContent = new Map();
        this.stepperService.stepperState$.subscribe((state) => {
            this.state = state;
        });
    }
    ngAfterContentInit() {
        this.tplStepsContent.forEach((tplStepContent) => {
            if (tplStepContent.dfStepperStepContent !== -1) {
                this.mapStepToContent.set(tplStepContent.dfStepperStepContent, tplStepContent.templateRef);
            }
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfStepperComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.7", type: DfStepperComponent, isStandalone: true, selector: "[dfStepper]", host: { attributes: { "role": "tablist" }, properties: { "class.df-vertical-stepper": "state?.isVertical", "attr.aria-label": "stepperService.stepperAriaLabel", "attr.aria-orientation": "state?.isVertical ? \"vertical\": null" }, classAttribute: "df-stepper mb-2" }, queries: [{ propertyName: "tplStepsContent", predicate: DfStepperStepContentDirective }], hostDirectives: [{ directive: DfStepperDirective }], ngImport: i0, template: "<ng-template #myStepperLine>\n  <div class=\"df-stepper-line-wrapper\">\n    <div class=\"df-stepper-line\">\n      <div class=\"df-stepper-horizontal-line\"></div>\n    </div>\n    <div class=\"df-stepper-line d-none d-sm-flex\">\n      <div class=\"flex-grow-1\"></div>\n    </div>\n  </div>\n</ng-template>\n@if (state) {\n  @for (step of state.steps; track step; let i = $index) {\n    <button\n      #stepbasic\n      class=\"btn btn-link df-stepper-step\"\n      dfStepperStep\n      [class.active]=\"i === state.active\"\n      [class.flex-row]=\"state.isInline\"\n      [attr.tabIndex]=\"i === state.active ? 0 : -1\"\n      [class.df-stepper-step-warning]=\"step.type === 'warning'\"\n      [class.df-stepper-step-completed]=\"step.type === 'completed'\"\n      [class.df-stepper-step-visited]=\"step.type === 'visited'\"\n      [class.df-stepper-step-future]=\"step.type === 'future'\"\n      [disabled]=\"\n        state.isLinear &&\n        (step.type === 'future' || step.type === 'completed' || (step.type === 'visited' && i !== state.active))\n      \"\n      role=\"tab\"\n      [attr.aria-selected]=\"i === state.active ? 'true' : 'false'\"\n      [attr.aria-posinset]=\"i + 1\"\n      [attr.aria-setsize]=\"state.steps.length\"\n      [attr.aria-controls]=\"step.ariaControl\"\n      (click)=\"stepperService.selectWithFocus(i)\"\n    >\n      <div\n        [class.df-stepper-outline-number-visited]=\"step.type === 'visited'\"\n        [class.df-stepper-outline-icon]=\"step.type === 'warning'\"\n        [class.fa-exclamation-triangle]=\"step.type === 'warning'\"\n        [class.df-stepper-icon]=\"step.type === 'completed'\"\n        [class.fa-check]=\"step.type === 'completed'\"\n        [class.df-stepper-outline-number-future]=\"step.type === 'future'\"\n      >\n        <span aria-hidden=\"true\">{{ stepperService.getStepNumberLabel(i) }}</span>\n      </div>\n      <div\n        class=\"flex-column\"\n        [class.mt-1]=\"!state.isInline\"\n        [class.d-none]=\"!state.isVertical\"\n        [class.d-sm-flex]=\"!state.isVertical\"\n        [class.d-flex]=\"state.isVertical\"\n      >\n        @if (step.type === 'warning') {\n          <span class=\"visually-hidden\">{{ stepperService.warningLabel }}</span>\n        }\n        <span class=\"df-stepper-label\">{{ step.label }}</span>\n        @if (step.optionalLabel) {\n          <span class=\"df-stepper-optional-label\">{{ step.optionalLabel }}</span>\n        }\n        @if (step.completed) {\n          <span class=\"visually-hidden\">{{ stepperService.completedLabel }}</span>\n        }\n      </div>\n    </button>\n    @if (!state.isVertical && i < state.steps.length - 1) {\n      <ng-container [ngTemplateOutlet]=\"myStepperLine\"></ng-container>\n    }\n    @if (state.isVertical && i < state.steps.length) {\n      <div\n        [class.d-none]=\"state.steps.length - 1 === i\"\n        role=\"tabpanel\"\n        [id]=\"step.ariaControl\"\n        class=\"df-vertical-stepper-panel df-vertical-stepper-line\"\n      >\n        @if (mapStepToContent.has(i) && state.active === i) {\n          <ng-template\n            [ngTemplateOutlet]=\"mapStepToContent.get(i) || null\"\n            [ngTemplateOutletContext]=\"{ state, index: i, service: stepperService }\"\n          ></ng-template>\n        }\n      </div>\n    }\n  }\n}\n@if (stepperService.stepperState$ | async; as state) {\n  <div\n    class=\"text-primary mb-2\"\n    [class.d-block]=\"!state.isVertical\"\n    [class.d-sm-none]=\"!state.isVertical\"\n    [class.d-none]=\"state.isVertical\"\n  >\n    <span class=\"df-stepper-label\">{{ state.steps[state.active].label }}</span>\n  </div>\n}\n", dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1$1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "pipe", type: i1$1.AsyncPipe, name: "async" }, { kind: "directive", type: DfStepperStepDirective, selector: "[dfStepperStep]", exportAs: ["dfStepperStep"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfStepperComponent, decorators: [{
            type: Component,
            args: [{ selector: '[dfStepper]', standalone: true, changeDetection: ChangeDetectionStrategy.OnPush, imports: [CommonModule, DfStepperDirective, DfStepperStepDirective], hostDirectives: [DfStepperDirective], host: {
                        role: 'tablist',
                        class: 'df-stepper mb-2',
                        '[class.df-vertical-stepper]': 'state?.isVertical',
                        '[attr.aria-label]': 'stepperService.stepperAriaLabel',
                        '[attr.aria-orientation]': 'state?.isVertical ? "vertical": null'
                    }, template: "<ng-template #myStepperLine>\n  <div class=\"df-stepper-line-wrapper\">\n    <div class=\"df-stepper-line\">\n      <div class=\"df-stepper-horizontal-line\"></div>\n    </div>\n    <div class=\"df-stepper-line d-none d-sm-flex\">\n      <div class=\"flex-grow-1\"></div>\n    </div>\n  </div>\n</ng-template>\n@if (state) {\n  @for (step of state.steps; track step; let i = $index) {\n    <button\n      #stepbasic\n      class=\"btn btn-link df-stepper-step\"\n      dfStepperStep\n      [class.active]=\"i === state.active\"\n      [class.flex-row]=\"state.isInline\"\n      [attr.tabIndex]=\"i === state.active ? 0 : -1\"\n      [class.df-stepper-step-warning]=\"step.type === 'warning'\"\n      [class.df-stepper-step-completed]=\"step.type === 'completed'\"\n      [class.df-stepper-step-visited]=\"step.type === 'visited'\"\n      [class.df-stepper-step-future]=\"step.type === 'future'\"\n      [disabled]=\"\n        state.isLinear &&\n        (step.type === 'future' || step.type === 'completed' || (step.type === 'visited' && i !== state.active))\n      \"\n      role=\"tab\"\n      [attr.aria-selected]=\"i === state.active ? 'true' : 'false'\"\n      [attr.aria-posinset]=\"i + 1\"\n      [attr.aria-setsize]=\"state.steps.length\"\n      [attr.aria-controls]=\"step.ariaControl\"\n      (click)=\"stepperService.selectWithFocus(i)\"\n    >\n      <div\n        [class.df-stepper-outline-number-visited]=\"step.type === 'visited'\"\n        [class.df-stepper-outline-icon]=\"step.type === 'warning'\"\n        [class.fa-exclamation-triangle]=\"step.type === 'warning'\"\n        [class.df-stepper-icon]=\"step.type === 'completed'\"\n        [class.fa-check]=\"step.type === 'completed'\"\n        [class.df-stepper-outline-number-future]=\"step.type === 'future'\"\n      >\n        <span aria-hidden=\"true\">{{ stepperService.getStepNumberLabel(i) }}</span>\n      </div>\n      <div\n        class=\"flex-column\"\n        [class.mt-1]=\"!state.isInline\"\n        [class.d-none]=\"!state.isVertical\"\n        [class.d-sm-flex]=\"!state.isVertical\"\n        [class.d-flex]=\"state.isVertical\"\n      >\n        @if (step.type === 'warning') {\n          <span class=\"visually-hidden\">{{ stepperService.warningLabel }}</span>\n        }\n        <span class=\"df-stepper-label\">{{ step.label }}</span>\n        @if (step.optionalLabel) {\n          <span class=\"df-stepper-optional-label\">{{ step.optionalLabel }}</span>\n        }\n        @if (step.completed) {\n          <span class=\"visually-hidden\">{{ stepperService.completedLabel }}</span>\n        }\n      </div>\n    </button>\n    @if (!state.isVertical && i < state.steps.length - 1) {\n      <ng-container [ngTemplateOutlet]=\"myStepperLine\"></ng-container>\n    }\n    @if (state.isVertical && i < state.steps.length) {\n      <div\n        [class.d-none]=\"state.steps.length - 1 === i\"\n        role=\"tabpanel\"\n        [id]=\"step.ariaControl\"\n        class=\"df-vertical-stepper-panel df-vertical-stepper-line\"\n      >\n        @if (mapStepToContent.has(i) && state.active === i) {\n          <ng-template\n            [ngTemplateOutlet]=\"mapStepToContent.get(i) || null\"\n            [ngTemplateOutletContext]=\"{ state, index: i, service: stepperService }\"\n          ></ng-template>\n        }\n      </div>\n    }\n  }\n}\n@if (stepperService.stepperState$ | async; as state) {\n  <div\n    class=\"text-primary mb-2\"\n    [class.d-block]=\"!state.isVertical\"\n    [class.d-sm-none]=\"!state.isVertical\"\n    [class.d-none]=\"state.isVertical\"\n  >\n    <span class=\"df-stepper-label\">{{ state.steps[state.active].label }}</span>\n  </div>\n}\n" }]
        }], ctorParameters: () => [], propDecorators: { tplStepsContent: [{
                type: ContentChildren,
                args: [DfStepperStepContentDirective]
            }] } });

class DfStepperModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfStepperModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.7", ngImport: i0, type: DfStepperModule, imports: [DfStepperDirective, DfStepperStepDirective, DfStepperComponent, DfStepperStepContentDirective], exports: [DfStepperDirective, DfStepperStepDirective, DfStepperComponent, DfStepperStepContentDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfStepperModule, imports: [DfStepperComponent] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfStepperModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [DfStepperDirective, DfStepperStepDirective, DfStepperComponent, DfStepperStepContentDirective],
                    exports: [DfStepperDirective, DfStepperStepDirective, DfStepperComponent, DfStepperStepContentDirective]
                }]
        }] });

/**
 * My dfTooltipTruncate directive
 */
class DfTooltipTruncateDirective {
    constructor(tooltip, elementRef, renderer) {
        this.tooltip = tooltip;
        this.elementRef = elementRef;
        this.renderer = renderer;
    }
    ngOnInit() {
        this.renderer.addClass(this.elementRef.nativeElement, 'text-truncate');
    }
    ngDoCheck() {
        this.tooltip.disableTooltip = !hasOverflow(this.elementRef.nativeElement);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfTooltipTruncateDirective, deps: [{ token: i1.NgbTooltip }, { token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.7", type: DfTooltipTruncateDirective, isStandalone: true, selector: "[dfTooltipTruncate]", exportAs: ["dfTooltipTruncate"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfTooltipTruncateDirective, decorators: [{
            type: Directive,
            args: [{
                    exportAs: 'dfTooltipTruncate',
                    selector: '[dfTooltipTruncate]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i1.NgbTooltip }, { type: i0.ElementRef }, { type: i0.Renderer2 }] });

class DfTooltipModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfTooltipModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.7", ngImport: i0, type: DfTooltipModule, imports: [DfTooltipTruncateDirective], exports: [DfTooltipTruncateDirective, NgbTooltipModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfTooltipModule, imports: [NgbTooltipModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfTooltipModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [DfTooltipTruncateDirective],
                    exports: [DfTooltipTruncateDirective, NgbTooltipModule]
                }]
        }] });

class DfSideNavModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfSideNavModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.7", ngImport: i0, type: DfSideNavModule, imports: [DfManageSideNavDirective,
            DfSideNavComponent,
            DfSideNavHeaderDirective,
            DfExcludeTrapDirective,
            DfSideNavIconDirective,
            DfSideNavItemDirective], exports: [DfManageSideNavDirective,
            DfSideNavComponent,
            DfSideNavHeaderDirective,
            DfExcludeTrapDirective,
            DfSideNavIconDirective,
            DfSideNavItemDirective,
            DfSideNavCollapseModule,
            DfMediaModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfSideNavModule, imports: [DfSideNavComponent, DfSideNavCollapseModule,
            DfMediaModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfSideNavModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        DfManageSideNavDirective,
                        DfSideNavComponent,
                        DfSideNavHeaderDirective,
                        DfExcludeTrapDirective,
                        DfSideNavIconDirective,
                        DfSideNavItemDirective
                    ],
                    exports: [
                        DfManageSideNavDirective,
                        DfSideNavComponent,
                        DfSideNavHeaderDirective,
                        DfExcludeTrapDirective,
                        DfSideNavIconDirective,
                        DfSideNavItemDirective,
                        DfSideNavCollapseModule,
                        DfMediaModule
                    ]
                }]
        }] });

class DfManageCardSelectionDirective {
    set dfManageCardSelected(isSelected) {
        const selectedClass = 'df-card-selected';
        if (isSelected === true) {
            this.renderer.addClass(this.element.nativeElement, selectedClass);
        }
        else {
            this.renderer.removeClass(this.element.nativeElement, selectedClass);
        }
    }
    constructor(element, renderer) {
        this.element = element;
        this.renderer = renderer;
    }
    ngAfterViewInit() {
        this.input = this.element.nativeElement.querySelector('.form-check-input');
        this.renderer.setAttribute(this.input, 'tabIndex', '-1');
    }
    click(event) {
        const target = event.target;
        // If the input or the label is clicked then the selection is already made
        // The card must have only one input
        if (!(target.classList.contains('form-check-input') || target.classList.contains('form-check-label'))) {
            this.input.click();
        }
        else {
            this.element.nativeElement.focus();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfManageCardSelectionDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.7", type: DfManageCardSelectionDirective, isStandalone: true, selector: "[dfManageCardSelected]", inputs: { dfManageCardSelected: "dfManageCardSelected" }, host: { listeners: { "click": "click($event)" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfManageCardSelectionDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[dfManageCardSelected]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }], propDecorators: { dfManageCardSelected: [{
                type: Input
            }], click: [{
                type: HostListener,
                args: ['click', ['$event']]
            }] } });

class DfAdvancedCardModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfAdvancedCardModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.7", ngImport: i0, type: DfAdvancedCardModule, imports: [DfManageCardSelectionDirective], exports: [DfManageCardSelectionDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfAdvancedCardModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfAdvancedCardModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [DfManageCardSelectionDirective],
                    exports: [DfManageCardSelectionDirective]
                }]
        }] });

const DF_MODULES = [
    DfAlertModule,
    DfDatePickerModule,
    DfSelectModule,
    DfProgressbarModule,
    DfProgressIndicatorModule,
    DfAdvancedInputModule,
    DfAccessibilityModule,
    DfSliderModule,
    DfPopoverModule,
    DfIconModule,
    DfTooltipModule,
    DfSideNavModule,
    DfSideNavCollapseModule,
    DfAdvancedCardModule,
    DfFooterModule,
    DfToastModule,
    DfStepperModule
];
/**
 * @deprecated Import only the needed modules or use standalone components instead of DfModule
 */
class DfModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.7", ngImport: i0, type: DfModule, imports: [DfAlertModule,
            DfDatePickerModule,
            DfSelectModule,
            DfProgressbarModule,
            DfProgressIndicatorModule,
            DfAdvancedInputModule,
            DfAccessibilityModule,
            DfSliderModule,
            DfPopoverModule,
            DfIconModule,
            DfTooltipModule,
            DfSideNavModule,
            DfSideNavCollapseModule,
            DfAdvancedCardModule,
            DfFooterModule,
            DfToastModule,
            DfStepperModule], exports: [DfAlertModule,
            DfDatePickerModule,
            DfSelectModule,
            DfProgressbarModule,
            DfProgressIndicatorModule,
            DfAdvancedInputModule,
            DfAccessibilityModule,
            DfSliderModule,
            DfPopoverModule,
            DfIconModule,
            DfTooltipModule,
            DfSideNavModule,
            DfSideNavCollapseModule,
            DfAdvancedCardModule,
            DfFooterModule,
            DfToastModule,
            DfStepperModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfModule, imports: [DF_MODULES, DfAlertModule,
            DfDatePickerModule,
            DfSelectModule,
            DfProgressbarModule,
            DfProgressIndicatorModule,
            DfAdvancedInputModule,
            DfAccessibilityModule,
            DfSliderModule,
            DfPopoverModule,
            DfIconModule,
            DfTooltipModule,
            DfSideNavModule,
            DfSideNavCollapseModule,
            DfAdvancedCardModule,
            DfFooterModule,
            DfToastModule,
            DfStepperModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: DF_MODULES,
                    exports: DF_MODULES
                }]
        }] });

class DfWavesOfProgressDirective {
    constructor() {
        this.http = inject(HttpClient, { optional: true });
        this.waveClass = 'df-waves-of-progress-bg';
        this.elementRef = inject(ElementRef);
        this.baseSvg = '';
    }
    /**
     * The type of waves to display. In order to display the waves, the app using the directive must include the assets of Design Factory.
     * It is possible to do so by adding the following snippet to the `angular.json` file in the `assets` configuration:
     * @example
     * "assets": [
     *  {
     *    "glob": "** / *", //remove the spaces between the asterisks and the slash
     *    "input": "node-modules/@design-factory/design-factory/assets/",
     *    "output": "assets/"
     *  },
     *  ...]
     */
    set wavesType(value) {
        this.getSvg(value);
    }
    /**
     * Prepares the SVG string by replacing color variables and encoding it.
     * @param bgColor - The background color.
     * @param lineColor - The line color.
     * @returns The prepared SVG string.
     */
    prepareSvg(bgColor, lineColor) {
        let baseSvg = this.baseSvg.replace(/var\(\-\-df\-waves\-of\-progress\-bg\-color\)/g, bgColor);
        baseSvg = baseSvg.replace(/var\(\-\-df\-waves\-of\-progress\-line\-color\)/g, lineColor);
        baseSvg = baseSvg.replace(/#/g, '%23');
        return `'data:image/svg+xml;utf8,${baseSvg}'`;
    }
    /**
     * Updates the SVG background image based on the current element's styles.
     */
    updateSvg() {
        const computedStyle = window.getComputedStyle(this.elementRef.nativeElement);
        const bgColor = computedStyle.getPropertyValue('--df-waves-of-progress-bg-color');
        const lineColor = computedStyle.getPropertyValue('--df-waves-of-progress-line-color');
        if (!bgColor || !lineColor) {
            this.elementRef.nativeElement.style.backgroundImage = null;
        }
        else {
            this.elementRef.nativeElement.style.backgroundImage = `url(${this.prepareSvg(bgColor, lineColor)})`;
        }
    }
    getSvg(wavesType) {
        if (!this.http) {
            throw Error('Could not find HttpClient provider for use with DF Waves of progress. ' +
                'Please include the HttpClientModule from @angular/common/http in your ' +
                'app imports.');
        }
        const url = `assets/waves_of_progress/${wavesType}.svg`;
        this.http.get(url, { responseType: 'text' }).subscribe({
            next: (svgString) => {
                this.baseSvg = svgString.replace(/\n|\r/g, '');
                this.updateSvg();
            },
            error: (error) => {
                throw Error('[Design Factory] (Waves of progress): ' + error.message);
            }
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfWavesOfProgressDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.7", type: DfWavesOfProgressDirective, isStandalone: true, selector: "[dfWavesOfProgress]", inputs: { wavesType: ["dfWavesOfProgress", "wavesType"] }, host: { properties: { "class": "this.waveClass" } }, exportAs: ["dfWavesOfProgress"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfWavesOfProgressDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[dfWavesOfProgress]',
                    standalone: true,
                    exportAs: 'dfWavesOfProgress'
                }]
        }], propDecorators: { wavesType: [{
                type: Input,
                args: ['dfWavesOfProgress']
            }], waveClass: [{
                type: HostBinding,
                args: ['class']
            }] } });

class DfWavesOfProgressModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfWavesOfProgressModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.7", ngImport: i0, type: DfWavesOfProgressModule, imports: [DfWavesOfProgressDirective], exports: [DfWavesOfProgressDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfWavesOfProgressModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: DfWavesOfProgressModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [DfWavesOfProgressDirective],
                    exports: [DfWavesOfProgressDirective]
                }]
        }] });

// Accessibility

/**
 * Public API Surface of design-factory
 */

/**
 * Generated bundle index. Do not edit.
 */

export { BREAKPOINTS_VARS, ChangeContext, CloseInputDatePickerDirective, DF_BREAKPOINTS_VAR_DEFAULT, DfAccessibilityModule, DfAdvancedCardModule, DfAdvancedInputModule, DfAlertModule, DfAmadeusIconComponent, DfBreakpoints, DfDatePickerModule, DfDatepickerRangeDirective, DfDatepickerRangeService, DfDefaultSpinnerComponent, DfDirectionDetectionService, DfExcludeTrapDirective, DfFooterModule, DfIconModule, DfIfMediaDirective, DfInputIconDirective, DfInsertIconDirective, DfInsertIconModule, DfManageBadgeEventsDirective, DfManageCardSelectionDirective, DfManageNavSelectDirective, DfManageSideNavDirective, DfMedia, DfMediaModule, DfMediaObserver, DfMediaQuery, DfModalService, DfModule, DfNavItemType, DfOptionHighlightDirective, DfPopoverConfig, DfPopoverModule, DfProgressIndicatorBackdropComponent, DfProgressIndicatorContainerComponent, DfProgressIndicatorContentPosition, DfProgressIndicatorContentProgressBar, DfProgressIndicatorContentSpinner, DfProgressIndicatorContentType, DfProgressIndicatorDirective, DfProgressIndicatorModule, DfProgressIndicatorRef, DfProgressIndicatorService, DfProgressbarComponent, DfProgressbarModule, DfSelectModule, DfSideNavCollapseDirective, DfSideNavCollapseModule, DfSideNavCollapseService, DfSideNavComponent, DfSideNavConfig, DfSideNavHeaderDirective, DfSideNavIconDirective, DfSideNavItemDirective, DfSideNavModule, DfSideNavService, DfSliderDirectionDirective, DfSliderModule, DfStepperComponent, DfStepperDirective, DfStepperModule, DfStepperService, DfStepperStepContentDirective, DfStepperStepDirective, DfTitleTruncateDirective, DfToastModule, DfTooltipModule, DfTooltipTruncateDirective, DfTriggerClickDirective, DfWavesOfProgressDirective, DfWavesOfProgressModule, LabelType, Options, PointerType, RightToLeftDirectionEnum, SkipLinkDirective, SkipLinksContainerComponent, SliderComponent };
